import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Info, Upload, X } from "lucide-react";
import { StipInstructionsContent } from "@/components/StipInstructionsContent";
import { StipInstructionsEditor } from "@/components/StipInstructionsEditor";
import { isProbablyRichTextHtml, isStipInstructionsEmpty } from "@/utils/stipInstructions";
import { InternalCollaborationSection } from "@/pages/applications/InternalCollaborationSection";
import { SosRegistryPanel, type SosRegistryApiPayload } from "@/components/sos/SosRegistryPanel";
import { DdStaffLensToggle } from "@/pages/applications/DdStaffLensToggle";
import { InternalCdProgramsCard } from "@/pages/applications/InternalCdProgramsCard";
import {
  ManualOfferDealerSection,
  hasPublicMatchedOffers,
} from "@/pages/applications/ManualOfferDealerSection";
import { staffUi } from "@/pages/applications/staff-ui-classes";
import { toast } from "sonner";
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, startTransition, lazy, Suspense, type ReactNode } from "react";
import type { ApplicationEconomicsModalsHandle } from "./economics-modals";
import {
  applicationQueryKey,
  applicationAsyncStatusQueryKey,
  fetchApplication,
  fetchApplicationAsyncStatus,
  mergeApplicationAsyncStatus,
  applicationNeedsAsyncPolling,
} from "@/lib/application-query";
import {
  mergeApprovalEconomicsInCache,
  mergeFundedEconomicsInCache,
  mergeLenderSubmissionInCache,
  patchApplicationCache,
} from "@/lib/application-cache";

const ApplicationEconomicsModalsLazy = lazy(() =>
  import("./economics-modals").then((m) => ({ default: m.ApplicationEconomicsModals }))
);

import { financedPrincipalForPayment } from "@/utils/financedPrincipal";
import { tierTermMonthsOrDefault } from "@/utils/prequalTerms";
import { formatPrequalFailureForDisplay } from "@/utils/prequalFailureDisplay";
import { PAYMENT_ESTIMATE_DISCLAIMER } from "@/utils/paymentEstimateDisclaimer";
import {
  CADENCE_LABELS,
  cadenceConfigFromOffer,
  computePaymentScheduleQuote,
  enabledCadencesFromOffer,
  preferredCadenceFromOffer,
  type CadenceConfig,
  type PaymentCadence,
} from "@/utils/paymentSchedule";
import {
  SeasonalPaymentRequestButton,
  SeasonalPaymentRequestModal,
  formatSeasonalRequestSummary,
  type SeasonalPaymentRequestRecord,
} from "@/components/seasonal-payment-request-modal";
import {
  applyLenderSubmissionEmailPlaceholders,
  buildLenderSubmissionEmailContext,
  RICH_LENDER_SUBMISSION_EMAIL_DEFAULT,
} from "@/utils/lenderSubmissionEmail";

// Pencil icon component
const PencilIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    />
  </svg>
);

// US States list for dropdowns
const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" }, { value: "DC", label: "District of Columbia" },
  { value: "PR", label: "Puerto Rico" }, { value: "VI", label: "U.S. Virgin Islands" }, { value: "AS", label: "American Samoa" },
  { value: "GU", label: "Guam" }, { value: "MP", label: "Northern Mariana Islands" },
];

const LS_INTERNAL_PREQUAL = "dealerdog.appDetail.internalPrequal";

/** Path-only Fastline listing URLs must not use the portal origin as the link base. */
const FASTLINE_LISTING_ORIGIN = "https://www.fastline.com";

function resolveFastlineListingHref(raw: string, sourcePlatform: string): string {
  const t = (raw || "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) return `https:${t}`;
  const plat = (sourcePlatform || "").trim().toLowerCase();
  if (t.startsWith("/") && plat === "fastline") {
    return `${FASTLINE_LISTING_ORIGIN}${t}`;
  }
  return t;
}

function readInternalPrequalOpen(userId: number | undefined, section: "otherMatched" | "unmatched"): boolean {
  if (userId == null) return false;
  try {
    return window.localStorage.getItem(`${LS_INTERNAL_PREQUAL}.${userId}.${section}`) === "true";
  } catch {
    return false;
  }
}

function writeInternalPrequalOpen(userId: number | undefined, section: "otherMatched" | "unmatched", open: boolean) {
  if (userId == null) return;
  try {
    window.localStorage.setItem(`${LS_INTERNAL_PREQUAL}.${userId}.${section}`, open ? "true" : "false");
  } catch {
    /* ignore */
  }
}

// Formatting utilities
const toDigits = (v: string) => (v || "").replace(/\D/g, "");

const formatPhone = (raw: string) => {
  if (!raw) return "";
  let digits = toDigits(raw);
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length === 12 && digits.startsWith("01")) digits = digits.slice(2);
  digits = digits.slice(0, 10);
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 10);
  if (part3) return `${part1}-${part2}-${part3}`;
  if (part2) return `${part1}-${part2}`;
  return part1;
};

const formatEIN = (raw: string) => {
  if (!raw) return "";
  const digits = toDigits(raw).slice(0, 9);
  if (digits.length > 2) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return digits;
};

const handlePhoneChange = (value: string, setter: (val: string) => void) => {
  const digits = toDigits(value);
  // Strip leading "1" if present (US country code)
  let cleaned = digits;
  if (digits.length === 11 && digits.startsWith('1')) {
    cleaned = digits.slice(1);
  }
  const limited = cleaned.slice(0, 10);
  const formatted = limited.length > 6 
    ? `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`
    : limited.length > 3
    ? `${limited.slice(0, 3)}-${limited.slice(3)}`
    : limited;
  setter(formatted);
};

const handleEINChange = (value: string, setter: (val: string) => void) => {
  const digits = toDigits(value);
  const limited = digits.slice(0, 9);
  const formatted = limited.length > 2
    ? `${limited.slice(0, 2)}-${limited.slice(2)}`
    : limited;
  setter(formatted);
};

const formatSSN = (raw: string) => {
  if (!raw) return "";
  const digits = toDigits(raw).slice(0, 9);
  if (digits.length === 9) return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  if (digits.length > 5) return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
};

const maskSSN = (ssn: string | null | undefined) => {
  if (!ssn) return "***-**-****";
  const digits = toDigits(ssn);
  if (digits.length < 9) return "***-**-****";
  return `xxx-xx-${digits.slice(5)}`;
};

// Format DOB as MM/DD/YYYY for display
const formatDOB = (raw: string | null | undefined) => {
  if (!raw) return "";
  // If already in YYYY-MM-DD format, convert to MM/DD/YYYY
  if (raw.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = raw.split('-');
    return `${month}/${day}/${year}`;
  }
  // If already in MM/DD/YYYY format, return as is
  if (raw.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return raw;
  }
  // If it's just digits, try to format (assume YYYYMMDD or MMDDYYYY)
  const digits = toDigits(raw);
  if (digits.length === 8) {
    // Try YYYYMMDD format first
    const year = digits.slice(0, 4);
    const month = digits.slice(4, 6);
    const day = digits.slice(6, 8);
    // If year is reasonable (1900-2100), assume YYYYMMDD
    if (parseInt(year) >= 1900 && parseInt(year) <= 2100) {
      return `${month}/${day}/${year}`;
    }
    // Otherwise assume MMDDYYYY
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }
  return raw;
};

// Convert MM/DD/YYYY to YYYY-MM-DD for backend
const convertDOBForBackend = (dob: string): string | null => {
  if (!dob || !dob.trim()) return null;
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob;
  // Remove all non-digits
  const digits = toDigits(dob);
  if (digits.length !== 8) return null; // Invalid format
  // Format: MMDDYYYY -> YYYY-MM-DD
  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  return `${year}-${month}-${day}`;
};

// Handle DOB input - format: MM/DD/YYYY
const handleDOBChange = (value: string, setter: (val: string) => void) => {
  // Remove everything except digits and slashes
  const cleaned = value.replace(/[^\d/]/g, '');
  
  // Extract just the digits for length limiting
  const digits = toDigits(cleaned);
  
  // If user typed slashes, try to preserve them in valid positions
  if (cleaned.includes('/')) {
    // Match pattern: digits, optional slash, digits, optional slash, digits
    const parts = cleaned.split('/');
    const month = parts[0]?.slice(0, 2) || '';
    const day = parts[1]?.slice(0, 2) || '';
    const year = parts[2]?.slice(0, 4) || '';
    
    // Build formatted string, adding slashes at appropriate positions
    let formatted = month;
    if (month.length === 2 || day.length > 0) {
      formatted += '/';
      formatted += day;
      if (day.length === 2 || year.length > 0) {
        formatted += '/';
        formatted += year;
      }
    }
    
    // Limit total length to 10 characters (MM/DD/YYYY)
    if (formatted.length > 10) {
      formatted = formatted.slice(0, 10);
    }
    
    setter(formatted);
  } else {
    // No slashes - auto-format as user types digits
    const limited = digits.slice(0, 8);
    let formatted = '';
    if (limited.length > 4) {
      formatted = `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
    } else if (limited.length > 2) {
      formatted = `${limited.slice(0, 2)}/${limited.slice(2)}`;
    } else {
      formatted = limited;
    }
    setter(formatted);
  }
};

const handleSSNChange = (value: string, setter: (val: string) => void) => {
  const digits = toDigits(value);
  const limited = digits.slice(0, 9);
  const formatted = formatSSN(limited);
  setter(formatted);
};

interface ApplicationOwner {
  id?: number;
  supplemental_signed_at?: string | null;
  owner: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address_1: string;
    city: string;
    state: string;
    postal_code: string;
    ssn?: string | null;
    dob?: string | null;
    credit_score?: number | null;
    credit_pull_status?: string;
    /** Org-admin manual initiations since last successful pull; API resets on COMPLETED. */
    org_admin_manual_credit_pull_count?: number;
    credit_pull_error?: string | null;
    equifax_pdf_path?: string | null;
    credit_summary_pdf_path?: string | null;
  };
  role_label: string;
  ownership_percent?: number | string | null;
}

interface LenderApprovalEconomics {
  id?: number;
  approval_amount: number;
  dealer_participation: number;
  down_payment_type: 'DOLLARS' | 'PERCENT' | null;
  down_payment_value: number;
  term_length: number;
  advance_payments: number;
  lender_doc_fees: number;
  at_doc_fees: number;
  contract_type: string | null;
  max_compensation_type: 'POINTS' | 'COMMISSION' | null;
  max_compensation_value: number;
  buy_rate: number | null;
  sell_rate: number | null;
  gross_margin: number | null;
  invoice_amount: number | null;
  total_financed_amount: number | null;
  total_commission: number | null;
  points: number | null;
  approved_date?: string | null;
  notes: string;
}

interface LenderSubmission {
  id: number;
  lender: number;
  lender_name: string;
  status: string;
  notes: string;
  submitted_at: string;
  approval_economics?: LenderApprovalEconomics | null;
}

interface Lender {
  id: number;
  name: string;
  is_active: boolean;
  default_subject?: string;
  default_body?: string;
  /** EMAIL = email to lender; API = partner HTTP (e.g. Amur) */
  submission_delivery?: string;
  prequal_visibility?: "PUBLIC" | "INTERNAL";
  grade?: string;
}

function monthlyPaymentFromPrequal(principal: number, annualRatePct: number, termMonths: number): number {
  if (principal <= 0 || termMonths <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return principal / termMonths;
  return principal * (r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1);
}

/** Simple interest %: pre-points principal; payment from principal*(1+points). Same as thank-you page. */
function simpleInterestPct(
  principalPrePoints: number,
  monthlyPmt: number,
  termMonths: number
): number {
  if (principalPrePoints <= 0 || termMonths <= 0) return 0;
  const n = termMonths;
  const termYears = n / 12;
  const totalPaid = monthlyPmt * n;
  const annualInterestDollars = (totalPaid - principalPrePoints) / termYears;
  return 100 * (annualInterestDollars / principalPrePoints);
}

/** APR including points: Excel RATE(nper, -pmt, pv, 0, 0)*12. Same as thank-you page. */
function finalAprIncludingPoints(
  principalPrePoints: number,
  noteRatePct: number,
  termMonths: number,
  pointsPct: number
): number {
  if (principalPrePoints <= 0 || termMonths <= 0) return noteRatePct;
  const principalWithPoints = principalPrePoints * (1 + pointsPct / 100);
  const payment = monthlyPaymentFromPrequal(principalWithPoints, noteRatePct, termMonths);
  if (payment <= 0) return noteRatePct;
  const nper = termMonths;
  const pmt = payment;
  const pv = principalPrePoints;
  const target = pv / pmt;
  let iLow = 1e-6;
  let iHigh = 0.5;
  for (let iter = 0; iter < 120; iter++) {
    const iMid = (iLow + iHigh) / 2;
    const annuity = (1 - Math.pow(1 + iMid, -nper)) / iMid;
    if (annuity > target) iLow = iMid;
    else iHigh = iMid;
  }
  const monthlyRate = (iLow + iHigh) / 2;
  return monthlyRate * 12 * 100;
}

interface Document {
  id: number;
  filename: string;
  mime: string;
  size: number;
  scan_status: string;
  kind: string;
  created_at: string;
  s3_key: string;
  stip_id?: number | null;
  stip_name?: string | null;
  uploaded_by_name?: string | null;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_active?: boolean;
  /** Django flags; used for staff-only tools (e.g. unsigned app PDF). */
  is_staff?: boolean;
  is_superuser?: boolean;
  can_pull_paynet?: boolean;
  can_submit_any_lender?: boolean;
}

interface StipulationTemplate {
  id: number;
  name: string;
  description: string;
  destination: string;
  is_active: boolean;
}

/**
 * Tax-style names like "2024 Corporate Tax Returns" -> year + type bucket "Corporate Tax Returns".
 * Only the leading year in the name is used (not years buried in the description).
 */
function parseStipNameLeadingYearAndType(name: string): { year: number; typeLabel: string } | null {
  const m = name.trim().match(/^\s*((?:19|20)\d{2})\s+(.+)$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  if (y < 1900 || y > 2099) return null;
  const typeLabel = m[2].trim();
  if (!typeLabel) return null;
  return { year: y, typeLabel };
}

interface ApplicationStip {
  id: number;
  template_name: string;
  template_description: string;
  destination: string;
  status: string;
  note: string;
  /** Rich text (HTML) or legacy markdown; editable only by SUPERADMIN / DD_ACCOUNT_MANAGER. */
  instructions?: string;
  is_completed: boolean;
  created_at: string;
  updated_at: string;
}

interface FundedDealEconomics {
  id?: number;
  lender_financed_with: number | null;
  lender_financed_with_name?: string;
  equipment_cost: number;
  shipping_warranty_other_fees: number;
  dealer_participation: number;
  lender_fees_financed: number;
  down_payment: number;
  term_months: number;
  lender_doc_fees_collected: number;
  at_doc_fees: number;
  monthly_payment: number;
  number_of_advances: number;
  contract_type: string | null;
  buy_rate: number;
  sell_rate: number;
  gross_margin: number;
  invoice_amount: number | null;
  total_financed_amount: number | null;
  total_commission: number | null;
  invoice_number: string | null;
  points: number;
  funded_date?: string | null;
  dealer_referral?: number | null;
}

interface EquipmentTypeOption {
  id: number;
  name: string;
  slug: string;
  underwriting_signals?: string[];
}

interface EquipmentItem {
  id: number;
  equipment_type?: EquipmentTypeOption | null;
  equipment_type_id?: number | null;
  make: string | null;
  model_name: string | null;
  year: number | null;
  hours: number | null;
  mileage: number | null;
  trailer_type: string | null;
  titled_vs_non_titled: string | null;
  free_text: string | null;
  price: string | number | null;
  name: string;
  dealer: number | null;
  created_at: string;
  updated_at: string;
}

interface PrequalResultItem {
  lender_id: number;
  tier_id: number;
  lender_name: string;
  tier_name: string;
  matched: boolean;
  failure_reason?: string | null;
  failure_code?: string | null;
  /** All failure reasons (internal); up to 5. When present, first entry matches failure_reason/failure_code. */
  failure_reasons?: { reason: string; code?: string | null }[] | null;
  equipment_requirements?: string | null;
  rate_min: number;
  rate_max: number;
  term_months: number[];
  payment_range?: Record<number, { min_payment: number; max_payment: number }>;
  /** Lender + dealer points % used for payment (same as thank-you page). */
  total_points_used?: number;
  /** Optional note shown on approval/offer card (e.g. RDL first-payment timing). */
  approval_card_note?: string | null;
  /** CHB: when ownership %s are set and do not total 100%, show this note on the offer card. */
  ownership_100_pct_note?: string | null;
  /** Min down payment as % of equipment/principal (e.g. 10 for Mitsubishi used). */
  min_down_payment_pct?: number | null;
  /** Min down payment as fixed amount. */
  min_down_payment_amount?: number | null;
  /** When false (RDL), min_down_payment_amount is required OOP and does not reduce financed principal; overage does. */
  min_down_payment_reduces_principal?: boolean | null;
  /** For display_offers: bucket id for grouping (no_down_payment | down_payment_required). */
  display_bucket?: string | null;
  /** For display_offers: 1 = best in bucket, 2 = also recommended. */
  display_rank_in_bucket?: number | null;
  /** Non-monthly payment options from lender config (when present in prequal payload). */
  enabled_cadences?: string[];
  non_monthly_cadences?: Record<string, CadenceConfig>;
}

interface SourceDealerDto {
  id: number;
  platform: string;
  name: string;
  email?: string | null;
  email_domain?: string | null;
  phone?: string | null;
  contact_name?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  application_count: number;
  created_at: string;
  updated_at: string;
}

interface Application {
  id: number;
  internal_id: string;
  /** Dealer org for scoping users API (e.g. sales rep dropdown). */
  organization_id?: number;
  organization_name?: string;
  organization_type?: string | null;
  intake_program?: { intake_profile?: string; cadence_picker?: boolean } | null;
  equipment_year?: number | string | null;
  ref_url?: string | null;
  source_metadata?: Record<string, unknown> | null;
  /** Staff-only: sanitized HTTP context at org link / embed bootstrap (referer, origin, path, etc.). */
  intake_bootstrap_context?: Record<string, string> | null;
  source_dealer?: SourceDealerDto | null;
  equipment_type?: { id: number; name: string } | null;
  equipment_name: string;
  primary_equipment_custom_values?: Record<string, string> | null;
  primary_equipment_custom_field_defs?: { key: string; label: string }[];
  equipment_price: string;
  requested_amount: string;
  annual_revenue?: string | number | null;
  industry?: string | null;
  stage: string;
  stips_requested: boolean;
  application_signed: boolean;
  signer_ip?: string | null;
  signed_at?: string | null;
  signature_s3_key?: string | null;
  company: any;
  primary_contact: any;
  owners: ApplicationOwner[];
  contact_person?: ApplicationOwner | null;
  lender_submissions: LenderSubmission[];
  stips?: ApplicationStip[];
  assigned_sales_rep?: {
    id: number;
    first_name: string;
    last_name: string;
  } | null;
  funded_deal_economics?: FundedDealEconomics | null;
  documents?: Document[];
  equipments?: EquipmentItem[];
  prequal_results?: { run_at?: string; results?: PrequalResultItem[]; display_offers?: PrequalResultItem[]; manual_review_flags?: string[] } | null;
  bank_statement_metrics?: any;
  internal_prequal_results?: { run_at?: string; results?: any[]; display_offers?: any[] } | null;
  manual_offer_publish?: {
    published_at?: string;
    source_tier_id?: number;
    source_lender_id?: number;
    dealer_card?: {
      label?: string;
      term_months?: number | null;
      payment?: number | null;
      down_payment_pct?: number | null;
      down_payment_amount?: number | null;
      notes?: string;
      published_at?: string;
    };
  } | null;
  prequalified_at?: string | null;
  prequal_selection?: {
    lender_id?: number;
    tier_id?: number;
    lender_name?: string;
    tier_name?: string;
    term_months?: number;
    down_payment_amount?: number | null;
    monthly_payment?: number | null;
    payment_cadence?: PaymentCadence;
    payment_amount?: number | null;
    total_payments?: number | null;
    due_at_signing?: number | null;
    first_payment_due_months?: number | null;
    selected_at?: string;
  } | null;
  prequal_lever_answers?: {
    submitted_at?: string;
    prior_1099_or_sole_prop?: boolean;
    updated_years_in_business?: number | string;
    other_company_similar_industry?: boolean;
    other_company_years_in_business?: number | string;
    new_business_name?: string;
  } | null;
  /** Intake CDL / SAFER screening answers (yes/no, fleet counts). */
  prequal_gate_answers?: Record<string, unknown> | null;
  notes?: string;
  application_source_token?: string | null;
  est_close_date?: string | null;
  is_archived?: boolean;
  mask_lender_names?: boolean;
  can_submit_to_lender?: boolean;
  manual_review_requested?: boolean;
  seasonal_payment_eligible?: boolean;
  seasonal_payment_requests?: SeasonalPaymentRequestRecord[];
  /** Org setting: when false, hide simple interest % and APR in dealer prequal helper UI. */
  show_prequal_interest_and_apr?: boolean;
  submission_mode?: string | null;
  commercial_loans_completed?: string | null;
  corp_only_elected_at?: string | null;
  corp_only_converted_to_full_at?: string | null;
  corp_only_eligible_declined?: boolean;
  sos_registry?: SosRegistryApiPayload | null;
}

function equipmentSummaryForSubmissionEmail(application: Application | undefined): string {
  if (!application) return "—";
  const first = application.equipments?.[0];
  if (first) {
    const parts = [first.year, first.make, first.model_name].filter(
      (x) => x != null && String(x).trim() !== ""
    );
    if (parts.length) return parts.map(String).join(" ");
  }
  return (application.equipment_name || "").trim() || "—";
}

/** Offer 1/2/… labels by lender_id (same ordering as API mask_prequal / _apply_lender_name_masking). */
function maskedOfferLabelByLenderId(
  prequal: NonNullable<Application["prequal_results"]> | null | undefined
): Map<number, string> {
  const map = new Map<number, string>();
  if (!prequal) return map;
  const order: number[] = [];
  const seen = new Set<number>();
  const add = (lid: number | undefined) => {
    if (lid == null || seen.has(lid)) return;
    seen.add(lid);
    order.push(lid);
  };
  for (const r of prequal.results ?? []) add(r.lender_id);
  for (const r of prequal.display_offers ?? []) add(r.lender_id);
  order.forEach((lid, i) => map.set(lid, `Offer ${i + 1}`));
  return map;
}

function defaultCustomerSignKind(
  app: Application,
  supplementalOwnerRows: ApplicationOwner[]
): "PRIMARY_CONTACT" | "ADDITIONAL_OWNER" | "PG_UPGRADE" {
  const isCorpOnly =
    app.submission_mode === "CORP_ONLY" && !app.corp_only_converted_to_full_at;
  const enabled: ("PRIMARY_CONTACT" | "ADDITIONAL_OWNER" | "PG_UPGRADE")[] = [];
  if (!(isCorpOnly && app.application_signed)) {
    enabled.push("PRIMARY_CONTACT");
  }
  if (supplementalOwnerRows.length > 0) {
    enabled.push("ADDITIONAL_OWNER");
  }
  if (isCorpOnly) {
    enabled.push("PG_UPGRADE");
  }
  if (enabled.length === 1) {
    return enabled[0];
  }
  return "PRIMARY_CONTACT";
}

/** True when `requested_amount` is stored on the application (not only implied by equipment price). */
function hasPersistedRequestedAmount(app: Application | undefined): boolean {
  if (!app || app.requested_amount == null) return false;
  return String(app.requested_amount).trim() !== "";
}

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState("");
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editedPrice, setEditedPrice] = useState("");
  const [isEditingEstCloseDate, setIsEditingEstCloseDate] = useState(false);
  const [editedEstCloseDate, setEditedEstCloseDate] = useState("");
  const [selectedLender, setSelectedLender] = useState<number | null>(null);
  const [emailBody, setEmailBody] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [lenderApiSubmissionNotes, setLenderApiSubmissionNotes] = useState("");
  const [notes, setNotes] = useState("");
  const notesDirtyRef = useRef(false); // Track if notes have unsaved changes
  const requestedAmountBackfillDoneRef = useRef<number | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedStipForUpload, setSelectedStipForUpload] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lenderSubmitFileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lenderSubmitDragging, setLenderSubmitDragging] = useState(false);
  const [showAddStipsDialog, setShowAddStipsDialog] = useState(false);
  const [addStipsTemplateSearch, setAddStipsTemplateSearch] = useState("");
  const [expandedApprovalEconomics, setExpandedApprovalEconomics] = useState<{ [key: number]: boolean }>({});
  const [editingApprovalEconomics, setEditingApprovalEconomics] = useState<{ [key: number]: boolean }>({});
  const economicsModalsRef = useRef<ApplicationEconomicsModalsHandle>(null);
  const [editedApprovalEconomicsData, setEditedApprovalEconomicsData] = useState<{ [key: number]: Partial<LenderApprovalEconomics> }>({});
  const [editingSubmissionNotes, setEditingSubmissionNotes] = useState<number | null>(null);
  const [editedSubmissionNotes, setEditedSubmissionNotes] = useState<{ [key: number]: string }>({});
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
  /** Per-template markdown when adding stips (keys only for templates user entered text for). */
  const [addStipsTemplateInstructions, setAddStipsTemplateInstructions] = useState<Record<number, string>>({});
  const [editStipInstructionsStip, setEditStipInstructionsStip] = useState<ApplicationStip | null>(null);
  const [editStipInstructionsDraft, setEditStipInstructionsDraft] = useState("");
  const [stipDestination, setStipDestination] = useState<'CUSTOMER' | 'INTERNAL'>('CUSTOMER');
  const [uploadLinkUrl, setUploadLinkUrl] = useState<string | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);
  const [documentRename, setDocumentRename] = useState<{ id: number; base: string; ext: string } | null>(null);
  const queryClient = useQueryClient();

  // Editing states
  const [editingEquipmentName, setEditingEquipmentName] = useState(false);
  const [editedEquipmentName, setEditedEquipmentName] = useState("");
  const [editingPrimaryCustomFields, setEditingPrimaryCustomFields] = useState(false);
  const [editedPrimaryCustomFields, setEditedPrimaryCustomFields] = useState<Record<string, string>>({});
  const [editingOwner, setEditingOwner] = useState<{ [key: number]: boolean }>({});
  const [editedOwnerData, setEditedOwnerData] = useState<{ [key: number]: any }>({});
  const [editingCompany, setEditingCompany] = useState(false);
  const [editedCompanyData, setEditedCompanyData] = useState<any>({});
  const [editingFundedEconomics, setEditingFundedEconomics] = useState(false);
  const [editedFundedEconomicsData, setEditedFundedEconomicsData] = useState<Partial<FundedDealEconomics>>({});
  
  // PayNet card (internal lens)
  const [paynetExpanded, setPaynetExpanded] = useState(true);

  // Unmatched offer reasons dialog (all failure reasons for one tier)
  const [unmatchedReasonsDialog, setUnmatchedReasonsDialog] = useState<{
    key: string;
    lenderName: string;
    tierName?: string;
    reasons: { reason: string; code?: string | null }[];
  } | null>(null);

  const [internalOtherMatchedOpen, setInternalOtherMatchedOpen] = useState(false);
  const [internalUnmatchedOpen, setInternalUnmatchedOpen] = useState(false);

  // Internal prequal selection (select offer for customer when they didn't)
  const [selectedPrequalTierForCustomer, setSelectedPrequalTierForCustomer] = useState<PrequalResultItem | null>(null);
  const [internalPrequalTermMonths, setInternalPrequalTermMonths] = useState<number>(60);
  const [internalPrequalDownPayment, setInternalPrequalDownPayment] = useState<number>(0);
  const [internalPrequalCadence, setInternalPrequalCadence] = useState<PaymentCadence>("monthly");
  const prequalSelectionPanelRef = useRef<HTMLDivElement>(null);
  const [seasonalModalTarget, setSeasonalModalTarget] = useState<{
    lenderId: number;
    tierId?: number;
    termMonths?: number;
    downPaymentAmount?: number;
    monthlyPayment?: number;
  } | null>(null);

  // Equipment (M2M) modal and form
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [editingEquipmentId, setEditingEquipmentId] = useState<number | null>(null);
  const [equipmentFormData, setEquipmentFormData] = useState<{
    equipment_type_id: number | null;
    equipment_subtype_id: number | null;
    year: string;
    make: string;
    model_name: string;
    mileage: string;
    hours: string;
    price: string;
    free_text: string;
  }>({ equipment_type_id: null, equipment_subtype_id: null, year: '', make: '', model_name: '', mileage: '', hours: '', price: '', free_text: '' });

  const [showCustomerSignModal, setShowCustomerSignModal] = useState(false);
  const [customerSignKind, setCustomerSignKind] = useState<
    "PRIMARY_CONTACT" | "ADDITIONAL_OWNER" | "PG_UPGRADE"
  >("PRIMARY_CONTACT");
  const [customerSignOwnerRowId, setCustomerSignOwnerRowId] = useState("");
  const [customerSignUrl, setCustomerSignUrl] = useState("");

  const [showAddOwnerModal, setShowAddOwnerModal] = useState(false);
  const [addOwnerForm, setAddOwnerForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    postal_code: "",
    ssn: "",
    dob: "",
    ownership_percent: "",
  });

  // Equipment type search (synonym/search API) in add/edit modal) — restricted to application org's allowed types when set
  const [equipmentSearchQuery, setEquipmentSearchQuery] = useState("");
  const [equipmentSearchResults, setEquipmentSearchResults] = useState<{ equipment_type: { id: number; name: string }; equipment_subtype: { id: number; name: string } | null }[]>([]);
  const equipmentSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper functions for date-only fields (no timezone conversion)
  const formatDateOnly = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    // If already in YYYY-MM-DD format, parse as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}/${year}`;
    }
    // Fallback for other formats
    try {
      const date = new Date(dateString + 'T00:00:00'); // Add time to avoid UTC interpretation
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Helper to convert date string to YYYY-MM-DD format for date inputs (no timezone conversion)
  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Try to parse and extract YYYY-MM-DD without timezone conversion
    try {
      const [year, month, day] = dateString.split(/[-\/]/);
      if (year && month && day) {
        return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    } catch (e) {
      return "";
    }
    return "";
  };

  // Helper functions for timezone conversion (UTC <-> Local for datetime-local inputs)
  const utcToLocalDatetime = (utcString: string | null | undefined): string => {
    if (!utcString) return '';
    try {
      const date = new Date(utcString);
      // Get local time components (browser's local timezone)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  const localToUtcISO = (localDatetime: string): string | null => {
    if (!localDatetime) return null;
    try {
      // Create date from local datetime string (browser interprets as local time)
      const localDate = new Date(localDatetime);
      // Convert to ISO string (UTC) - this is what the backend expects
      return localDate.toISOString();
    } catch (e) {
      return null;
    }
  };

  const isArchivedView = searchParams.get("is_archived") === "true";

  const { data: application, isLoading } = useQuery({
    queryKey: applicationQueryKey(id),
    queryFn: async () => {
      const data = await fetchApplication(id!, isArchivedView) as Application;
      // Only update amount if not currently editing (requested_amount derived from selection; fallback to equipment_price)
      if (!isEditingAmount) {
        setEditedAmount(data.requested_amount ?? data.equipment_price ?? "");
      }
      // Only update price if not currently editing
      if (!isEditingPrice) {
        setEditedPrice(data.equipment_price || "");
      }
      // Only update equipment name if not currently editing
      if (!editingEquipmentName) {
        setEditedEquipmentName(data.equipment_name || "");
      }
      // Only update est_close_date if not currently editing
      if (!isEditingEstCloseDate) {
        setEditedEstCloseDate(formatDateForInput(data.est_close_date));
      }
      // Only update notes from server if user hasn't made unsaved changes
      if (!notesDirtyRef.current) {
        setNotes(data.notes || "");
      }
      return data;
    },
  });

  const asyncPollEnabled = Boolean(
    id && application && applicationNeedsAsyncPolling(application)
  );

  const { data: asyncStatusPatch } = useQuery({
    queryKey: applicationAsyncStatusQueryKey(id),
    queryFn: () => fetchApplicationAsyncStatus(id!, isArchivedView),
    enabled: asyncPollEnabled,
    refetchInterval: 2000,
  });

  useEffect(() => {
    if (!asyncStatusPatch || !id) return;
    queryClient.setQueryData(applicationQueryKey(id), (prev) => {
      if (!prev) return prev;
      return mergeApplicationAsyncStatus(prev as Application, asyncStatusPatch);
    });
  }, [asyncStatusPatch, id, queryClient]);

  const supplementalSignOwnerRows = useMemo(() => {
    if (!application?.owners) return [];
    const pcId = application.primary_contact?.id;
    return application.owners.filter(
      (o) =>
        o.id != null &&
        o.role_label !== "CONTACT_PERSON" &&
        !o.supplemental_signed_at &&
        o.owner?.id != null &&
        o.owner.id !== pcId
    );
  }, [application]);

  useEffect(() => {
    if (showCustomerSignModal && application) {
      setCustomerSignUrl("");
      setCustomerSignOwnerRowId("");
      setCustomerSignKind(defaultCustomerSignKind(application, supplementalSignOwnerRows));
    }
  }, [showCustomerSignModal, application, supplementalSignOwnerRows]);

  useEffect(() => {
    if (showAddOwnerModal) {
      setAddOwnerForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        address_1: "",
        address_2: "",
        city: "",
        state: "",
        postal_code: "",
        ssn: "",
        dob: "",
        ownership_percent: "",
      });
    }
  }, [showAddOwnerModal]);

  // Equipment type search: run after application is loaded so we can use application.allowed_equipment_type_ids
  useEffect(() => {
    if (!showEquipmentModal || !equipmentSearchQuery.trim()) {
      setEquipmentSearchResults([]);
      return;
    }
    if (equipmentSearchDebounceRef.current) clearTimeout(equipmentSearchDebounceRef.current);
    equipmentSearchDebounceRef.current = setTimeout(() => {
      const params: { q: string; allowed_types?: string } = { q: equipmentSearchQuery.trim() };
      const allowedIds = application?.allowed_equipment_type_ids;
      if (Array.isArray(allowedIds) && allowedIds.length > 0) {
        params.allowed_types = allowedIds.join(",");
      }
      customInstance.get<{ options: { equipment_type: { id: number; name: string }; equipment_subtype: { id: number; name: string } | null }[] }>(
        "/api/equipment-types/search/",
        { params }
      ).then(({ data }) => {
        setEquipmentSearchResults(data?.options ?? []);
      }).catch(() => setEquipmentSearchResults([]));
      equipmentSearchDebounceRef.current = null;
    }, 300);
    return () => {
      if (equipmentSearchDebounceRef.current) clearTimeout(equipmentSearchDebounceRef.current);
    };
  }, [showEquipmentModal, equipmentSearchQuery, application?.allowed_equipment_type_ids]);

  // Sync notes state when application data changes (e.g., after refresh)
  // Only sync if notes are not dirty (no unsaved changes)
  useEffect(() => {
    if (application?.notes !== undefined && !notesDirtyRef.current) {
      setNotes(application.notes || "");
    }
  }, [application?.notes]);

  // Pre-select current offer in "Change selected offer" when application already has a prequal selection
  useEffect(() => {
    const sel = application?.prequal_selection;
    const results = application?.prequal_results?.results ?? [];
    if (!sel || sel.lender_id == null || sel.tier_id == null || !results.length) return;
    const match = results.find((r: PrequalResultItem) => r.lender_id === sel.lender_id && r.tier_id === sel.tier_id);
    if (match) {
      setSelectedPrequalTierForCustomer(match);
      const allowedTerms = tierTermMonthsOrDefault(match.term_months);
      const preferred = sel.term_months != null ? Number(sel.term_months) : null;
      const term =
        preferred != null && allowedTerms.includes(preferred)
          ? preferred
          : allowedTerms[allowedTerms.length - 1];
      setInternalPrequalTermMonths(term);
      setInternalPrequalDownPayment(Number(sel.down_payment_amount) || 0);
      const allowedCadences = enabledCadencesFromOffer(match);
      const savedCadence = (sel.payment_cadence as PaymentCadence | undefined) ?? "monthly";
      setInternalPrequalCadence(
        allowedCadences.includes(savedCadence) ? savedCadence : (allowedCadences[0] ?? "monthly")
      );
    }
  }, [application?.prequal_selection, application?.prequal_results?.results]);

  const { data: lenders } = useQuery({
    queryKey: ["lenders"],
    queryFn: async () => {
      const { data } = await customInstance.get<{ results: Lender[] }>("/api/lenders/");
      return data.results;
    },
  });

  // Pre-populate subject and body when lender dialog opens
  useEffect(() => {
    if (selectedLender && application && lenders) {
      const lender = lenders.find(l => l.id === selectedLender);
      
      // Pre-fill subject
      if (lender?.default_subject) {
        // Format the subject template with application data
        const bizName = application.company?.name || "N/A";
        const dbaName = application.company?.dba || "";
        const internalDealId = application.internal_id || "";
        
        let formattedSubject = lender.default_subject
          .replace(/{biz_name}/g, bizName)
          .replace(/{internal_deal_id}/g, internalDealId);
        
        // Handle DBA - include if exists, otherwise remove "DBA {dba_name}" from template
        if (dbaName) {
          formattedSubject = formattedSubject.replace(/{dba_name}/g, dbaName);
        } else {
          formattedSubject = formattedSubject.replace(/ DBA \{dba_name\}/g, "");
        }
        
        setEmailSubject(formattedSubject);
      } else {
        setEmailSubject("");
      }
      
      // Pre-fill body (placeholders + deal context for legacy templates without placeholders)
      const sel = application.prequal_selection;
      const liveTier = application.prequal_results?.results?.find(
        (r: PrequalResultItem) =>
          r.matched && r.lender_id === sel?.lender_id && r.tier_id === sel?.tier_id
      );
      const tierForEmail = liveTier?.tier_name ?? sel?.tier_name ?? "—";
      const emailCtx = buildLenderSubmissionEmailContext({
        companyName: application.company?.name,
        tierName: tierForEmail,
        requestedAmount: application.requested_amount,
        equipmentSummary: equipmentSummaryForSubmissionEmail(application),
      });
      const rawTemplate = (lender?.default_body && lender.default_body.trim())
        ? lender.default_body
        : RICH_LENDER_SUBMISSION_EMAIL_DEFAULT;
      const hasDealPlaceholders = /\{(company_name|tier_name|requested_amount|equipment_summary)\}/.test(
        rawTemplate
      );
      let composedBody = applyLenderSubmissionEmailPlaceholders(rawTemplate, emailCtx);
      if (!hasDealPlaceholders) {
        const appendix = applyLenderSubmissionEmailPlaceholders(
          "\n\n---\nCompany: {company_name}\nEquipment: {equipment_summary}\nRequested financing amount: {requested_amount}\nSelected program / tier: {tier_name}\n",
          emailCtx
        );
        composedBody = `${composedBody.trim()}\n${appendix}`;
      }
      setEmailBody(composedBody);
    } else if (!selectedLender) {
      // Clear subject and body when dialog closes
      setEmailSubject("");
      setEmailBody("");
    }
  }, [selectedLender, application, lenders]);

  const { data: salesReps } = useQuery({
    queryKey: ["sales-reps", application?.organization_id],
    queryFn: async () => {
      const oid = application?.organization_id;
      if (oid == null) {
        return [];
      }
      const { data } = await customInstance.get<{ results: User[] }>("/api/users/", {
        params: { role: "SALES", organization_id: oid },
      });
      return data.results;
    },
    enabled: application?.organization_id != null,
  });

  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data } = await customInstance.get<User>("/api/auth/me/");
      return data;
    },
  });

  useEffect(() => {
    const uid = currentUser?.id;
    const role = currentUser?.role;
    if (uid == null) return;
    if (role !== "SUPERADMIN" && role !== "DD_ACCOUNT_MANAGER") return;
    setInternalOtherMatchedOpen(readInternalPrequalOpen(uid, "otherMatched"));
    setInternalUnmatchedOpen(readInternalPrequalOpen(uid, "unmatched"));
  }, [currentUser?.id, currentUser?.role]);

  const { data: stipulationTemplates, isLoading: stipulationTemplatesLoading } = useQuery({
    queryKey: ["stipulation-templates"],
    queryFn: async () => {
      const { data } = await customInstance.get<{ results: StipulationTemplate[] } | StipulationTemplate[]>(
        "/api/stipulation-templates/"
      );
      return Array.isArray(data) ? data : (data.results ?? []);
    },
    enabled: showAddStipsDialog,
  });

  const addStipsGroupedTemplates = useMemo(() => {
    if (!stipulationTemplates?.length) {
      return { kind: "none" as const };
    }
    const q = addStipsTemplateSearch.trim().toLowerCase();
    let list = stipulationTemplates.filter((t) => t.destination === stipDestination);
    if (q) {
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q)
      );
    }
    if (list.length === 0) {
      return q ? ({ kind: "no-search-match" as const } as const) : ({ kind: "no-destination" as const } as const);
    }
    const byType = new Map<string, StipulationTemplate[]>();
    for (const t of list) {
      const parsed = parseStipNameLeadingYearAndType(t.name);
      const key = parsed?.typeLabel ?? (t.name.trim() || `Template #${t.id}`);
      const arr = byType.get(key);
      if (arr) arr.push(t);
      else byType.set(key, [t]);
    }
    const typeLabels = Array.from(byType.keys()).sort((a, b) => a.localeCompare(b));
    const groups: { label: string; templates: StipulationTemplate[] }[] = typeLabels.map((typeLabel) => ({
      label: typeLabel,
      templates: [...(byType.get(typeLabel) || [])].sort((a, b) => {
        const pa = parseStipNameLeadingYearAndType(a.name);
        const pb = parseStipNameLeadingYearAndType(b.name);
        const ya = pa?.year ?? 0;
        const yb = pb?.year ?? 0;
        if (yb !== ya) return yb - ya;
        return a.name.localeCompare(b.name);
      }),
    }));
    return { kind: "groups" as const, groups };
  }, [stipulationTemplates, stipDestination, addStipsTemplateSearch]);

  const { data: intakeData } = useQuery({
    queryKey: ["intake-data"],
    queryFn: async () => {
      const { data } = await customInstance.get("/api/intake/data/");
      return data as { industries: { id: number; name: string }[]; consent_language?: { content: string } };
    },
  });

  const updateAmountMutation = useMutation({
    mutationFn: async (amount: string) => {
      await customInstance.patch(`/api/applications/${id}/`, { requested_amount: amount });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Amount updated");
      setIsEditingAmount(false);
    },
  });

  const updatePriceMutation = useMutation({
    mutationFn: async (price: string) => {
      const current = queryClient.getQueryData<Application>([
        "application",
        id,
        searchParams.get("is_archived"),
      ]);
      const payload: Record<string, string> = { equipment_price: price };
      if (!hasPersistedRequestedAmount(current)) {
        payload.requested_amount = price;
      }
      await customInstance.patch(`/api/applications/${id}/`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Price updated");
      setIsEditingPrice(false);
    },
  });

  const updateEquipmentNameMutation = useMutation({
    mutationFn: async (name: string) => {
      await customInstance.patch(`/api/applications/${id}/`, { equipment_name: name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Equipment name updated");
      setEditingEquipmentName(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to update equipment name");
    },
  });

  const updateEstCloseDateMutation = useMutation({
    mutationFn: async (date: string | null) => {
      await customInstance.patch(`/api/applications/${id}/`, { 
        est_close_date: date || null 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Est. Close Date updated");
      setIsEditingEstCloseDate(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to update Est. Close Date");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      await customInstance.post(`/api/applications/${id}/archive/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      queryClient.invalidateQueries({ queryKey: ["stage-counts"] });
      toast.success("Application archived");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to archive application");
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: async () => {
      await customInstance.post(`/api/applications/${id}/unarchive/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      queryClient.invalidateQueries({ queryKey: ["applications"] });
      queryClient.invalidateQueries({ queryKey: ["stage-counts"] });
      toast.success("Application unarchived");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to unarchive application");
    },
  });

  const requestManualReviewMutation = useMutation({
    mutationFn: async () => {
      await customInstance.post(`/api/applications/${id}/request-manual-review/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Manual review requested");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to request manual review");
    },
  });

  const saveSeasonalPaymentRequestMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await customInstance.post(`/api/applications/${id}/seasonal-payment-request/`, payload);
      return data;
    },
    onSuccess: () => {
      setSeasonalModalTarget(null);
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Seasonal payment request saved");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to save seasonal payment request");
    },
  });

  // Helper function to format Django REST Framework validation errors
  const formatValidationErrors = (error: any): string => {
    if (!error?.response?.data) {
      return "An unexpected error occurred";
    }

    const data = error.response.data;

    // If there's a simple error message
    if (typeof data === 'string' || data.error) {
      return data.error || data;
    }

    // If it's a validation error object with field names
    if (typeof data === 'object') {
      const errorMessages: string[] = [];
      Object.keys(data).forEach((field) => {
        const fieldErrors = Array.isArray(data[field]) ? data[field] : [data[field]];
        fieldErrors.forEach((msg: string) => {
          if (msg) {
            // Format field name (e.g., "ein" -> "EIN", "phone" -> "Phone")
            const fieldName = field
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            errorMessages.push(`${fieldName}: ${msg}`);
          }
        });
      });
      if (errorMessages.length > 0) {
        return errorMessages.join('. ');
      }
    }

    return "Failed to update";
  };

  const updateOwnerMutation = useMutation({
    mutationFn: async ({ ownerId, data }: { ownerId: number; data: any }) => {
      await customInstance.patch(`/api/applications/${id}/owners/${ownerId}/`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Owner updated");
    },
    onError: (error: any) => {
      const errorMessage = formatValidationErrors(error);
      toast.error(errorMessage);
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: async (data: any) => {
      await customInstance.patch(`/api/applications/${id}/update_company/`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Company updated");
    },
    onError: (error: any) => {
      const errorMessage = formatValidationErrors(error);
      toast.error(errorMessage);
    },
  });

  const { data: equipmentTypes } = useQuery({
    queryKey: ["equipment-types"],
    queryFn: async () => {
      const { data } = await customInstance.get<{ results?: EquipmentTypeOption[] }>("/api/equipment-types/");
      return Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
    },
    enabled: showEquipmentModal,
  });

  const toastAfterEquipmentFinancialsSync = (
    sync: { skipped?: boolean; selection_cleared?: boolean; prequal_ran?: boolean } | undefined,
    verb: "added" | "updated" | "removed",
  ) => {
    const base =
      verb === "added" ? "Equipment added" : verb === "updated" ? "Equipment updated" : "Equipment removed";
    if (!sync || sync.skipped) {
      toast.success(base);
      return;
    }
    if (sync.selection_cleared && sync.prequal_ran) {
      toast.success(`${base}. Previous offer selection was cleared and prequal was re-run.`);
    } else if (sync.prequal_ran) {
      toast.success(`${base}. Application amounts updated and prequal re-run.`);
    } else {
      toast.success(base);
    }
  };

  const addEquipmentMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await customInstance.post(`/api/applications/${id}/equipments/`, payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      setShowEquipmentModal(false);
      setEquipmentFormData({ equipment_type_id: null, equipment_subtype_id: null, year: '', make: '', model_name: '', mileage: '', hours: '', price: '', free_text: '' });
      setEquipmentSearchQuery("");
      setEquipmentSearchResults([]);
      toastAfterEquipmentFinancialsSync(data?.equipment_financials_sync, "added");
    },
    onError: (error: any) => {
      const msg = error?.response?.data ? (typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data)) : "Failed to add equipment";
      toast.error(msg);
    },
  });

  const updateEquipmentMutation = useMutation({
    mutationFn: async ({ equipmentId, payload }: { equipmentId: number; payload: Record<string, unknown> }) => {
      const { data } = await customInstance.patch(`/api/applications/${id}/equipments/${equipmentId}/`, payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      setShowEquipmentModal(false);
      setEditingEquipmentId(null);
      setEquipmentFormData({ equipment_type_id: null, equipment_subtype_id: null, year: '', make: '', model_name: '', mileage: '', hours: '', price: '', free_text: '' });
      setEquipmentSearchQuery("");
      setEquipmentSearchResults([]);
      toastAfterEquipmentFinancialsSync(data?.equipment_financials_sync, "updated");
    },
    onError: (error: any) => {
      const msg = error?.response?.data ? (typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data)) : "Failed to update equipment";
      toast.error(msg);
    },
  });

  const deleteEquipmentMutation = useMutation({
    mutationFn: async (equipmentId: number) => {
      const { data } = await customInstance.delete<{ status?: string; equipment_financials_sync?: { skipped?: boolean; selection_cleared?: boolean; prequal_ran?: boolean } }>(
        `/api/applications/${id}/equipments/${equipmentId}/`,
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toastAfterEquipmentFinancialsSync(data?.equipment_financials_sync, "removed");
    },
    onError: () => toast.error("Failed to remove equipment"),
  });

  // Messages (dealer / internal board)
  interface AppMessage {
    id: number;
    author: number;
    author_display: string;
    is_internal: boolean;
    body: string;
    created_at: string;
  }
  const [messageBody, setMessageBody] = useState("");
  const [disagreementBody, setDisagreementBody] = useState("");

  interface PrequalDisagreementRow {
    id: number;
    author_email: string;
    author_name: string;
    body: string;
    created_at: string;
    sheet_appended_at: string | null;
    sheet_error: string;
  }
  const { data: prequalDisagreements = [] } = useQuery({
    queryKey: ["prequal-disagreements", id],
    queryFn: async () => {
      const { data } = await customInstance.get<PrequalDisagreementRow[]>(
        `/api/applications/${id}/prequal-disagreements/`,
      );
      return data;
    },
    enabled: !!id && currentUser?.role === "SUPERADMIN",
  });
  const submitDisagreementMutation = useMutation({
    mutationFn: async (body: string) => {
      const { data } = await customInstance.post<PrequalDisagreementRow>(
        `/api/applications/${id}/prequal-disagreements/`,
        { body },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prequal-disagreements", id] });
      setDisagreementBody("");
      toast.success("Prequal disagreement submitted. Superadmins were notified.");
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.body != null
          ? String(Array.isArray(error.response.data.body) ? error.response.data.body[0] : error.response.data.body)
          : error?.response?.data?.detail || "Failed to submit";
      toast.error(msg);
    },
  });

  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const pendingMessagesScrollRef = useRef(true);
  const lastMessagesAppIdRef = useRef<string | undefined>(undefined);

  const { data: messages = [], refetch: refetchMessages, isFetched: messagesFetched } = useQuery({
    queryKey: ["application-messages", id],
    queryFn: async () => {
      const { data } = await customInstance.get<AppMessage[]>(`/api/applications/${id}/messages/`);
      return data;
    },
    enabled: !!id,
  });

  useLayoutEffect(() => {
    // Ensure the "scroll-to-bottom" flag is set *before* we attempt to scroll on a new application.
    // useEffect([id]) runs after paint, which can miss the first layout scroll.
    if (id && lastMessagesAppIdRef.current !== id) {
      lastMessagesAppIdRef.current = id;
      pendingMessagesScrollRef.current = true;
    }

    if (!messagesFetched || !pendingMessagesScrollRef.current) return;
    const el = messagesScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
    pendingMessagesScrollRef.current = false;
  }, [id, messages, messagesFetched]);

  const postMessageMutation = useMutation({
    mutationFn: async (body: string) => {
      const { data } = await customInstance.post<AppMessage>(`/api/applications/${id}/messages/`, { body });
      return data;
    },
    onSuccess: () => {
      pendingMessagesScrollRef.current = true;
      queryClient.invalidateQueries({ queryKey: ["application-messages", id] });
      setMessageBody("");
      toast.success("Message sent");
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.body ? (Array.isArray(error.response.data.body) ? error.response.data.body[0] : error.response.data.body) : "Failed to send message";
      toast.error(msg);
    },
  });

  const rerunPrequalMutation = useMutation({
    mutationFn: async () => {
      const { data } = await customInstance.post(`/api/applications/${id}/rerun-prequal/`);
      return data;
    },
    onSuccess: (_, __, context: { invalidate?: boolean } | undefined) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Prequal re-run complete");
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error ?? "Failed to re-run prequal";
      toast.error(msg);
    },
  });

  const savePrequalSelectionMutation = useMutation({
    mutationFn: async (payload: {
      lender_id: number;
      tier_id: number;
      term_months: number;
      down_payment_amount: number;
      monthly_payment: number;
      payment_cadence?: PaymentCadence;
      payment_amount?: number;
      total_payments?: number;
      due_at_signing?: number;
      first_payment_due_months?: number;
    }) => {
      const { data } = await customInstance.post(`/api/applications/${id}/prequal-selection/`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      setSelectedPrequalTierForCustomer(null);
      setInternalPrequalDownPayment(0);
      setInternalPrequalCadence("monthly");
      toast.success("Prequal selection saved");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to save prequal selection");
    },
  });

  const pullCreditMutation = useMutation({
    mutationFn: async (ownerId: number) => {
      await customInstance.post(`/api/applications/${id}/owners/${ownerId}/pull-credit/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Credit pull initiated");
    },
    onError: () => {
      toast.error("Failed to pull credit");
    },
  });

  const searchPaynetMutation = useMutation({
    mutationFn: async () => {
      const response = await customInstance.post(`/api/applications/${id}/search-paynet/`);
      return response.data;
    },
    onSuccess: (data: { match_count?: number }) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      const count = data.match_count ?? 0;
      toast.success(
        count
          ? `Found ${count} PayNet match${count === 1 ? "" : "es"}`
          : "No PayNet matches found"
      );
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || "Failed to search PayNet";
      toast.error(errorMessage);
    },
  });

  const pullPaynetMutation = useMutation({
    mutationFn: async () => {
      const response = await customInstance.post(`/api/applications/${id}/pull-paynet/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("PayNet report pull initiated");
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || "Failed to pull PayNet report";
      toast.error(errorMessage);
    },
  });

  const downloadPaynetReportMutation = useMutation({
    mutationFn: async (reportId: number) => {
      const { data } = await customInstance.get<Blob>(
        `/api/applications/${id}/paynet-reports/${reportId}/download/`,
        { responseType: "blob" }
      );
      return data;
    },
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to open PayNet report");
    },
  });

  const clearPaynetIdMutation = useMutation({
    mutationFn: async () => {
      const response = await customInstance.post(`/api/applications/${id}/clear-paynet-id/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("PayNet ID and matches cleared. A new search will be performed on the next report pull.");
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || "Failed to clear PayNet ID";
      toast.error(errorMessage);
    },
  });

  const selectPaynetIdMutation = useMutation({
    mutationFn: async (paynetId: string) => {
      const response = await customInstance.post(`/api/applications/${id}/select-paynet-id/`, {
        paynet_id: paynetId
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("PayNet ID selected");
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || "Failed to select PayNet ID";
      toast.error(errorMessage);
    },
  });

  const downloadSignedPdfMutation = useMutation({
    mutationFn: async () => {
      const { data } = await customInstance.post<{ download_url?: string }>(`/api/applications/${id}/download-signed-pdf/`);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      if (data.download_url) {
        window.open(data.download_url, "_blank");
        toast.success("Opening signed PDF");
      } else {
        toast.success("Signed application PDF");
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to download signed PDF");
    },
  });

  const downloadUnsignedStaffAppPdfMutation = useMutation({
    mutationFn: async (internalIdForFile: string) => {
      const { data } = await customInstance.get<Blob>(`/api/applications/${id}/unsigned-application-pdf/`, {
        responseType: "blob",
      });
      return { blob: data, internalIdForFile };
    },
    onSuccess: ({ blob, internalIdForFile }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `application-${internalIdForFile}-unsigned.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Unsigned application PDF downloaded");
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error;
      if (msg) {
        toast.error(typeof msg === "string" ? msg : "Failed to download unsigned PDF");
        return;
      }
      if (error.response?.data instanceof Blob) {
        error.response.data.text().then((t: string) => {
          try {
            const j = JSON.parse(t);
            toast.error(j.error || "Failed to download unsigned PDF");
          } catch {
            toast.error("Failed to download unsigned PDF");
          }
        });
        return;
      }
      toast.error("Failed to download unsigned PDF");
    },
  });

  const downloadRedactedStaffAppPdfMutation = useMutation({
    mutationFn: async (internalIdForFile: string) => {
      const { data } = await customInstance.get<Blob>(`/api/applications/${id}/redacted-application-pdf/`, {
        responseType: "blob",
      });
      return { blob: data, internalIdForFile };
    },
    onSuccess: ({ blob, internalIdForFile }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `application-${internalIdForFile}-redacted.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Redacted application PDF downloaded");
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error;
      if (msg) {
        toast.error(typeof msg === "string" ? msg : "Failed to download redacted PDF");
        return;
      }
      if (error.response?.data instanceof Blob) {
        error.response.data.text().then((t: string) => {
          try {
            const j = JSON.parse(t);
            toast.error(j.error || "Failed to download redacted PDF");
          } catch {
            toast.error("Failed to download redacted PDF");
          }
        });
        return;
      }
      toast.error("Failed to download redacted PDF");
    },
  });

  const customerSignLinkMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { signer_kind: customerSignKind };
      if (customerSignKind === "ADDITIONAL_OWNER") {
        body.application_owner_id = Number(customerSignOwnerRowId);
      }
      const { data } = await customInstance.post<{ url: string }>(
        `/api/applications/${id}/customer-sign-links/`,
        body
      );
      return data;
    },
    onSuccess: (data) => {
      setCustomerSignUrl(data.url);
      toast.success("Link generated. Copy and send it to the customer.");
    },
    onError: (error: any) => {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        "Failed to generate customer sign link";
      toast.error(typeof msg === "string" ? msg : "Failed to generate customer sign link");
    },
  });

  const addOwnerMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        first_name: addOwnerForm.first_name.trim(),
        last_name: addOwnerForm.last_name.trim(),
      };
      const emailTrim = addOwnerForm.email.trim();
      if (emailTrim) body.email = emailTrim;
      const phoneDigits = toDigits(addOwnerForm.phone);
      if (phoneDigits.length === 10) {
        body.phone = phoneDigits;
      }
      const a1 = addOwnerForm.address_1.trim();
      if (a1) body.address_1 = a1;
      const a2 = addOwnerForm.address_2.trim();
      if (a2) body.address_2 = a2;
      const city = addOwnerForm.city.trim();
      if (city) body.city = city;
      const st = addOwnerForm.state.trim().toUpperCase().slice(0, 2);
      if (st.length === 2) body.state = st;
      const zip = addOwnerForm.postal_code.trim();
      if (zip) body.postal_code = zip;
      const ssnDigits = toDigits(addOwnerForm.ssn);
      if (ssnDigits.length === 9) {
        body.ssn = ssnDigits;
      }
      const dobOut = convertDOBForBackend(addOwnerForm.dob);
      if (dobOut) body.dob = dobOut;
      const op = addOwnerForm.ownership_percent.trim();
      if (op) {
        const n = parseFloat(op.replace(/[^\d.]/g, ""));
        if (!Number.isNaN(n)) body.ownership_percent = n;
      }
      const { data } = await customInstance.post(`/api/applications/${id}/add-owner/`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Guarantor added. You can send a customer sign link for supplemental signature.");
      setShowAddOwnerModal(false);
    },
    onError: (error: any) => {
      const d = error.response?.data;
      if (d && typeof d === "object" && !d.error) {
        const first = Object.entries(d).find(([, v]) => v != null);
        if (first) {
          const msg = Array.isArray(first[1]) ? first[1][0] : String(first[1]);
          toast.error(`${first[0]}: ${msg}`);
          return;
        }
      }
      toast.error(d?.error || "Failed to add guarantor");
    },
  });

  const addStipsMutation = useMutation({
    mutationFn: async () => {
      const canSetInstructions =
        currentUser?.role === "SUPERADMIN" || currentUser?.role === "DD_ACCOUNT_MANAGER";
      const payload: {
        template_ids: number[];
        template_instructions?: Record<string, string>;
      } = { template_ids: selectedTemplateIds };
      if (canSetInstructions) {
        const template_instructions: Record<string, string> = {};
        for (const tid of selectedTemplateIds) {
          const raw = addStipsTemplateInstructions[tid];
          if (raw != null && !isStipInstructionsEmpty(String(raw))) {
            template_instructions[String(tid)] = String(raw);
          }
        }
        if (Object.keys(template_instructions).length > 0) {
          payload.template_instructions = template_instructions;
        }
      }
      await customInstance.post(`/api/applications/${id}/stips/add/`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Stips added successfully");
      setShowAddStipsDialog(false);
      setSelectedTemplateIds([]);
      setAddStipsTemplateInstructions({});
      setStipDestination('CUSTOMER');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to add stips");
    },
  });

  const updateStipStatusMutation = useMutation({
    mutationFn: async ({ stipId, status }: { stipId: number; status: string }) => {
      await customInstance.patch(`/api/stips/${stipId}/update-status/`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Stip status updated");
    },
    onError: () => {
      toast.error("Failed to update stip status");
    },
  });

  const deleteStipMutation = useMutation({
    mutationFn: async (stipId: number) => {
      await customInstance.delete(`/api/stips/${stipId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Stip deleted");
    },
    onError: () => {
      toast.error("Failed to delete stip");
    },
  });

  const patchStipInstructionsMutation = useMutation({
    mutationFn: async ({ stipId, instructions }: { stipId: number; instructions: string }) => {
      await customInstance.patch(`/api/stips/${stipId}/`, { instructions });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Instructions saved");
      setEditStipInstructionsStip(null);
      setEditStipInstructionsDraft("");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to save instructions");
    },
  });

  const updateStipDestinationMutation = useMutation({
    mutationFn: async ({ stipId, destination, previousDestination }: { stipId: number; destination: string; previousDestination: string }) => {
      await customInstance.patch(`/api/stips/${stipId}/update-destination/`, { destination });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.success("Stip destination updated");
    },
    onError: (error: any, variables) => {
      // Revert the toggle by invalidating queries (will refetch with original data)
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      toast.error(error.response?.data?.error || "Failed to update stip destination");
    },
  });

  // Check if there are customer stips with REQUESTED status
  const hasRequestedCustomerStips = application?.stips?.some(
    (stip) => stip.destination === 'CUSTOMER' && stip.status === 'REQUESTED'
  ) ?? false;

  // Fetch existing upload link on page load (for sales reps to see if link exists)
  const { data: existingUploadLink } = useQuery({
    queryKey: ["upload-link", id],
    queryFn: async () => {
      try {
        const { data } = await customInstance.get(`/api/applications/${id}/stips/upload-link/`);
        const frontendUrl = window.location.origin;
        return `${frontendUrl}/public/uploads/${data.token}`;
      } catch (error: any) {
        // If no link exists or error, return null (don't show error to user)
        if (error?.response?.status === 404) {
          return null;
        }
        return null;
      }
    },
    enabled: !!id && !!application && hasRequestedCustomerStips, // Only fetch if we have customer stips with REQUESTED status
    retry: false, // Don't retry on error
  });

  // Update uploadLinkUrl state when existing link is fetched
  useEffect(() => {
    if (existingUploadLink) {
      setUploadLinkUrl(existingUploadLink);
    }
  }, [existingUploadLink]);

  const BANK_STATEMENT_STIP_NAME = "Last 3 months of full bank statements (all pages)";
  const hasBankStatementStip = application?.stips?.some(
    (stip) => stip.template_name === BANK_STATEMENT_STIP_NAME
  ) ?? false;

  const { data: plaidBankLink } = useQuery({
    queryKey: ["plaid-bank-link", id],
    queryFn: async () => {
      const { data } = await customInstance.get(`/api/applications/${id}/plaid-bank-link/`);
      return data as {
        applicable: boolean;
        hosted_link_url: string | null;
        expires_at: string | null;
        status: string | null;
        institution_name: string;
        statements_saved_count: number;
        missing_months: string[];
        error: string;
      };
    },
    enabled: !!id && !!application && hasBankStatementStip,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "pending" || status === "connected" || status === "refreshing") return 15000;
      return false;
    },
  });

  const getUploadLinkMutation = useMutation({
    mutationFn: async () => {
      const { data } = await customInstance.get(`/api/applications/${id}/stips/upload-link/`);
      const frontendUrl = window.location.origin;
      return `${frontendUrl}/public/uploads/${data.token}`;
    },
    onSuccess: (link) => {
      setUploadLinkUrl(link);
      navigator.clipboard.writeText(link);
      toast.success("Upload link copied to clipboard!");
      // Invalidate the query to refresh
      queryClient.invalidateQueries({ queryKey: ["upload-link", id] });
    },
    onError: () => {
      toast.error("Failed to generate upload link");
    },
  });

  const updateFundedEconomicsMutation = useMutation({
    mutationFn: async (data: Partial<FundedDealEconomics>) => {
      const { data: saved } = await customInstance.patch(
        `/api/applications/${id}/funded-deal-economics/`,
        data
      );
      return saved as Record<string, unknown>;
    },
    onSuccess: (saved) => {
      if (id && saved) {
        mergeFundedEconomicsInCache(queryClient, id, saved);
        window.setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: applicationQueryKey(id) });
        }, 3000);
      }
      toast.success("Funded deal economics updated");
      setEditingFundedEconomics(false);
      setEditedFundedEconomicsData({});
      // Don't clear sessionStorage flag here - only clear when moving away from FUNDED stage
      // This prevents the modal from showing again immediately after save
    },
    onError: (error: any) => {
      const errorMessage = formatValidationErrors(error);
      toast.error(errorMessage);
    },
  });

  // Build API payload for approval economics: only writable fields present in raw, coerce types to avoid 400s
  const buildApprovalEconomicsPayload = (raw: Partial<LenderApprovalEconomics>): Record<string, unknown> => {
    const num = (v: unknown): number => (v === null || v === undefined || v === '') ? 0 : Number(v);
    const numOrNull = (v: unknown): number | null => (v === null || v === undefined || v === '') ? null : Number(v);
    const strOrNull = (v: unknown): string | null => (v === null || v === undefined || v === '') ? null : String(v);
    const writableKeys = [
      'approval_amount', 'dealer_participation', 'down_payment_type', 'down_payment_value', 'term_length',
      'advance_payments', 'lender_doc_fees', 'at_doc_fees', 'contract_type', 'max_compensation_type',
      'max_compensation_value', 'buy_rate', 'sell_rate', 'gross_margin', 'invoice_amount', 'monthly_payment',
      'points', 'approved_date', 'notes',
    ] as const;
    const out: Record<string, unknown> = {};
    writableKeys.forEach((key) => {
      if (!(key in raw)) return;
      const v = (raw as Record<string, unknown>)[key];
      if (key === 'approved_date') out[key] = v ?? null;
      else if (key === 'notes') out[key] = v != null ? String(v) : '';
      else if (['contract_type', 'max_compensation_type', 'down_payment_type'].includes(key)) out[key] = strOrNull(v) || null;
      else if (['buy_rate', 'sell_rate', 'gross_margin', 'invoice_amount', 'monthly_payment', 'points'].includes(key)) out[key] = numOrNull(v);
      else out[key] = num(v);
    });
    return out;
  };

  const updateApprovalEconomicsMutation = useMutation({
    mutationFn: async ({ submissionId, data }: { submissionId: number; data: Partial<LenderApprovalEconomics> }) => {
      const payload = buildApprovalEconomicsPayload(data);
      const { data: saved } = await customInstance.patch(
        `/api/lender-submissions/${submissionId}/approval-economics/`,
        payload
      );
      return { submissionId, saved: saved as Record<string, unknown> };
    },
    onSuccess: ({ submissionId, saved }) => {
      if (id && saved) {
        mergeApprovalEconomicsInCache(queryClient, id, submissionId, saved);
        window.setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: applicationQueryKey(id) });
        }, 3000);
      }
      toast.success("Approval economics updated");
    },
    onError: (error: any) => {
      const errorMessage = formatValidationErrors(error);
      toast.error(errorMessage);
    },
  });

  // Fetch and populate approval economics when lender is selected
  const fetchApprovalEconomicsForLender = (lenderId: number | null) => {
    if (!lenderId || !application) return;
    
    try {
      // Find the submission for this lender
      const submission = application.lender_submissions?.find(
        (sub: any) => sub.lender === lenderId
      );
      
      if (submission?.approval_economics) {
        const approval = submission.approval_economics;
        
        // Calculate down payment
        let downPayment = 0;
        if (approval.down_payment_type === 'DOLLARS' && approval.down_payment_value) {
          downPayment = approval.down_payment_value;
        } else if (approval.down_payment_type === 'PERCENT' && approval.down_payment_value && approval.invoice_amount) {
          downPayment = approval.invoice_amount * (approval.down_payment_value / 100);
        }
        
        // Update form state with approval economics data
        setEditedFundedEconomicsData(prev => ({
          ...prev,
          contract_type: approval.contract_type || prev.contract_type,
          dealer_participation: approval.dealer_participation || prev.dealer_participation,
          down_payment: downPayment || prev.down_payment,
          term_months: approval.term_length || prev.term_months,
          lender_doc_fees_collected: approval.lender_doc_fees || prev.lender_doc_fees_collected,
          at_doc_fees: approval.at_doc_fees || prev.at_doc_fees,
          monthly_payment: approval.monthly_payment || prev.monthly_payment,
          number_of_advances: approval.advance_payments || prev.number_of_advances,
          buy_rate: approval.buy_rate || prev.buy_rate,
          sell_rate: approval.sell_rate || prev.sell_rate,
          gross_margin: approval.gross_margin || prev.gross_margin,
          points: approval.points || prev.points,
          invoice_amount: approval.invoice_amount || prev.invoice_amount,
        }));
      }
    } catch (error) {
      console.error("Error fetching approval economics:", error);
    }
  };

  const uploadDocumentMutation = useMutation({
    mutationFn: async ({ files, stipId }: { files: File[]; stipId?: number | null }) => {
      const formData = new FormData();
      // Append all files
      files.forEach(file => {
        formData.append('files', file);
      });
      if (stipId) {
        formData.append('stip_id', stipId.toString());
      }
      const { data } = await customInstance.post(`/api/applications/${id}/documents/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      
      if (data.documents) {
        const uploadedCount = data.documents.filter((d: any) => d.status === 'uploaded').length;
        const duplicateCount = data.documents.filter((d: any) => d.status === 'duplicate').length;
        
        if (uploadedCount > 0) {
          toast.success(`Successfully uploaded ${uploadedCount} document(s)`);
          // Check if any document is tagged to a stip
          const stipDoc = data.documents.find((d: any) => d.document?.stip_name);
          if (stipDoc?.document?.stip_name) {
            toast.success(`Stip "${stipDoc.document.stip_name}" status updated to "Submitted by Customer"`);
          }
        }
        if (duplicateCount > 0) {
          toast.info(`${duplicateCount} document(s) already exist`);
        }
        if (data.errors && data.errors.length > 0) {
          toast.error(`Some files failed: ${data.errors.join('; ')}`);
        }
      } else if (data.status === 'duplicate') {
        toast.info(data.message || 'This file already exists');
      } else if (data.document) {
        toast.success("Document uploaded successfully");
        if (data.document?.stip_name) {
          toast.success(`Stip "${data.document.stip_name}" status updated to "Submitted by Customer"`);
        }
      }
      
      setSelectedStipForUpload(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to upload documents");
    },
    onSettled: () => {
      setUploadingDocument(false);
    },
  });

  const updateDocumentStipMutation = useMutation({
    mutationFn: async ({ documentId, stipId }: { documentId: number; stipId: number | null }) => {
      const { data } = await customInstance.patch(`/api/documents/${documentId}/update-stip/`, {
        stip_id: stipId,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      if (data.document?.stip_name) {
        toast.success(`Document tagged to "${data.document.stip_name}"`);
        // Check if stip status was updated
        const stip = application?.stips?.find(s => s.id === data.document.stip_id);
        if (stip && stip.status === 'REQUESTED') {
          toast.success(`Stip "${data.document.stip_name}" status updated to "Submitted by Customer"`);
        }
      } else {
        toast.success("Stip tag removed from document");
      }
    },
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: number) => {
      await customInstance.delete(`/api/documents/${documentId}/delete/`);
    },
    onSuccess: () => {
      toast.success("Document deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      setDocumentToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to delete document");
      setDocumentToDelete(null);
    },
  });

  const renameDocumentMutation = useMutation({
    mutationFn: async ({ documentId, baseName }: { documentId: number; baseName: string }) => {
      const { data } = await customInstance.patch<{ document?: Document }>(
        `/api/documents/${documentId}/rename/`,
        { base_name: baseName }
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Document renamed");
      queryClient.invalidateQueries({ queryKey: ["application", id] });
      setDocumentRename(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to rename document");
    },
  });

  // File validation helper
  const validateFiles = (files: File[]): { validFiles: File[]; errors: string[] } => {
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif'];
    const validFiles: File[] = [];
    const errors: string[] = [];
    
    for (const file of files) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File exceeds 25MB limit`);
        continue;
      }
      
      // Check file type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        errors.push(`${file.name}: Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    return { validFiles, errors };
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const { validFiles, errors } = validateFiles(files);
    
    // Show errors
    errors.forEach(error => toast.error(error));
    
    if (validFiles.length === 0) return;
    
    setUploadingDocument(true);
    uploadDocumentMutation.mutate({
      files: validFiles,
      stipId: selectedStipForUpload,
    });
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone itself, not a child element
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (uploadingDocument) return;
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    
    const { validFiles, errors } = validateFiles(files);
    
    // Show errors
    errors.forEach(error => toast.error(error));
    
    if (validFiles.length === 0) return;
    
    setUploadingDocument(true);
    uploadDocumentMutation.mutate({
      files: validFiles,
      stipId: selectedStipForUpload,
    });
  };

  const isAdminOrManager = currentUser?.role === 'ORG_ADMIN' || currentUser?.role === 'TERRITORY_SALES_MANAGER' || currentUser?.role === 'DD_ACCOUNT_MANAGER' || currentUser?.role === 'SUPERADMIN';
  const canEditPrimaryEquipmentCustom = isAdminOrManager;
  const isFIManagerOrAdmin = isAdminOrManager;
  /** Org admins: only NOT_STARTED/FAILED and max 3 manual initiations until a successful pull resets the counter. */
  const orgAdminCanManualPullCredit = (owner: ApplicationOwner["owner"]) => {
    if (currentUser?.role !== "ORG_ADMIN" && currentUser?.role !== "TERRITORY_SALES_MANAGER") return true;
    const st = owner.credit_pull_status;
    const cnt = owner.org_admin_manual_credit_pull_count ?? 0;
    return (st === "NOT_STARTED" || st === "FAILED") && cnt < 3;
  };
  const orgAdminPullCreditDisabledTitle = (owner: ApplicationOwner["owner"]) => {
    if (
      (currentUser?.role !== "ORG_ADMIN" && currentUser?.role !== "TERRITORY_SALES_MANAGER") ||
      orgAdminCanManualPullCredit(owner)
    ) {
      return undefined;
    }
    const cnt = owner.org_admin_manual_credit_pull_count ?? 0;
    if (cnt >= 3) {
      return "Maximum manual credit pulls for this owner (3). After a successful pull the limit resets; contact DealerDog support if needed.";
    }
    return "Org admins may only pull credit when status is not started or failed.";
  };
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const isSuperAdminOrAccountManager = isSuperAdmin || currentUser?.role === 'DD_ACCOUNT_MANAGER';
  const canPullPaynet = isSuperAdmin || !!currentUser?.can_pull_paynet;
  const canSubmitAnyLender = isSuperAdmin || !!currentUser?.can_submit_any_lender;
  const canDownloadStaffUnsignedAppPdf =
    isSuperAdmin && !!currentUser?.is_staff && !!currentUser?.is_superuser;

  const DD_APP_DETAIL_LENS_KEY = "dd_app_detail_lens_v1";
  /** Dealer-facing vs internal workspace (DD staff + superadmin only). Synced to ?lens= and localStorage. Default internal when no saved preference. */
  const ddStaffLens = useMemo<"dealer" | "internal" | null>(() => {
    if (!isSuperAdminOrAccountManager) return null;
    const q = searchParams.get("lens");
    if (q === "dealer" || q === "internal") return q;
    if (typeof window !== "undefined") {
      try {
        const s = localStorage.getItem(DD_APP_DETAIL_LENS_KEY);
        if (s === "dealer" || s === "internal") return s;
      } catch {
        /* noop */
      }
    }
    return "internal";
  }, [isSuperAdminOrAccountManager, searchParams]);

  const setDdStaffLens = useCallback(
    (lens: "dealer" | "internal") => {
      try {
        localStorage.setItem(DD_APP_DETAIL_LENS_KEY, lens);
      } catch {
        /* noop */
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("lens", lens);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const ddInternalLens = ddStaffLens === "internal";
  const ddDealerLens = ddStaffLens === "dealer";

  /** Superadmin + DD Account Manager: reorder right-column Prequal -> Screening -> Lenders (internal lens only) */
  const internalDdReorderSidebar =
    (isSuperAdmin || currentUser?.role === "DD_ACCOUNT_MANAGER") && ddInternalLens;
  const showMarketplaceSourceCard =
    isSuperAdminOrAccountManager &&
    application?.organization_type === "MARKETPLACE_PARTNER" &&
    ddInternalLens;
  const isSales = currentUser?.role === 'SALES';
  /** True when we must never show lender names (API mask or org_admin/sales role). Use for all offer labels. */
  const effectiveMaskLenderNames = !!(
    application?.mask_lender_names ||
    currentUser?.role === 'ORG_ADMIN' ||
    currentUser?.role === 'TERRITORY_SALES_MANAGER' ||
    currentUser?.role === 'SALES' ||
    (isSuperAdminOrAccountManager && ddDealerLens)
  );

  const maskedOfferLabels = useMemo(
    () =>
      effectiveMaskLenderNames && application?.prequal_results
        ? maskedOfferLabelByLenderId(application.prequal_results)
        : new Map<number, string>(),
    [effectiveMaskLenderNames, application?.prequal_results]
  );

  /** Manual Review is a real lender (seed_manual_review_lender) so we use same submit/email flow; no sentinel id. */
  const canEditApplicationNotes = !isSales || (isSales && application?.assigned_sales_rep?.id === currentUser?.id);

  useEffect(() => {
    requestedAmountBackfillDoneRef.current = null;
  }, [id]);

  // Persist requested_amount when UI shows equipment price as the financing amount (portal fallback).
  useEffect(() => {
    if (!id || !application || currentUser?.role === "SALES" || isEditingAmount) return;
    if (hasPersistedRequestedAmount(application)) return;
    const ep = application.equipment_price;
    if (ep == null || ep === "" || Number(ep) === 0) return;
    if (requestedAmountBackfillDoneRef.current === application.id) return;
    requestedAmountBackfillDoneRef.current = application.id;
    const amountStr = String(ep).replace(/,/g, "");
    customInstance
      .patch(`/api/applications/${id}/`, { requested_amount: amountStr })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["application", id] });
      })
      .catch(() => {
        requestedAmountBackfillDoneRef.current = null;
      });
  }, [id, application, isEditingAmount, queryClient, currentUser?.role]);

  // Helper function to check if funded economics is empty (exists but hasn't been filled in)
  const isFundedEconomicsEmpty = (economics: FundedDealEconomics | null | undefined): boolean => {
    if (!economics) {
      console.log('[Funded Modal Debug] No economics object');
      return true;
    }
    
    // Check if key fields are still at default/empty values
    // Convert string values to numbers for comparison (API returns DecimalField as strings)
    const equipmentCost = typeof economics.equipment_cost === 'string' 
      ? parseFloat(economics.equipment_cost) 
      : economics.equipment_cost;
    const monthlyPayment = typeof economics.monthly_payment === 'string' 
      ? parseFloat(economics.monthly_payment) 
      : economics.monthly_payment;
    
    const isEmpty = (
      !economics.lender_financed_with &&
      economics.term_months === 0 &&
      equipmentCost === 0 &&
      monthlyPayment === 0
    );
    
    console.log('[Funded Modal Debug] Economics check:', {
      economics,
      lender_financed_with: economics.lender_financed_with,
      term_months: economics.term_months,
      equipment_cost: economics.equipment_cost,
      equipment_cost_parsed: equipmentCost,
      monthly_payment: economics.monthly_payment,
      monthly_payment_parsed: monthlyPayment,
      isEmpty
    });
    
    return isEmpty;
  };

  // Stip status display labels
  const stipStatusLabels: Record<string, string> = {
    REQUESTED: "Requested",
    SUBMITTED_BY_CUSTOMER: "Submitted by Customer",
    SUBMITTED_TO_LENDER: "Submitted to Lender",
    CLEARED: "Cleared",
  };

  // Show funded economics modal when stage is FUNDED and economics empty (DD staff internal lens)
  useEffect(() => {
    if (
      application?.stage === "FUNDED" &&
      isSuperAdminOrAccountManager &&
      ddInternalLens &&
      isFundedEconomicsEmpty(application.funded_deal_economics) &&
      id
    ) {
      const hasShown = sessionStorage.getItem(`funded_economics_modal_shown_${id}`);
      if (!hasShown) {
        economicsModalsRef.current?.openFunded();
        sessionStorage.setItem(`funded_economics_modal_shown_${id}`, "true");
      }
    }
  }, [
    application?.stage,
    application?.funded_deal_economics,
    isSuperAdminOrAccountManager,
    ddInternalLens,
    id,
  ]);

  useEffect(() => {
    if (!isSuperAdminOrAccountManager || !ddDealerLens) return;
    setSelectedLender(null);
    setEmailBody("");
    setCcEmails("");
    setEmailSubject("");
    setLenderApiSubmissionNotes("");
    setSelectedDocuments([]);
    setUploadedFiles([]);
    setLenderSubmitDragging(false);
    setUnmatchedReasonsDialog(null);
  }, [isSuperAdminOrAccountManager, ddDealerLens]);

  if (isLoading) return <div>Loading...</div>;
  if (!application) return <div>Application not found</div>;

  const stageColors: Record<string, string> = {
    CREATED: "#3b82f6",
    RECEIVED: "#6b7280",
    IN_REVIEW: "#eab308",
    SUBMITTED: "#3b82f6",
    APPROVED: "#22c55e",
    CONTRACTS_REQUESTED: "#f59e0b",
    CONTRACTS_OUT: "#a855f7",
    CONTRACTS_IN: "#6366f1",
    FUNDED: "#10b981",
    DECLINED: "#ef4444",
    DEAL_LOST: "#ef4444",
  };

  const stageLabels: Record<string, string> = {
    CREATED: "Created",
    RECEIVED: "Received",
    IN_REVIEW: "In Review",
    SUBMITTED: "Submitted",
    APPROVED: "Approved",
    CONTRACTS_REQUESTED: "Contracts Requested",
    CONTRACTS_OUT: "Contracts Out",
    CONTRACTS_IN: "Contracts In",
    FUNDED: "Funded",
    DECLINED: "Declined",
    DEAL_LOST: "Deal Lost",
  };

  const isCorpOnlyApplication =
    application.submission_mode === "CORP_ONLY" && !application.corp_only_converted_to_full_at;

  const hideDealerPrequalAfterApproval =
    (currentUser?.role === "ORG_ADMIN" ||
      currentUser?.role === "TERRITORY_SALES_MANAGER" ||
      currentUser?.role === "SALES") &&
    (application.lender_submissions ?? []).some((s: LenderSubmission) => Boolean(s.approval_economics));

  const paynetReports = application.company?.paynet_reports ?? [];
  const latestCompletedPaynetReport = [...paynetReports]
    .filter(
      (r: { status: string; pdf_path?: string | null; html_path?: string | null }) =>
        r.status === "COMPLETED" && (r.pdf_path || r.html_path)
    )
    .sort(
      (a: { created_at: string }, b: { created_at: string }) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0] ?? null;
  const hasViewableCommercialCredit = Boolean(
    latestCompletedPaynetReport ||
      application.company?.paynet_pdf_path ||
      application.company?.paynet_html_path
  );
  const canDownloadPaynet =
    isSuperAdminOrAccountManager ||
    currentUser?.role === "ORG_ADMIN" ||
    currentUser?.role === "TERRITORY_SALES_MANAGER" ||
    currentUser?.role === "SALES";
  const showCommercialCreditCard =
    Boolean(application.company) &&
    !ddInternalLens &&
    canDownloadPaynet &&
    hasViewableCommercialCredit;
  const showPaynetInternalCard =
    Boolean(application.company) && ddInternalLens && isSuperAdminOrAccountManager;
  const paynetPullInProgress = paynetReports.some(
    (r: { status: string }) => r.status === "IN_PROGRESS" || r.status === "NOT_STARTED"
  );

  const lenderStatusColors: Record<string, string> = {
    NOT_SUBMITTED: "bg-gray-500",
    SUBMITTED: "bg-blue-500",
    NEEDS_INFO: "bg-yellow-500",
    CONDITIONALLY_APPROVED: "bg-green-500",
    APPROVED: "bg-green-600",
    DOCS_REQUESTED: "bg-amber-500",
    DOCS_OUT: "bg-purple-500",
    DOCS_IN: "bg-indigo-500",
    FUNDED: "bg-emerald-500",
    DECLINED: "bg-red-500",
  };

  return (
    <div className="staff-app-detail space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-4">
          <div className="w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-3">
              <select
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white cursor-pointer shadow-sm appearance-none bg-no-repeat bg-right pr-8 w-full sm:w-auto"
                style={{ 
                  backgroundColor: stageColors[application.stage] || "#6b7280",
                  backgroundImage: (currentUser?.role === 'SUPERADMIN' || currentUser?.role === 'DD_ACCOUNT_MANAGER') ? "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")" : "none",
                  cursor: (currentUser?.role === 'SUPERADMIN' || currentUser?.role === 'DD_ACCOUNT_MANAGER') ? "pointer" : "default"
                }}
                value={application.stage}
                disabled={currentUser?.role !== 'SUPERADMIN' && currentUser?.role !== 'DD_ACCOUNT_MANAGER'}
                onChange={async (e) => {
                  if (currentUser?.role !== 'SUPERADMIN' && currentUser?.role !== 'DD_ACCOUNT_MANAGER') return;
                  const newStage = e.target.value;
                  const previousStage = application.stage;
                  if (id) {
                    patchApplicationCache(queryClient, id, (p) => ({ ...p, stage: newStage }));
                  }
                  try {
                    await customInstance.patch(`/api/applications/${id}/`, { stage: newStage });
                    toast.success("Stage updated");
                    queryClient.invalidateQueries({ queryKey: ["stage-counts"] });
                    if (id) {
                      window.setTimeout(() => {
                        queryClient.invalidateQueries({ queryKey: applicationQueryKey(id) });
                      }, 2500);
                    }
                    
                    // Clear sessionStorage flag when moving away from FUNDED so modal can show again if moved back
                    if (application?.stage === 'FUNDED' && newStage !== 'FUNDED') {
                      sessionStorage.removeItem(`funded_economics_modal_shown_${id}`);
                    }
                    
                    // If moving to FUNDED and user is DD Account Manager or Super Admin (not org admin/sales), show modal only if economics is empty
                    if (
                      newStage === "FUNDED" &&
                      isSuperAdminOrAccountManager &&
                      ddInternalLens &&
                      isFundedEconomicsEmpty(application?.funded_deal_economics)
                    ) {
                      // Check sessionStorage to avoid showing if already shown
                      const hasShown = sessionStorage.getItem(`funded_economics_modal_shown_${id}`);
                      if (!hasShown) {
                        // Wait a bit for the query to refetch, then show modal
                        setTimeout(() => {
                          economicsModalsRef.current?.openFunded();
                          sessionStorage.setItem(`funded_economics_modal_shown_${id}`, "true");
                        }, 500);
                      }
                    }
                  } catch (error: any) {
                    if (id) {
                      patchApplicationCache(queryClient, id, (p) => ({ ...p, stage: previousStage }));
                    }
                    toast.error("Failed to update stage");
                  }
                }}
              >
                <option value="CREATED">Created</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RECEIVED">Received</option>
                <option value="IN_REVIEW">In Review</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="APPROVED">Approved</option>
                <option value="CONTRACTS_REQUESTED">Contracts Requested</option>
                <option value="CONTRACTS_OUT">Contracts Out</option>
                <option value="CONTRACTS_IN">Contracts In</option>
                <option value="FUNDED">Funded</option>
                <option value="DECLINED">Declined</option>
                <option value="DEAL_LOST">Deal Lost</option>
              </select>
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-4">
                <Badge variant={application.application_signed ? "default" : "destructive"}>
                  {application.application_signed ? "Application Signed" : "Application Not Signed"}
                </Badge>
                {application.application_signed && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadSignedPdfMutation.mutate()}
                    disabled={downloadSignedPdfMutation.isPending}
                    className="text-xs sm:text-sm"
                  >
                    {downloadSignedPdfMutation.isPending ? "Downloading..." : "Download app pdf"}
                  </Button>
                )}
                {canDownloadStaffUnsignedAppPdf && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      downloadUnsignedStaffAppPdfMutation.mutate(
                        application.internal_id ?? String(id)
                      )
                    }
                    disabled={downloadUnsignedStaffAppPdfMutation.isPending}
                    className="text-xs sm:text-sm"
                    title="Same packet template as signed flow, without e-signature (platform superuser only)"
                  >
                    {downloadUnsignedStaffAppPdfMutation.isPending ? "Downloading…" : "Unsigned app PDF"}
                  </Button>
                )}
                {canDownloadStaffUnsignedAppPdf && !isCorpOnlyApplication && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      downloadRedactedStaffAppPdfMutation.mutate(
                        application.internal_id ?? String(id)
                      )
                    }
                    disabled={downloadRedactedStaffAppPdfMutation.isPending}
                    className="text-xs sm:text-sm"
                    title="Staff-only corporate-only application PDF export"
                  >
                    {downloadRedactedStaffAppPdfMutation.isPending ? "Downloading…" : "Corp Only App PDF"}
                  </Button>
                )}
                {isCorpOnlyApplication && (
                  <Badge variant="secondary" className="bg-indigo-100 text-indigo-900 border-indigo-200">
                    Corp-only
                  </Badge>
                )}
                {application.corp_only_converted_to_full_at && (
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-900 border-emerald-200">
                    Full guarantor
                  </Badge>
                )}
                {application.stips_requested && <Badge variant="secondary">Stips Requested</Badge>}
                {application.stage === "RECEIVED" && (
                  application.prequal_selection ? (
                    <Badge className="bg-green-600">Offer selected</Badge>
                  ) : (
                    <Badge variant="secondary">No offer selected</Badge>
                  )
                )}
                {application.is_archived && (
                  <Badge variant="secondary" className="bg-gray-500">Archived</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <p className="text-gray-600">ID: {application.internal_id}</p>
              {isFIManagerOrAdmin && (
                <>
                  {application.is_archived ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => unarchiveMutation.mutate()}
                      disabled={unarchiveMutation.isPending}
                      className="text-xs sm:text-sm"
                    >
                      {unarchiveMutation.isPending ? "Unarchiving..." : "Unarchive"}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => archiveMutation.mutate()}
                      disabled={archiveMutation.isPending}
                      className="text-xs sm:text-sm"
                    >
                      {archiveMutation.isPending ? "Archiving..." : "Archive"}
                    </Button>
                  )}
                </>
              )}
            </div>
            {(application as any).invite_url && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {application.stage === 'RECEIVED' ? 'Complete equipment link:' : 'Application link:'}
                </span>
                <Input readOnly className="max-w-lg" value={(application as any).invite_url} />
                <Button size="sm" onClick={() => navigator.clipboard.writeText((application as any).invite_url)}>Copy</Button>
              </div>
            )}
            {isSuperAdminOrAccountManager && ddInternalLens && (
              <div className="mt-2">
                <Button size="sm" variant="secondary" type="button" onClick={() => setShowCustomerSignModal(true)}>
                  Customer sign link
                </Button>
              </div>
            )}
            {editingEquipmentName ? (
              <div className="mt-4 flex gap-2 items-center">
                <Input
                  type="text"
                  value={editedEquipmentName}
                  onChange={(e) => setEditedEquipmentName(e.target.value)}
                  className="text-2xl font-bold max-w-2xl"
                />
                <Button size="sm" onClick={() => updateEquipmentNameMutation.mutate(editedEquipmentName)}>
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  setEditingEquipmentName(false);
                  setEditedEquipmentName(application.equipment_name || "");
                }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="mt-4 flex gap-2 items-center">
                <h1 className="text-2xl font-bold text-gray-900">{application.equipment_name}</h1>
                {!isSales && (
                  <Button size="sm" variant="ghost" onClick={() => setEditingEquipmentName(true)} className="p-1">
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            {application.equipment_type?.name && (
              <p className="mt-1 text-sm text-gray-600">
                Equipment type: <span className="font-medium text-gray-800">{application.equipment_type.name}</span>
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div>
            <Label>Sales Rep</Label>
            <div className="mt-1">
              <select
                className="w-full max-w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={application.assigned_sales_rep?.id || ""}
                disabled={isSales}
                onChange={async (e) => {
                  if (isSales) return;
                  try {
                    const repId = e.target.value ? parseInt(e.target.value) : null;
                    await customInstance.patch(`/api/applications/${id}/`, { 
                      assigned_sales_rep_id: repId
                    });
                    toast.success("Sales rep assigned");
                    queryClient.invalidateQueries({ queryKey: ["application", id] });
                    queryClient.invalidateQueries({ queryKey: ["applications"] });
                  } catch (error: any) {
                    console.error("Assign sales rep error:", error);
                    toast.error("Failed to assign sales rep");
                  }
                }}
              >
                <option value="">-- Select Sales Rep --</option>
                {salesReps
                  ?.filter(
                    (rep) =>
                      rep.is_active !== false ||
                      rep.id === application.assigned_sales_rep?.id
                  )
                  .map((rep) => (
                  <option key={rep.id} value={rep.id}>
                    {rep.first_name} {rep.last_name}
                    {rep.is_active === false ? " (inactive)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label>Equipment Price</Label>
            {isEditingPrice ? (
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  value={editedPrice}
                  onChange={(e) => setEditedPrice(e.target.value)}
                  className="w-32"
                />
                <Button size="sm" onClick={() => updatePriceMutation.mutate(editedPrice)}>
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditingPrice(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1">
                <p className="text-lg font-semibold text-gray-900">
                  ${Number(application.equipment_price || 0).toLocaleString()}
                </p>
                {!isSales && (
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingPrice(true)} className="p-1">
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>Requested Amount</Label>
            {isEditingAmount ? (
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  value={editedAmount}
                  onChange={(e) => setEditedAmount(e.target.value)}
                  className="w-32"
                />
                <Button size="sm" onClick={() => updateAmountMutation.mutate(editedAmount)}>
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditingAmount(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1">
                <p className="text-lg font-semibold text-gray-900">
                  ${Number(application.requested_amount ?? application.equipment_price ?? 0).toLocaleString()}
                </p>
                {!isSales && (
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingAmount(true)} className="p-1">
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>Est. Close Date</Label>
            {isEditingEstCloseDate ? (
              <div className="flex gap-2 mt-1">
                <Input
                  type="date"
                  value={editedEstCloseDate}
                  onChange={(e) => setEditedEstCloseDate(e.target.value)}
                  className="w-40"
                />
                <Button size="sm" onClick={() => updateEstCloseDateMutation.mutate(editedEstCloseDate || null)}>
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  setIsEditingEstCloseDate(false);
                  setEditedEstCloseDate(formatDateForInput(application.est_close_date));
                }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1">
                <p className="text-lg font-semibold text-gray-900">
                  {application.est_close_date ? formatDateOnly(application.est_close_date) : "Not set"}
                </p>
                {!isSales && (
                  <Button size="sm" variant="ghost" onClick={() => setIsEditingEstCloseDate(true)} className="p-1">
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          {isSuperAdminOrAccountManager && ddInternalLens && (
            <div>
              <Label>Organization</Label>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {(application.organization_name || "").trim() || "—"}
              </p>
            </div>
          )}
        </div>

        {isSuperAdminOrAccountManager && ddStaffLens && (
          <div className={staffUi.workspaceStrip}>
            <div className="min-w-0 space-y-1">
              <p className={staffUi.workspaceEyebrow}>Workspace</p>
              <p className={staffUi.workspaceBody}>
                {ddDealerLens ? (
                  <>
                    <span className="font-semibold text-slate-800">Dealer view</span> — streamlined
                    layout, masked offers, no lender pipeline (switch to Internal for that and other staff-only tools).
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-violet-900">Internal view</span> — source
                    data, org context, team threads, lenders, and full funded economics editing.
                  </>
                )}
              </p>
            </div>
            <DdStaffLensToggle value={ddStaffLens} onChange={setDdStaffLens} className="shrink-0 w-full sm:w-auto" />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-4 sm:gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {showMarketplaceSourceCard && application && (() => {
            const meta =
              application.source_metadata && typeof application.source_metadata === "object"
                ? application.source_metadata
                : {};
            const metaStr = (k: string) => {
              const v = meta[k];
              if (v == null) return "";
              const s = String(v).trim();
              return s;
            };
            const hasMetaContent = ["source_platform", "source_listing_id", "listing_url", "category_hint", "stock_number"].some(
              (k) => metaStr(k) !== ""
            );
            const sd = application.source_dealer;
            const firstEq = application.equipments?.[0];
            const equipParts = [
              application.equipment_year ?? firstEq?.year,
              firstEq?.make,
              firstEq?.model_name,
              metaStr("category_hint") || undefined,
            ].filter((x) => x != null && String(x).trim() !== "");
            const rawListing = metaStr("listing_url") || (application.ref_url || "").trim();
            const listingHref = resolveFastlineListingHref(rawListing, metaStr("source_platform"));
            const formatDt = (iso: string | undefined) => {
              if (!iso) return "—";
              try {
                return new Date(iso).toLocaleString();
              } catch {
                return iso;
              }
            };
            return (
              <Card>
                <CardHeader>
                  <CardTitle>Source</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  {!sd && !hasMetaContent && (
                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                      No source metadata was passed for this application. This may indicate a template configuration
                      issue on the partner side.
                    </p>
                  )}
                  {metaStr("source_platform") && (
                    <div>
                      <span className="font-medium text-gray-700">Source platform: </span>
                      <span className="text-gray-900">{metaStr("source_platform")}</span>
                    </div>
                  )}
                  {listingHref && (
                    <div>
                      <span className="font-medium text-gray-700">Listing URL: </span>
                      <a
                        href={listingHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 underline break-all"
                      >
                        {listingHref}
                      </a>
                    </div>
                  )}
                  {equipParts.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Equipment summary: </span>
                      <span className="text-gray-900">{equipParts.map(String).join(" / ")}</span>
                    </div>
                  )}
                  {metaStr("stock_number") && (
                    <div>
                      <span className="font-medium text-gray-700">Stock number: </span>
                      <span className="text-gray-900">{metaStr("stock_number")}</span>
                    </div>
                  )}
                  {application.intake_bootstrap_context &&
                    Object.keys(application.intake_bootstrap_context).length > 0 && (
                    <div className="rounded-md border border-gray-200 bg-slate-50 p-3 space-y-1 text-xs">
                      <div className="font-semibold text-gray-900 text-sm">Bootstrap (diagnostics)</div>
                      <p className="text-gray-600 mb-1">
                        Sanitized request context when this application was created (untrusted). Use with partner
                        traffic and ALB logs.
                      </p>
                      <dl className="space-y-1">
                        {Object.entries(application.intake_bootstrap_context).map(([k, v]) => (
                          <div key={k}>
                            <dt className="inline font-medium text-gray-700">{k}: </dt>
                            <dd className="inline text-gray-900 break-all">{String(v)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                  {sd && (
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                      <div className="font-semibold text-gray-900">Selling dealer</div>
                      <div>
                        <span className="font-medium text-gray-700">Name: </span>
                        {sd.name}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Location: </span>
                        {[sd.city, sd.state, sd.postal_code].filter(Boolean).join(", ") || "—"}
                      </div>
                      {sd.phone && (
                        <div>
                          <span className="font-medium text-gray-700">Phone: </span>
                          <span>{sd.phone}</span>
                          <span className="text-gray-500"> (masked — partner-proxied)</span>
                        </div>
                      )}
                      {(sd.contact_name || sd.email) && (
                        <div>
                          <span className="font-medium text-gray-700">Contact: </span>
                          {[sd.contact_name, sd.email].filter(Boolean).join(" · ") || "—"}
                        </div>
                      )}
                      <div className="text-xs text-gray-600 pt-1 border-t border-gray-200 mt-2">
                        <div>First seen: {formatDt(sd.created_at)}</div>
                        <div>Last seen: {formatDt(sd.updated_at)}</div>
                        <div>Total applications from this dealer: {sd.application_count}</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
          {/* Contact Person Card (if exists) */}
          {application.contact_person && (() => {
            const contactPersonOwnerId = application.contact_person.owner.id;
            const isEditingContactPerson = editingOwner[contactPersonOwnerId] || false;
            const contactPersonData = editedOwnerData[contactPersonOwnerId] || {};

            const startEditingContactPerson = () => {
              setEditingOwner(prev => ({ ...prev, [contactPersonOwnerId]: true }));
              setEditedOwnerData(prev => ({
                ...prev,
                [contactPersonOwnerId]: {
                  first_name: application.contact_person.owner.first_name,
                  last_name: application.contact_person.owner.last_name,
                  email: application.contact_person.owner.email,
                  phone: formatPhone(application.contact_person.owner.phone),
                }
              }));
            };

            const cancelEditingContactPerson = () => {
              setEditingOwner(prev => {
                const newState = { ...prev };
                delete newState[contactPersonOwnerId];
                return newState;
              });
            };

            const saveContactPerson = () => {
              const data: any = {
                first_name: contactPersonData.first_name,
                last_name: contactPersonData.last_name,
                email: contactPersonData.email,
                phone: toDigits(contactPersonData.phone || ""),
              };
              updateOwnerMutation.mutate({ ownerId: contactPersonOwnerId, data }, {
                onSuccess: () => {
                  cancelEditingContactPerson();
                }
              });
            };

            return (
              <Card>
                <CardHeader className="relative">
                  <CardTitle>Contact Person</CardTitle>
                  {!isSales && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={isEditingContactPerson ? cancelEditingContactPerson : startEditingContactPerson}
                      className="absolute top-2 right-2 p-1"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {isEditingContactPerson ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">First Name</Label>
                          <Input
                            type="text"
                            value={contactPersonData.first_name || ""}
                            onChange={(e) => setEditedOwnerData(prev => ({
                              ...prev,
                              [contactPersonOwnerId]: { ...prev[contactPersonOwnerId], first_name: e.target.value }
                            }))}
                            placeholder="First Name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Last Name</Label>
                          <Input
                            type="text"
                            value={contactPersonData.last_name || ""}
                            onChange={(e) => setEditedOwnerData(prev => ({
                              ...prev,
                              [contactPersonOwnerId]: { ...prev[contactPersonOwnerId], last_name: e.target.value }
                            }))}
                            placeholder="Last Name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input
                            type="email"
                            value={contactPersonData.email || ""}
                            onChange={(e) => setEditedOwnerData(prev => ({
                              ...prev,
                              [contactPersonOwnerId]: { ...prev[contactPersonOwnerId], email: e.target.value }
                            }))}
                            placeholder="Email"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Phone</Label>
                          <Input
                            type="text"
                            value={contactPersonData.phone || ""}
                            onChange={(e) => handlePhoneChange(e.target.value, (val) => setEditedOwnerData(prev => ({
                              ...prev,
                              [contactPersonOwnerId]: { ...prev[contactPersonOwnerId], phone: val }
                            })))}
                            placeholder="Phone"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2 border-t">
                        <Button size="sm" onClick={saveContactPerson}>Save</Button>
                        <Button size="sm" variant="outline" onClick={cancelEditingContactPerson}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {application.contact_person.owner.first_name} {application.contact_person.owner.last_name}
                        </h4>
                        <p className="text-xs text-blue-600 font-medium mt-0.5">Contact Person</p>
                      </div>
                      <div className="space-y-1 mt-2">
                        <p className="text-sm text-gray-600">
                          <a href={`mailto:${application.contact_person.owner.email}`} className="text-blue-600 hover:underline">
                            {application.contact_person.owner.email}
                          </a>
                        </p>
                        <p className="text-sm text-gray-600">
                          <a 
                            href={`tel:${application.contact_person.owner.phone.replace(/\D/g, '')}`} 
                            className="text-blue-600 hover:underline"
                          >
                            {formatPhone(application.contact_person.owner.phone)}
                          </a>
                        </p>
                        {application.contact_person.owner.address_1 && (
                          <p className="text-sm text-gray-500">
                            {[
                              application.contact_person.owner.address_1,
                              application.contact_person.owner.address_2,
                              application.contact_person.owner.city,
                              application.contact_person.owner.state,
                              application.contact_person.owner.postal_code
                            ].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
          
          {/* Owners Card */}
          <Card>
            <CardHeader className="relative flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-4">
              <CardTitle>Owners / Guarantors</CardTitle>
              {isSuperAdminOrAccountManager && ddInternalLens && (
                <Button size="sm" variant="secondary" type="button" onClick={() => setShowAddOwnerModal(true)}>
                  Add guarantor
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {application.owners && application.owners.length > 0 ? (
                  // Filter out CONTACT_PERSON role from owners list
                  application.owners
                    .filter((appOwner: ApplicationOwner) => appOwner.role_label !== 'CONTACT_PERSON')
                    .map((appOwner: ApplicationOwner, idx: number) => {
                    const ownerId = appOwner.owner.id;
                    const isEditing = editingOwner[ownerId] || false;
                    const ownerData = editedOwnerData[ownerId] || {};

                    const startEditing = () => {
                      setEditingOwner(prev => ({ ...prev, [ownerId]: true }));
                      setEditedOwnerData(prev => ({
                        ...prev,
                        [ownerId]: {
                          first_name: appOwner.owner.first_name,
                          last_name: appOwner.owner.last_name,
                          email: appOwner.owner.email,
                          phone: formatPhone(appOwner.owner.phone),
                          address_1: appOwner.owner.address_1 || "",
                          address_2: appOwner.owner.address_2 || "",
                          city: appOwner.owner.city || "",
                          state: appOwner.owner.state || "",
                          postal_code: appOwner.owner.postal_code || "",
                          ssn: appOwner.owner.ssn ? formatSSN(appOwner.owner.ssn) : "",
                          dob: appOwner.owner.dob ? formatDOB(appOwner.owner.dob) : "",
                        }
                      }));
                    };

                    const cancelEditing = () => {
                      setEditingOwner(prev => {
                        const newState = { ...prev };
                        delete newState[ownerId];
                        return newState;
                      });
                    };

                    const saveOwner = () => {
                      const data: any = {
                        first_name: ownerData.first_name,
                        last_name: ownerData.last_name,
                        email: ownerData.email,
                        phone: toDigits(ownerData.phone || ""),
                        address_1: ownerData.address_1,
                        address_2: ownerData.address_2,
                        city: ownerData.city,
                        state: ownerData.state,
                        postal_code: ownerData.postal_code,
                      };
                      // Only include SSN and DOB if user is F&I Manager or Admin
                      if (isFIManagerOrAdmin) {
                        if (ownerData.ssn !== undefined) {
                          data.ssn = toDigits(ownerData.ssn || "");
                        }
                        if (ownerData.dob !== undefined) {
                          // Convert MM/DD/YYYY to YYYY-MM-DD for backend
                          data.dob = convertDOBForBackend(ownerData.dob);
                        }
                      }
                      updateOwnerMutation.mutate({ ownerId, data }, {
                        onSuccess: () => {
                          cancelEditing();
                        }
                      });
                    };

                    return (
                    <div key={idx} className="border-l-4 border-blue-600 pl-4 py-3 pr-4 bg-gray-50 rounded-r-md space-y-3 relative">
                      {isFIManagerOrAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={isEditing ? cancelEditing : startEditing}
                          className="absolute top-2 right-2 p-1"
                        >
                          <PencilIcon className={isEditing ? "w-5 h-5" : "w-5 h-5"} />
                        </Button>
                      )}
                      
                      {isEditing ? (
                        <div className="space-y-3 pr-8">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">First Name</Label>
                              <Input
                                type="text"
                                value={ownerData.first_name || ""}
                                onChange={(e) => setEditedOwnerData(prev => ({
                                  ...prev,
                                  [ownerId]: { ...prev[ownerId], first_name: e.target.value }
                                }))}
                                placeholder="First Name"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Last Name</Label>
                              <Input
                                type="text"
                                value={ownerData.last_name || ""}
                                onChange={(e) => setEditedOwnerData(prev => ({
                                  ...prev,
                                  [ownerId]: { ...prev[ownerId], last_name: e.target.value }
                                }))}
                                placeholder="Last Name"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Email</Label>
                            <Input
                              type="email"
                              value={ownerData.email || ""}
                              onChange={(e) => setEditedOwnerData(prev => ({
                                ...prev,
                                [ownerId]: { ...prev[ownerId], email: e.target.value }
                              }))}
                              placeholder="Email"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Phone</Label>
                            <Input
                              type="text"
                              value={ownerData.phone || ""}
                              onChange={(e) => handlePhoneChange(e.target.value, (val) => setEditedOwnerData(prev => ({
                                ...prev,
                                [ownerId]: { ...prev[ownerId], phone: val }
                              })))}
                              placeholder="xxx-xxx-xxxx"
                            />
                          </div>
                          <div className="space-y-2 border-t pt-2">
                            <div>
                              <Label className="text-xs">Street Address 1</Label>
                              <Input
                                type="text"
                                value={ownerData.address_1 || ""}
                                onChange={(e) => setEditedOwnerData(prev => ({
                                  ...prev,
                                  [ownerId]: { ...prev[ownerId], address_1: e.target.value }
                                }))}
                                placeholder="Street Address 1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Street Address 2 (optional)</Label>
                              <Input
                                type="text"
                                value={ownerData.address_2 || ""}
                                onChange={(e) => setEditedOwnerData(prev => ({
                                  ...prev,
                                  [ownerId]: { ...prev[ownerId], address_2: e.target.value }
                                }))}
                                placeholder="Street Address 2"
                              />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              <div>
                                <Label className="text-xs">City</Label>
                                <Input
                                  type="text"
                                  value={ownerData.city || ""}
                                  onChange={(e) => setEditedOwnerData(prev => ({
                                    ...prev,
                                    [ownerId]: { ...prev[ownerId], city: e.target.value }
                                  }))}
                                  placeholder="City"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">State</Label>
                                <select
                                  value={ownerData.state || ""}
                                  onChange={(e) => setEditedOwnerData(prev => ({
                                    ...prev,
                                    [ownerId]: { ...prev[ownerId], state: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 border rounded"
                                >
                                  <option value="">State</option>
                                  {US_STATES.map(state => (
                                    <option key={state.value} value={state.value}>{state.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <Label className="text-xs">Postal Code</Label>
                                <Input
                                  type="text"
                                  value={ownerData.postal_code || ""}
                                  onChange={(e) => setEditedOwnerData(prev => ({
                                    ...prev,
                                    [ownerId]: { ...prev[ownerId], postal_code: e.target.value }
                                  }))}
                                  placeholder="Postal Code"
                                />
                              </div>
                            </div>
                          </div>
                          {isFIManagerOrAdmin && (
                            <div className="space-y-2 border-t pt-2">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">SSN</Label>
                                  <Input
                                    type="text"
                                    value={ownerData.ssn || ""}
                                    onChange={(e) => handleSSNChange(e.target.value, (val) => setEditedOwnerData(prev => ({
                                      ...prev,
                                      [ownerId]: { ...prev[ownerId], ssn: val }
                                    })))}
                                    placeholder="xxx-xx-xxxx"
                                    maxLength={11}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Date of Birth</Label>
                                  <Input
                                    type="text"
                                    value={ownerData.dob || ""}
                                    onChange={(e) => handleDOBChange(e.target.value, (val) => {
                                      setEditedOwnerData(prev => ({
                                        ...prev,
                                        [ownerId]: { ...prev[ownerId], dob: val }
                                      }));
                                    })}
                                    placeholder="MM/DD/YYYY"
                                    maxLength={10}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2 pt-2 border-t">
                            <Button size="sm" onClick={saveOwner}>Save</Button>
                            <Button size="sm" variant="outline" onClick={cancelEditing}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="pr-8">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {appOwner.owner.first_name} {appOwner.owner.last_name}
                            </h4>
                            <p className="text-xs text-blue-600 font-medium mt-0.5">{appOwner.role_label}</p>
                          </div>
                          {appOwner.ownership_percent !== null && appOwner.ownership_percent !== undefined && (
                            <p className="text-sm font-medium text-gray-700 mt-2">
                              Ownership Percentage: {typeof appOwner.ownership_percent === 'number' ? appOwner.ownership_percent : parseFloat(String(appOwner.ownership_percent))}%
                            </p>
                          )}
                          <div className="space-y-1 mt-2">
                            <p className="text-sm text-gray-600">
                              <a href={`mailto:${appOwner.owner.email}`} className="text-blue-600 hover:underline">
                                {appOwner.owner.email}
                              </a>
                            </p>
                            <p className="text-sm text-gray-600">
                              <a 
                                href={`tel:${appOwner.owner.phone.replace(/\D/g, '')}`} 
                                className="text-blue-600 hover:underline"
                              >
                                {formatPhone(appOwner.owner.phone)}
                              </a>
                            </p>
                            <p className="text-sm text-gray-500">
                              {[
                                appOwner.owner.address_1,
                                appOwner.owner.address_2,
                                appOwner.owner.city,
                                appOwner.owner.state,
                                appOwner.owner.postal_code
                              ].filter(Boolean).join(', ')}
                            </p>
                            {(appOwner.owner.ssn || appOwner.owner.dob) && (
                              <div className="mt-2 space-y-1">
                                {appOwner.owner.ssn && (
                                  <p className="text-sm text-gray-600">
                                    <span className="text-gray-500">SSN:</span>{" "}
                                    {isFIManagerOrAdmin ? maskSSN(appOwner.owner.ssn) : "***-**-****"}
                                  </p>
                                )}
                                {appOwner.owner.dob && (
                                  <p className="text-sm text-gray-600">
                                    <span className="text-gray-500">DOB:</span>{" "}
                                    {isFIManagerOrAdmin ? formatDOB(appOwner.owner.dob) : "********"}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          {appOwner.owner.credit_score !== null && appOwner.owner.credit_score !== undefined && (
                            <p className="text-sm font-medium text-gray-700 mt-2">
                              Credit Score: {appOwner.owner.credit_score}
                            </p>
                          )}
                          <div className="mt-2 space-y-2">
                            {appOwner.owner.credit_pull_status && (
                              <Badge variant={appOwner.owner.credit_pull_status === 'COMPLETED' ? 'default' : appOwner.owner.credit_pull_status === 'FAILED' ? 'destructive' : 'secondary'}>
                                Credit Pull: {appOwner.owner.credit_pull_status === 'COMPLETED' ? 'Completed' : appOwner.owner.credit_pull_status === 'FAILED' ? 'Failed' : appOwner.owner.credit_pull_status === 'IN_PROGRESS' ? 'In Progress' : 'Not Started'}
                              </Badge>
                            )}
                            {appOwner.owner.credit_pull_error && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm font-medium text-red-800">Credit Pull Error:</p>
                                <p className="text-xs text-red-700 mt-1">{appOwner.owner.credit_pull_error}</p>
                              </div>
                            )}
                            <div className="flex gap-2 mt-2 flex-wrap">
                              {isFIManagerOrAdmin && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pullCreditMutation.mutate(appOwner.owner.id);
                                  }}
                                  disabled={
                                    pullCreditMutation.isPending ||
                                    !orgAdminCanManualPullCredit(appOwner.owner)
                                  }
                                  title={orgAdminPullCreditDisabledTitle(appOwner.owner)}
                                >
                                  {pullCreditMutation.isPending ? "Pulling..." : "Pull Credit"}
                                </Button>
                              )}
                              {appOwner.owner.equifax_pdf_path && isSuperAdminOrAccountManager && ddInternalLens && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Use relative URL (works in both dev and production)
                                    const apiUrl = customInstance.defaults.baseURL || '';
                                    window.open(`${apiUrl}/api/applications/${id}/owners/${appOwner.owner.id}/equifax-report/`, '_blank');
                                  }}
                                >
                                  Download Equifax Report
                                </Button>
                              )}
                              {appOwner.owner.credit_summary_pdf_path && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const filename = appOwner.owner.credit_summary_pdf_path?.split(/[\\\/]/).pop() || '';
                                    // Use relative URL (works in both dev and production)
                                    const apiUrl = customInstance.defaults.baseURL || '';
                                    window.open(`${apiUrl}/api/applications/${id}/owners/${appOwner.owner.id}/credit-summary/`, '_blank');
                                  }}
                                >
                                  Download Credit Summary
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500">No owners/guarantors added yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Company Card */}
          <Card>
            <CardHeader className="relative">
              <CardTitle>Company</CardTitle>
              {(application.company || editingCompany) && !isSales && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (editingCompany) {
                      setEditingCompany(false);
                    } else {
                      setEditingCompany(true);
                      setEditedCompanyData({
                        name: application.company?.name || "",
                        dba: application.company?.dba || "",
                        address_1: application.company?.address_1 || "",
                        address_2: application.company?.address_2 || "",
                        city: application.company?.city || "",
                        state: application.company?.state || "",
                        postal_code: application.company?.postal_code || "",
                        business_phone: application.company?.business_phone ? formatPhone(application.company.business_phone) : "",
                        years_in_business: application.company?.years_in_business || "",
                        ein: application.company?.ein ? formatEIN(application.company.ein) : "",
                        annual_revenue: application.annual_revenue ? String(application.annual_revenue) : "",
                        industry: application.industry || "",
                      });
                    }
                  }}
                  className="absolute top-4 right-4 p-1"
                >
                  <PencilIcon className="w-5 h-5" />
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {application.company || editingCompany ? (
                <div className="space-y-3">
                  {editingCompany ? (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Company Name</Label>
                        <Input
                          type="text"
                          value={editedCompanyData.name || ""}
                          onChange={(e) => setEditedCompanyData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Company Name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">DBA (optional)</Label>
                        <Input
                          type="text"
                          value={editedCompanyData.dba || ""}
                          onChange={(e) => setEditedCompanyData(prev => ({ ...prev, dba: e.target.value }))}
                          placeholder="DBA"
                        />
                      </div>
                      <div className="space-y-2 border-t pt-2">
                        <Label className="text-xs font-semibold">Address</Label>
                        <div>
                          <Label className="text-xs">Street Address 1</Label>
                          <Input
                            type="text"
                            value={editedCompanyData.address_1 || ""}
                            onChange={(e) => setEditedCompanyData(prev => ({ ...prev, address_1: e.target.value }))}
                            placeholder="Street Address 1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Street Address 2 (optional)</Label>
                          <Input
                            type="text"
                            value={editedCompanyData.address_2 || ""}
                            onChange={(e) => setEditedCompanyData(prev => ({ ...prev, address_2: e.target.value }))}
                            placeholder="Street Address 2"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">City</Label>
                            <Input
                              type="text"
                              value={editedCompanyData.city || ""}
                              onChange={(e) => setEditedCompanyData(prev => ({ ...prev, city: e.target.value }))}
                              placeholder="City"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">State</Label>
                            <select
                              value={editedCompanyData.state || ""}
                              onChange={(e) => setEditedCompanyData(prev => ({ ...prev, state: e.target.value }))}
                              className="w-full px-3 py-2 border rounded"
                            >
                              <option value="">State</option>
                              {US_STATES.map(state => (
                                <option key={state.value} value={state.value}>{state.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Postal Code</Label>
                            <Input
                              type="text"
                              value={editedCompanyData.postal_code || ""}
                              onChange={(e) => setEditedCompanyData(prev => ({ ...prev, postal_code: e.target.value }))}
                              placeholder="Postal Code"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="border-t pt-2">
                        <Label className="text-xs">Business Phone Number (optional)</Label>
                        <Input
                          type="text"
                          value={editedCompanyData.business_phone || ""}
                          onChange={(e) => handlePhoneChange(e.target.value, (val) => setEditedCompanyData(prev => ({ ...prev, business_phone: val })))}
                          placeholder="xxx-xxx-xxxx"
                          maxLength={14}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-2">
                        <div>
                          <Label className="text-xs">Annual Revenue</Label>
                          <Input
                            type="text"
                            value={editedCompanyData.annual_revenue || ""}
                            onChange={(e) => setEditedCompanyData(prev => ({ ...prev, annual_revenue: e.target.value }))}
                            placeholder="Amount"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Industry</Label>
                          <select
                            value={editedCompanyData.industry || ""}
                            onChange={(e) => setEditedCompanyData(prev => ({ ...prev, industry: e.target.value }))}
                            className="w-full px-3 py-2 border rounded"
                          >
                            <option value="">Select Industry</option>
                            {(intakeData?.industries || []).map((ind) => (
                              <option key={ind.id} value={ind.name}>{ind.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Years in Business</Label>
                          <Input
                            type="number"
                            value={editedCompanyData.years_in_business || ""}
                            onChange={(e) => setEditedCompanyData(prev => ({ ...prev, years_in_business: e.target.value }))}
                            onWheelCapture={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (document.activeElement === e.currentTarget) {
                                e.currentTarget.blur();
                              }
                            }}
                            placeholder="Years"
                            min="0"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Tax ID (EIN)</Label>
                          <Input
                            type="text"
                            value={editedCompanyData.ein || ""}
                            onChange={(e) => handleEINChange(e.target.value, (val) => setEditedCompanyData(prev => ({ ...prev, ein: val })))}
                            placeholder="xx-xxxxxxx"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2 border-t">
                        <Button size="sm" onClick={() => {
                          // Save company fields
                          const companyData: any = {
                            name: editedCompanyData.name,
                            dba: editedCompanyData.dba || "",
                            address_1: editedCompanyData.address_1,
                            address_2: editedCompanyData.address_2 || "",
                            city: editedCompanyData.city,
                            state: editedCompanyData.state,
                            postal_code: editedCompanyData.postal_code,
                            business_phone: editedCompanyData.business_phone ? toDigits(editedCompanyData.business_phone) : "",
                            years_in_business: editedCompanyData.years_in_business ? parseInt(editedCompanyData.years_in_business) : null,
                            ein: editedCompanyData.ein ? toDigits(editedCompanyData.ein) : "",
                          };
                          updateCompanyMutation.mutate(companyData, {
                            onSuccess: () => {
                              // Save application fields (annual_revenue, industry)
                              const appData: any = {};
                              if (editedCompanyData.annual_revenue !== undefined) {
                                appData.annual_revenue = editedCompanyData.annual_revenue;
                              }
                              if (editedCompanyData.industry !== undefined) {
                                appData.industry = editedCompanyData.industry;
                              }
                              if (Object.keys(appData).length > 0) {
                                customInstance.patch(`/api/applications/${id}/`, appData).then(() => {
                                  queryClient.invalidateQueries({ queryKey: ["application", id] });
                                  setEditingCompany(false);
                                });
                              } else {
                                setEditingCompany(false);
                              }
                            }
                          });
                        }}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setEditingCompany(false);
                        }}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <p className="font-semibold">
                          {application.company?.name || "No company name"}
                          {application.company?.dba && ` DBA ${application.company.dba}`}
                        </p>
                        {(application.company?.address_1 || application.company?.city) && (
                          <div className="mt-1">
                            <p className="text-sm text-gray-600">
                              {application.company.address_1}{application.company.address_2 ? `, ${application.company.address_2}` : ""}
                              {(application.company.city || application.company.state || application.company.postal_code) && (
                                <>, {[application.company.city, application.company.state, application.company.postal_code].filter(Boolean).join(', ')}</>
                              )}
                            </p>
                          </div>
                        )}
                        {application.company?.business_phone && (
                          <div className="mt-1">
                            <p className="text-sm text-gray-600">
                              <a 
                                href={`tel:${application.company.business_phone.replace(/\D/g, '')}`} 
                                className="text-blue-600 hover:underline"
                              >
                                {formatPhone(application.company.business_phone)}
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
                        {(application.annual_revenue !== null && application.annual_revenue !== undefined && application.annual_revenue !== '') && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Annual Revenue</p>
                            <p className="text-sm font-semibold text-gray-900">
                              ${parseFloat(String(application.annual_revenue || '0')).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                        {application.industry && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Industry</p>
                            <p className="text-sm font-semibold text-gray-900">{application.industry}</p>
                          </div>
                        )}
                        {application.company?.years_in_business !== null && application.company?.years_in_business !== undefined && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Years in Business</p>
                            <p className="text-sm font-semibold text-gray-900">{application.company.years_in_business}</p>
                          </div>
                        )}
                        {ddInternalLens &&
                          application.company?.paynet_score != null &&
                          application.company?.paynet_score !== undefined && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">PayNet Score</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {application.company.paynet_score}
                            </p>
                          </div>
                        )}
                        {application.company?.ein && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Tax ID</p>
                            <p className="text-sm font-semibold text-gray-900">{formatEIN(application.company.ein)}</p>
                          </div>
                        )}
                      </div>
                      {application.sos_registry?.eligible && application.sos_registry && (
                        <SosRegistryPanel data={application.sos_registry} />
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-gray-500 mb-2">No company information</p>
                  {!isSales && (
                    <Button size="sm" variant="outline" onClick={() => {
                      setEditingCompany(true);
                      setEditedCompanyData({ name: "", dba: "", address_1: "", address_2: "", city: "", state: "", postal_code: "", years_in_business: "", ein: "", annual_revenue: "", industry: "" });
                    }}>Add Company</Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Equipment Card (M2M) - add/edit/delete, rerun prequal */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Equipment</CardTitle>
              <div className="flex gap-2">
                {!isSales && (application.equipments?.length ?? 0) === 0 && (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingEquipmentId(null);
                    setEquipmentFormData({ equipment_type_id: null, equipment_subtype_id: null, year: '', make: '', model_name: '', mileage: '', hours: '', price: '', free_text: '' });
                    setShowEquipmentModal(true);
                  }}>Add equipment</Button>
                )}
                {/* Rerun prequal: allowed for all user types until at least one lender (or Manual Review) is submitted */}
                {(application.lender_submissions?.length ?? 0) === 0 && (
                  <Button size="sm" variant="outline" className="bg-white hover:bg-gray-50" onClick={() => rerunPrequalMutation.mutate()} disabled={rerunPrequalMutation.isPending}>
                    {rerunPrequalMutation.isPending ? "Running…" : "Rerun prequal"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const defs = application.primary_equipment_custom_field_defs ?? [];
                const vals = application.primary_equipment_custom_values ?? {};
                const defKeys = new Set(defs.map((d) => d.key));
                const legacyKeys = Object.keys(vals).filter((k) => !defKeys.has(k));
                if (defs.length === 0 && legacyKeys.length === 0) return null;
                const openEdit = () => {
                  const m: Record<string, string> = {};
                  for (const d of defs) {
                    m[d.key] = String(vals[d.key] ?? "");
                  }
                  for (const k of legacyKeys) {
                    m[k] = String(vals[k] ?? "");
                  }
                  setEditedPrimaryCustomFields(m);
                  setEditingPrimaryCustomFields(true);
                };
                return (
                  <div className={`mb-4 ${staffUi.insetPanel} space-y-2`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900">Primary equipment — additional info</p>
                      {canEditPrimaryEquipmentCustom && !editingPrimaryCustomFields && (
                        <Button size="sm" variant="outline" type="button" onClick={openEdit}>
                          Edit
                        </Button>
                      )}
                    </div>
                    {!editingPrimaryCustomFields ? (
                      <dl className="space-y-1 text-sm">
                        {defs.map((d) => (
                          <div key={d.key} className="flex flex-wrap gap-x-2 gap-y-0.5">
                            <dt className="text-gray-600">{d.label}</dt>
                            <dd className="font-medium text-gray-900">
                              {String(vals[d.key] ?? "").trim() || "—"}
                            </dd>
                          </div>
                        ))}
                        {legacyKeys.map((k) => (
                          <div key={k} className="flex flex-wrap gap-x-2 gap-y-0.5">
                            <dt className="text-gray-600">Former field ({k})</dt>
                            <dd className="font-medium text-gray-900">
                              {String(vals[k] ?? "").trim() || "—"}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <div className="space-y-2">
                        {defs.map((d) => (
                          <div key={d.key}>
                            <Label className="text-xs text-gray-600">{d.label}</Label>
                            <Input
                              value={editedPrimaryCustomFields[d.key] ?? ""}
                              maxLength={144}
                              onChange={(e) =>
                                setEditedPrimaryCustomFields((prev) => ({
                                  ...prev,
                                  [d.key]: e.target.value,
                                }))
                              }
                              className="mt-1"
                            />
                          </div>
                        ))}
                        {legacyKeys.map((k) => (
                          <div key={k}>
                            <Label className="text-xs text-gray-600">Former field ({k})</Label>
                            <Input
                              value={editedPrimaryCustomFields[k] ?? ""}
                              maxLength={144}
                              onChange={(e) =>
                                setEditedPrimaryCustomFields((prev) => ({
                                  ...prev,
                                  [k]: e.target.value,
                                }))
                              }
                              className="mt-1"
                            />
                          </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            type="button"
                            onClick={async () => {
                              if (!id) return;
                              try {
                                await customInstance.patch(`/api/applications/${id}/`, {
                                  primary_equipment_custom_values: editedPrimaryCustomFields,
                                });
                                toast.success("Saved.");
                                setEditingPrimaryCustomFields(false);
                                queryClient.invalidateQueries({ queryKey: ["application", id] });
                              } catch (e: any) {
                                const d = e?.response?.data;
                                const msg =
                                  (typeof d === "object" &&
                                    d?.primary_equipment_custom_values?.[0]) ||
                                  (typeof d === "string" ? d : null) ||
                                  "Save failed";
                                toast.error(msg);
                              }
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            type="button"
                            onClick={() => setEditingPrimaryCustomFields(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {application.equipments && application.equipments.length > 0 ? (
                <div className="space-y-3">
                  {application.equipments.map((eq: EquipmentItem, index: number) => (
                    <div key={eq.id} className={`flex items-start justify-between gap-2 p-3 ${staffUi.insetPanel}`}>
                      <div>
                        <p className="font-medium text-gray-900">
                          {application.intake_program?.intake_profile === "demco_grain_cart" && index === 0
                            ? "Demco Grain Cart"
                            : eq.name || `Equipment #${eq.id}`}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
                          {eq.equipment_type?.name && <span>Type: {eq.equipment_type.name}</span>}
                          {eq.year != null && <span>Year: {eq.year}</span>}
                          {eq.mileage != null && <span>Mileage: {eq.mileage.toLocaleString()}</span>}
                          {eq.hours != null && <span>Hours: {eq.hours.toLocaleString()}</span>}
                          {eq.price != null && eq.price !== '' && <span>Price: ${Number(eq.price).toLocaleString()}</span>}
                        </div>
                      </div>
                      {!isSales && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditingEquipmentId(eq.id);
                            setEquipmentFormData({
                              equipment_type_id: eq.equipment_type_id ?? eq.equipment_type?.id ?? null,
                              equipment_subtype_id: eq.subtype_id ?? eq.subtype?.id ?? null,
                              year: eq.year != null ? String(eq.year) : '',
                              make: eq.make ?? '',
                              model_name: eq.model_name ?? '',
                              mileage: eq.mileage != null ? String(eq.mileage) : '',
                              hours: eq.hours != null ? String(eq.hours) : '',
                              price: eq.price != null ? String(eq.price) : '',
                              free_text: eq.free_text ?? '',
                            });
                            setShowEquipmentModal(true);
                          }}>Edit</Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                            if (window.confirm("Remove this equipment from the application?")) deleteEquipmentMutation.mutate(eq.id);
                          }}>Remove</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No equipment added. Add equipment to run prequal and see offers.</p>
              )}
            </CardContent>
          </Card>

          {/* Equipment add/edit modal */}
          <Dialog open={showEquipmentModal} onOpenChange={setShowEquipmentModal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingEquipmentId ? "Edit equipment" : "Add equipment"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="relative">
                  <Label>Equipment type</Label>
                  <Input
                    type="text"
                    placeholder="Search (e.g. semi, forklift, reefer)"
                    value={equipmentSearchQuery}
                    onChange={(e) => setEquipmentSearchQuery(e.target.value)}
                    className="w-full mt-1 mb-1"
                  />
                  {equipmentSearchResults.length > 0 && (
                    <ul className="absolute z-10 w-full max-w-md bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-auto mt-0">
                      {equipmentSearchResults.map((opt, idx) => (
                        <li key={idx}>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                            onClick={() => {
                              setEquipmentFormData(prev => ({
                                ...prev,
                                equipment_type_id: opt.equipment_type.id,
                                equipment_subtype_id: opt.equipment_subtype?.id ?? null,
                              }));
                              setEquipmentSearchQuery("");
                              setEquipmentSearchResults([]);
                            }}
                          >
                            {opt.equipment_type.name}{opt.equipment_subtype ? ` — ${opt.equipment_subtype.name}` : ""}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <select
                    className="w-full mt-1 px-3 py-2 border rounded"
                    value={equipmentFormData.equipment_type_id ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? parseInt(e.target.value, 10) : null;
                      const types = equipmentTypes || [];
                      const allowedIds = application?.allowed_equipment_type_ids;
                      const filtered = Array.isArray(allowedIds) && allowedIds.length > 0
                        ? types.filter((et: EquipmentTypeOption) => allowedIds.includes(et.id))
                        : types;
                      const selectedType = val != null ? filtered.find((et: EquipmentTypeOption) => et.id === val) : null;
                      const subtypes = (selectedType as { subtypes?: { id: number; name: string }[] })?.subtypes ?? [];
                      const singleSubtypeId = subtypes.length === 1 ? subtypes[0].id : null;
                      setEquipmentFormData(prev => ({
                        ...prev,
                        equipment_type_id: val,
                        equipment_subtype_id: val != null ? singleSubtypeId : null,
                      }));
                    }}
                  >
                    <option value="">-- Select type --</option>
                    {(() => {
                      const types = equipmentTypes || [];
                      const allowedIds = application?.allowed_equipment_type_ids;
                      const filtered = Array.isArray(allowedIds) && allowedIds.length > 0
                        ? types.filter((et: EquipmentTypeOption) => allowedIds.includes(et.id))
                        : types;
                      return filtered.map((et: EquipmentTypeOption) => (
                        <option key={et.id} value={et.id}>{et.name}</option>
                      ));
                    })()}
                  </select>
                </div>
                {equipmentFormData.equipment_type_id != null && (() => {
                  const types = equipmentTypes || [];
                  const allowedIds = application?.allowed_equipment_type_ids;
                  const filtered = Array.isArray(allowedIds) && allowedIds.length > 0
                    ? types.filter((et: EquipmentTypeOption) => allowedIds.includes(et.id))
                    : types;
                  const selectedType = filtered.find((et: EquipmentTypeOption) => et.id === equipmentFormData.equipment_type_id);
                  const subtypes = (selectedType as { subtypes?: { id: number; name: string }[] })?.subtypes ?? [];
                  if (subtypes.length <= 1) return null;
                  return (
                    <div>
                      <Label>Equipment subtype</Label>
                      <select
                        className="w-full mt-1 px-3 py-2 border rounded"
                        value={equipmentFormData.equipment_subtype_id ?? ''}
                        onChange={(e) => setEquipmentFormData(prev => ({ ...prev, equipment_subtype_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                      >
                        <option value="">-- Optional --</option>
                        {subtypes.map((sub: { id: number; name: string }) => (
                          <option key={sub.id} value={sub.id}>{sub.name}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Year</Label>
                    <Input value={equipmentFormData.year} onChange={(e) => setEquipmentFormData(prev => ({ ...prev, year: e.target.value }))} placeholder="e.g. 2020" />
                  </div>
                  <div>
                    <Label>Price ($) *</Label>
                    <Input type="text" value={equipmentFormData.price} onChange={(e) => setEquipmentFormData(prev => ({ ...prev, price: e.target.value }))} placeholder="Required" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Make</Label>
                    <Input value={equipmentFormData.make} onChange={(e) => setEquipmentFormData(prev => ({ ...prev, make: e.target.value }))} placeholder="e.g. John Deere" />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input value={equipmentFormData.model_name} onChange={(e) => setEquipmentFormData(prev => ({ ...prev, model_name: e.target.value }))} placeholder="e.g. 8R" />
                  </div>
                </div>
                {equipmentFormData.equipment_type_id != null && (() => {
                  const types = equipmentTypes || [];
                  const allowedIds = application?.allowed_equipment_type_ids;
                  const filtered = Array.isArray(allowedIds) && allowedIds.length > 0
                    ? types.filter((et: EquipmentTypeOption) => allowedIds.includes(et.id))
                    : types;
                  const selectedType = filtered.find((et: EquipmentTypeOption) => et.id === equipmentFormData.equipment_type_id);
                  const signals: string[] = selectedType?.underwriting_signals ?? [];
                  const showMileage = signals.includes("mileage");
                  const showHours = signals.includes("hours");
                  if (!showMileage && !showHours) return null;
                  return (
                    <div className="grid grid-cols-2 gap-2">
                      {showMileage && (
                        <div>
                          <Label>Mileage (optional)</Label>
                          <Input type="text" value={equipmentFormData.mileage} onChange={(e) => setEquipmentFormData(prev => ({ ...prev, mileage: e.target.value }))} placeholder="e.g. 450000" />
                        </div>
                      )}
                      {showHours && (
                        <div>
                          <Label>Hours (optional)</Label>
                          <Input type="text" value={equipmentFormData.hours} onChange={(e) => setEquipmentFormData(prev => ({ ...prev, hours: e.target.value }))} placeholder="e.g. 5000" />
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <Label>Notes (optional)</Label>
                  <Input value={equipmentFormData.free_text} onChange={(e) => setEquipmentFormData(prev => ({ ...prev, free_text: e.target.value }))} placeholder="Free text" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowEquipmentModal(false);
                  setEquipmentSearchQuery("");
                  setEquipmentSearchResults([]);
                }}>Cancel</Button>
                <Button onClick={() => {
                  const priceRaw = equipmentFormData.price.trim();
                  const priceNum = parseFloat(priceRaw);
                  if (!priceRaw || Number.isNaN(priceNum) || priceNum <= 0) {
                    toast.error("Enter a valid equipment price greater than zero.");
                    return;
                  }
                  const payload: Record<string, unknown> = {
                    year: equipmentFormData.year ? parseInt(equipmentFormData.year, 10) : null,
                    make: equipmentFormData.make || null,
                    model_name: equipmentFormData.model_name || null,
                    mileage: equipmentFormData.mileage ? parseInt(equipmentFormData.mileage, 10) : null,
                    hours: equipmentFormData.hours ? parseInt(equipmentFormData.hours, 10) : null,
                    price: priceNum,
                    free_text: equipmentFormData.free_text || null,
                  };
                  if (equipmentFormData.equipment_type_id != null) payload.equipment_type_id = equipmentFormData.equipment_type_id;
                  payload.subtype_id = equipmentFormData.equipment_subtype_id ?? null;
                  if (editingEquipmentId != null) {
                    updateEquipmentMutation.mutate({ equipmentId: editingEquipmentId, payload });
                  } else {
                    addEquipmentMutation.mutate(payload);
                  }
                }} disabled={addEquipmentMutation.isPending || updateEquipmentMutation.isPending}>
                  {editingEquipmentId ? "Save" : "Add"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {showCommercialCreditCard && (
            <Card>
              <CardHeader>
                <CardTitle>Commercial Credit</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {application.company?.paynet_score != null && application.company?.paynet_score !== undefined && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">PayNet Score</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {application.company.paynet_score}
                    </p>
                  </div>
                )}
                {latestCompletedPaynetReport && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadPaynetReportMutation.mutate(latestCompletedPaynetReport.id)}
                    disabled={downloadPaynetReportMutation.isPending}
                  >
                    {downloadPaynetReportMutation.isPending ? "Opening…" : "View report"}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {showPaynetInternalCard && (
            <Card>
              <CardHeader>
                <button
                  type="button"
                  onClick={() => setPaynetExpanded(!paynetExpanded)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <CardTitle>PayNet</CardTitle>
                  <span className="text-lg">{paynetExpanded ? "−" : "+"}</span>
                </button>
              </CardHeader>
              {paynetExpanded && (
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => searchPaynetMutation.mutate()}
                        disabled={searchPaynetMutation.isPending}
                      >
                        {searchPaynetMutation.isPending ? "Searching…" : "Search PayNet"}
                      </Button>
                      {application.company?.paynet_id && (
                        <span className="text-xs text-gray-600 font-mono">
                          Selected ID: {application.company.paynet_id}
                        </span>
                      )}
                      {paynetPullInProgress && (
                        <Badge variant="secondary">Pull in progress…</Badge>
                      )}
                    </div>

                    {(application.company?.paynet_matches?.length ?? 0) > 0 && (
                      <div className="border rounded-lg p-3 bg-blue-50 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-700">PayNet matches</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Select a PayNet ID before pulling. Clear to run a fresh search.
                            </p>
                          </div>
                          {canPullPaynet && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                confirm("Clear all cached PayNet matches?") &&
                                clearPaynetIdMutation.mutate()
                              }
                              disabled={clearPaynetIdMutation.isPending}
                            >
                              {clearPaynetIdMutation.isPending ? "Clearing…" : "Clear"}
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2">
                          {(application.company.paynet_matches || []).map((match: {
                            paynet_id: string;
                            name?: string;
                            match_score?: number;
                            address?: string;
                            city?: string;
                            state?: string;
                          }) => (
                            <div
                              key={match.paynet_id}
                              className={`border rounded p-2 ${
                                application.company.paynet_id === match.paynet_id
                                  ? "bg-green-50 border-green-300"
                                  : "bg-white"
                              }`}
                            >
                              <p className="text-sm font-mono">PayNet ID: {match.paynet_id}</p>
                              {match.name && (
                                <p className="text-xs text-gray-600">Company: {match.name}</p>
                              )}
                              {match.match_score != null && (
                                <p className="text-xs text-gray-500">Match: {match.match_score}%</p>
                              )}
                              {(match.address || match.city || match.state) && (
                                <p className="text-xs text-gray-500">
                                  {[match.address, match.city, match.state].filter(Boolean).join(", ")}
                                </p>
                              )}
                              {canPullPaynet &&
                                application.company.paynet_id !== match.paynet_id && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="mt-2"
                                    onClick={() =>
                                      confirm(`Select PayNet ID ${match.paynet_id}?`) &&
                                      selectPaynetIdMutation.mutate(match.paynet_id)
                                    }
                                    disabled={selectPaynetIdMutation.isPending}
                                  >
                                    {selectPaynetIdMutation.isPending ? "Selecting…" : "Select"}
                                  </Button>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {canPullPaynet && (
                      <div className="border rounded-lg p-4 space-y-4">
                        <h3 className="font-semibold text-sm">Pull PayNet Report</h3>
                        <div className="opacity-60 pointer-events-none space-y-3">
                          <p className="text-xs text-gray-500">
                            Base CHR only in v1. Score and product options are not available yet.
                          </p>
                          <div>
                            <Label className="text-xs text-gray-400">PayNet Score (v2)</Label>
                            <select
                              disabled
                              className="w-full px-3 py-2 border rounded text-sm bg-gray-50 text-gray-400"
                              value=""
                            >
                              <option value="">None</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-400">Product options (v2)</Label>
                            <p className="text-sm text-gray-400 mt-1 border rounded p-2">
                              Base CHR only (no additional products)
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => pullPaynetMutation.mutate()}
                          disabled={
                            pullPaynetMutation.isPending ||
                            !application.company?.paynet_id ||
                            paynetPullInProgress
                          }
                        >
                          {pullPaynetMutation.isPending ? "Pulling…" : "Pull PayNet Report"}
                        </Button>
                        {!application.company?.paynet_id && (
                          <p className="text-xs text-amber-700">
                            Search and select a PayNet ID before pulling.
                          </p>
                        )}
                      </div>
                    )}

                    {application.company?.paynet_score != null &&
                      application.company?.paynet_score !== undefined && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Score</p>
                          <p className="text-3xl font-bold text-gray-900">
                            {application.company.paynet_score}
                          </p>
                        </div>
                      )}

                    {paynetReports.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="font-semibold text-sm">Reports</h3>
                        {paynetReports.map((report: {
                          id: number;
                          status: string;
                          status_display?: string;
                          pull_kind?: string;
                          pull_kind_display?: string;
                          error_message?: string | null;
                          pdf_path?: string | null;
                          html_path?: string | null;
                          created_at: string;
                        }) => (
                          <div key={report.id} className="border rounded-lg p-3 text-sm space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-gray-500">
                                {new Date(report.created_at).toLocaleString()}
                              </span>
                              <Badge
                                variant={
                                  report.status === "COMPLETED"
                                    ? "default"
                                    : report.status === "FAILED"
                                      ? "destructive"
                                      : "secondary"
                                }
                              >
                                {report.status_display || report.status}
                              </Badge>
                              {report.pull_kind_display && (
                                <span className="text-xs text-gray-500">{report.pull_kind_display}</span>
                              )}
                            </div>
                            {report.error_message && (
                              <div className="p-2 bg-red-50 rounded">
                                <p className="text-xs text-red-700">{report.error_message}</p>
                              </div>
                            )}
                            {report.status === "COMPLETED" && (report.pdf_path || report.html_path) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadPaynetReportMutation.mutate(report.id)}
                                disabled={downloadPaynetReportMutation.isPending}
                              >
                                View report
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {isSuperAdminOrAccountManager && ddInternalLens && application && (
            <InternalCollaborationSection applicationId={application.id} />
          )}

          {/* Notes Card */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              {canEditApplicationNotes ? (
                <>
                  <StipInstructionsEditor
                    key={id}
                    value={notes}
                    onChange={(html) => {
                      setNotes(html);
                      notesDirtyRef.current = true;
                    }}
                    placeholder="Add notes…"
                  />
                  <Button
                    className="mt-3"
                    size="sm"
                    onClick={async () => {
                      try {
                        await customInstance.patch(`/api/applications/${id}/`, { notes });
                        notesDirtyRef.current = false;
                        await queryClient.invalidateQueries({ queryKey: ["application", id] });
                        toast.success("Notes saved");
                      } catch (error: any) {
                        toast.error("Failed to save notes");
                      }
                    }}
                  >
                    Save Notes
                  </Button>
                </>
              ) : (
                <div className={`min-h-[120px] ${staffUi.insetPanel}`}>
                  {notes?.trim() ? (
                    <StipInstructionsContent content={notes} />
                  ) : (
                    <p className="text-sm text-muted-foreground">No notes.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {isSuperAdmin && ddInternalLens && (
            <Card className="border-amber-200/80">
              <CardHeader>
                <CardTitle>Prequal disagreement</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  For superadmins only. Submits a snapshot of credit summary fields (no SSN), appends a row to the
                  configured Google Sheet when set up, and emails all portal superadmins.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {prequalDisagreements.length > 0 && (
                  <div className={`space-y-2 max-h-48 overflow-y-auto text-sm ${staffUi.insetPanel}`}>
                    <p className="font-medium text-xs text-muted-foreground uppercase">Previous submissions</p>
                    {prequalDisagreements.map((d: PrequalDisagreementRow) => (
                      <div key={d.id} className="border-b border-border/60 pb-2 last:border-0 last:pb-0">
                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                          <span>{new Date(d.created_at).toLocaleString()}</span>
                          <span>·</span>
                          <span>{d.author_name || d.author_email}</span>
                          {d.sheet_appended_at ? (
                            <span className="text-green-700">· Sheet OK</span>
                          ) : d.sheet_error ? (
                            <span className="text-amber-800">· Sheet: {d.sheet_error.slice(0, 80)}</span>
                          ) : null}
                        </div>
                        <p className="text-foreground whitespace-pre-wrap mt-1">{d.body}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 flex-col sm:flex-row sm:items-end">
                  <textarea
                    className="flex-1 min-h-[100px] px-3 py-2 border rounded-md text-sm resize-y"
                    placeholder="e.g. Disagree with prequal: medical charge-off should exclude Mitsubishi tier…"
                    value={disagreementBody}
                    onChange={(e) => setDisagreementBody(e.target.value)}
                    disabled={submitDisagreementMutation.isPending}
                  />
                  <Button
                    className="shrink-0"
                    variant="secondary"
                    onClick={() => {
                      const t = disagreementBody.trim();
                      if (!t) return;
                      submitDisagreementMutation.mutate(t);
                    }}
                    disabled={!disagreementBody.trim() || submitDisagreementMutation.isPending}
                  >
                    {submitDisagreementMutation.isPending ? "Submitting…" : "Submit disagreement"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Messages (dealer / internal) - scrolls within box when many messages */}
          <Card>
            <CardHeader>
              <CardTitle>Messages</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div ref={messagesScrollRef} className="space-y-3 max-h-80 overflow-y-auto">
                {messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No messages yet. Use the box below to send one.</p>
                ) : (
                  messages.map((msg: AppMessage) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.is_internal ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm border ${
                          msg.is_internal
                            ? "bg-orange-50 border-orange-100"
                            : "bg-gray-100 border-gray-200"
                        }`}
                      >
                        <div className="flex flex-wrap items-baseline gap-2 mb-1">
                          <span className="font-medium text-gray-900">{msg.author_display}</span>
                          {msg.is_internal ? (
                            <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800 border-0">DealerDog</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Dealer</Badge>
                          )}
                          <span className="text-muted-foreground text-xs">
                            {new Date(msg.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                          </span>
                        </div>
                        <p className="text-gray-800 whitespace-pre-wrap">{msg.body}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <textarea
                  className="flex-1 min-h-[80px] px-3 py-2 border rounded-md text-sm resize-y"
                  placeholder="Type a message..."
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  disabled={postMessageMutation.isPending}
                />
                <Button
                  onClick={() => {
                    const body = messageBody.trim();
                    if (!body) return;
                    postMessageMutation.mutate(body);
                  }}
                  disabled={!messageBody.trim() || postMessageMutation.isPending}
                >
                  {postMessageMutation.isPending ? "Sending…" : "Send"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Deal Economics Card — approval/funded summary; same visibility as dealer/org roles (not lens-gated for DD staff) */}
          {(() => {
            const approvalStates = ['CONDITIONALLY_APPROVED', 'APPROVED', 'DOCS_REQUESTED', 'DOCS_OUT', 'DOCS_IN', 'FUNDED'];
            const approvedSubmission = (application.lender_submissions ?? []).find(
              (s: LenderSubmission) => approvalStates.includes(s.status) && s.approval_economics
            );
            const fromFunded = application.stage === 'FUNDED' && application.funded_deal_economics;
            const economics = fromFunded ? application.funded_deal_economics : approvedSubmission?.approval_economics;
            if (!economics) return null;
            const isFromFunded = Boolean(fromFunded);
            const fmt = (n: number) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            return (
              <Card>
                <CardHeader>
                  <CardTitle>Deal Economics</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isFromFunded ? 'Funded deal summary' : 'Approval summary'}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Approval Amount: approval_amount (approval) / total_financed_amount (funded) — currency */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Approval amount</p>
                      <p className="text-sm font-semibold">
                        {isFromFunded
                          ? fmt((economics as FundedDealEconomics).total_financed_amount ?? (economics as FundedDealEconomics).equipment_cost ?? 0)
                          : fmt((economics as LenderApprovalEconomics).approval_amount || 0)}
                      </p>
                    </div>
                    {/* Down Payment: down_payment_type+value (approval) / down_payment (funded) — $ or % */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Down payment</p>
                      <p className="text-sm font-semibold">
                        {isFromFunded
                          ? fmt((economics as FundedDealEconomics).down_payment || 0)
                          : (economics as LenderApprovalEconomics).down_payment_type === 'DOLLARS'
                            ? fmt((economics as LenderApprovalEconomics).down_payment_value || 0)
                            : (economics as LenderApprovalEconomics).down_payment_type === 'PERCENT'
                              ? `${Number((economics as LenderApprovalEconomics).down_payment_value || 0).toFixed(2)}%`
                              : '—'}
                      </p>
                    </div>
                    {/* Advance Payment: advance_payments (approval) / number_of_advances (funded) — integer */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Advance payment</p>
                      <p className="text-sm font-semibold">
                        {isFromFunded
                          ? (economics as FundedDealEconomics).number_of_advances ?? 0
                          : (economics as LenderApprovalEconomics).advance_payments ?? 0}
                      </p>
                    </div>
                    {/* Term in months: term_length (approval) / term_months (funded) — integer */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Term in months</p>
                      <p className="text-sm font-semibold">
                        {isFromFunded
                          ? (economics as FundedDealEconomics).term_months ?? 0
                          : (economics as LenderApprovalEconomics).term_length ?? 0}
                      </p>
                    </div>
                    {/* Monthly Payment — currency */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Monthly payment</p>
                      <p className="text-sm font-semibold">
                        {(isFromFunded ? (economics as FundedDealEconomics).monthly_payment : (economics as LenderApprovalEconomics).monthly_payment) != null &&
                         Number(isFromFunded ? (economics as FundedDealEconomics).monthly_payment : (economics as LenderApprovalEconomics).monthly_payment) !== 0
                          ? fmt(Number(isFromFunded ? (economics as FundedDealEconomics).monthly_payment : (economics as LenderApprovalEconomics).monthly_payment))
                          : '—'}
                      </p>
                    </div>
                    {/* Upfront Doc Fees: lender_doc_fees (approval) / lender_doc_fees_collected (funded) — currency */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Upfront doc fees</p>
                      <p className="text-sm font-semibold">
                        {isFromFunded
                          ? fmt((economics as FundedDealEconomics).lender_doc_fees_collected ?? 0)
                          : fmt((economics as LenderApprovalEconomics).lender_doc_fees ?? 0)}
                      </p>
                    </div>
                    {/* Other Upfront Moneys: at_doc_fees (TCC Doc Fees) — currency */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase">Other upfront moneys</p>
                      <p className="text-sm font-semibold">
                        {isFromFunded
                          ? fmt((economics as FundedDealEconomics).at_doc_fees ?? 0)
                          : fmt((economics as LenderApprovalEconomics).at_doc_fees ?? 0)}
                      </p>
                    </div>
                    {isFromFunded && (economics as FundedDealEconomics).funded_date && (
                      <div className="sm:col-span-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase">Funded date</p>
                        <p className="text-sm font-semibold">{new Date((economics as FundedDealEconomics).funded_date!).toLocaleDateString('en-US')}</p>
                      </div>
                    )}
                    {isFromFunded && (economics as FundedDealEconomics).dealer_referral != null && Number((economics as FundedDealEconomics).dealer_referral) !== 0 && (
                      <div className={`sm:col-span-2 ${staffUi.warningPanel}`}>
                        <p className="text-xs font-medium text-amber-800 uppercase tracking-wide">Dealer Referral</p>
                        <p className="text-lg font-semibold text-amber-900">{fmt(Number((economics as FundedDealEconomics).dealer_referral))}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          <div className="flex flex-col gap-6">
          {/* Lenders Card — DD staff internal lens only */}
          <div className={internalDdReorderSidebar ? "order-3" : "order-1"}>
          {isSuperAdminOrAccountManager && ddInternalLens && (
          <Card>
            <CardHeader>
              <CardTitle>Lenders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(() => {
                  const canSubmit = application.can_submit_to_lender !== false;
                  const matchedLenderIds = new Set(
                    (application.prequal_results?.results ?? [])
                      .filter((r: { matched?: boolean }) => r.matched)
                      .map((r: { lender_id?: number }) => r.lender_id)
                      .filter((id: number | undefined): id is number => id != null)
                  );
                  const manualReviewLender = lenders?.find((l: any) => (l as any).name === "Manual Review");
                  // Only show lenders the customer prequalified for + already submitted + Manual Review (real lender from API)
                  const seen = new Set<number>();
                  const lendersToShow: { id: number; name: string }[] = [];
                  for (const r of application.prequal_results?.results ?? []) {
                    const lid = r.lender_id;
                    if (lid != null && !seen.has(lid)) {
                      seen.add(lid);
                      const name = effectiveMaskLenderNames
                        ? ((r as any).lender_name ?? `Offer ${lendersToShow.length + 1}`)
                        : (lenders?.find((l: any) => l.id === lid)?.name ?? (r as any).lender_name ?? "Offer");
                      lendersToShow.push({ id: lid, name });
                    }
                  }
                  for (const s of application.lender_submissions ?? []) {
                    const lid = s.lender;
                    if (lid != null && !seen.has(lid)) {
                      seen.add(lid);
                      const name = effectiveMaskLenderNames
                        ? (s.lender_name ?? `Offer ${lendersToShow.length + 1}`)
                        : (lenders?.find((l: any) => l.id === lid)?.name ?? s.lender_name ?? "Offer");
                      lendersToShow.push({ id: lid, name });
                    }
                  }
                  if (manualReviewLender && !seen.has(manualReviewLender.id)) {
                    seen.add(manualReviewLender.id);
                    lendersToShow.push({ id: manualReviewLender.id, name: (manualReviewLender as any).name });
                  }

                  // INTERNAL (C/D) lenders for AM/SA submit list
                  for (const r of application.internal_prequal_results?.results ?? []) {
                    const lid = r.lender_id;
                    if (lid != null && !seen.has(lid)) {
                      seen.add(lid);
                      lendersToShow.push({
                        id: lid,
                        name: lenders?.find((l: Lender) => l.id === lid)?.name ?? r.lender_name ?? "Internal program",
                      });
                    }
                  }
                  for (const l of lenders ?? []) {
                    if (
                      l.prequal_visibility === "INTERNAL" &&
                      l.id != null &&
                      !seen.has(l.id)
                    ) {
                      seen.add(l.id);
                      lendersToShow.push({ id: l.id, name: l.name });
                    }
                  }

                  // Superadmin: show all active lenders from API (prequal/submissions first, then the rest A—Z)
                  if (canSubmitAnyLender && lenders?.length) {
                    const rest = lenders
                      .filter((l: Lender) => l.id != null && !seen.has(l.id))
                      .slice()
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                    for (const l of rest) {
                      seen.add(l.id);
                      lendersToShow.push({ id: l.id, name: l.name });
                    }
                  }

                  const sortedLendersToShow = [...lendersToShow].sort((a, b) => {
                    const ma = matchedLenderIds.has(a.id);
                    const mb = matchedLenderIds.has(b.id);
                    if (ma !== mb) return ma ? -1 : 1;
                    return (a.name || "").localeCompare(b.name || "");
                  });
                  return sortedLendersToShow.map((lender) => {
                  const submission = application.lender_submissions?.find((s: LenderSubmission) => s.lender === lender.id);
                  const status = submission?.status || "NOT_SUBMITTED";
                  const isInternalCdLender =
                    lenders?.find((l: Lender) => l.id === lender.id)?.prequal_visibility === "INTERNAL";
                  const canSubmitToThisLender =
                    canSubmitAnyLender ||
                    submission != null ||
                    matchedLenderIds.has(lender.id) ||
                    (manualReviewLender != null && lender.id === manualReviewLender.id) ||
                    (isSuperAdminOrAccountManager && !!isInternalCdLender);
                  const isMatchedPrequal = matchedLenderIds.has(lender.id);
                  const isManualReviewRow = manualReviewLender != null && lender.id === manualReviewLender.id;
                  
                  return (
                    <div key={lender.id} className="p-3 border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{lender.name}</p>
                            {isManualReviewRow ? (
                              <Badge variant="outline">Manual review</Badge>
                            ) : isMatchedPrequal ? (
                              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">Matched</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-muted-foreground">Not matched</Badge>
                            )}
                          </div>
                          <Badge className={lenderStatusColors[status] || "bg-gray-500"}>
                            {status.replace(/_/g, " ")}
                          </Badge>
                        </div>
                        {!isSales && (submission || (canSubmit && canSubmitToThisLender)) && (
                          <Button 
                            size="sm" 
                            variant={submission ? "outline" : "default"}
                            onClick={() => setSelectedLender(lender.id)}
                            disabled={!submission && !canSubmitToThisLender}
                            title={
                              !submission && !canSubmitToThisLender
                                ? "You can only submit to lenders the customer prequalified for."
                                : undefined
                            }
                          >
                            {submission ? "View" : "Submit"}
                          </Button>
                        )}
                      </div>
                      {submission && (
                        <div className="flex gap-2">
                          <select
                            className="flex-1 px-2 py-1 border rounded text-sm"
                            value={status}
                            disabled={isSales}
                            onChange={async (e) => {
                              if (isSales) return;
                              const newStatus = e.target.value;
                              const oldStatus = status;
                              try {
                                await customInstance.patch(`/api/lender-submissions/${submission.id}/`, {
                                  status: newStatus
                                });
                                if (id) {
                                  mergeLenderSubmissionInCache(queryClient, id, submission.id, {
                                    status: newStatus,
                                  });
                                  window.setTimeout(() => {
                                    queryClient.invalidateQueries({ queryKey: applicationQueryKey(id) });
                                  }, 2500);
                                }
                                toast.success("Status updated");
                                
                                // Show approval economics modal if moved to CONDITIONALLY_APPROVED or APPROVED (DD staff only, not org admin/sales)
                                if (isSuperAdminOrAccountManager && 
                                    (newStatus === 'CONDITIONALLY_APPROVED' || newStatus === 'APPROVED') &&
                                    oldStatus !== newStatus) {
                                  startTransition(() => {
                                    economicsModalsRef.current?.openApproval(submission.id);
                                  });
                                }
                                
                                // Show docs out economics modal if moved to DOCS_REQUESTED or DOCS_OUT (DD staff only)
                                if (isSuperAdminOrAccountManager && 
                                    (newStatus === 'DOCS_REQUESTED' || newStatus === 'DOCS_OUT') &&
                                    oldStatus !== newStatus) {
                                  const existingEconomics = submission.approval_economics;
                                  startTransition(() => {
                                    economicsModalsRef.current?.openDocsOut(submission.id, {
                                      dealer_participation: existingEconomics?.dealer_participation ?? null,
                                      down_payment_type: existingEconomics?.down_payment_type ?? null,
                                      down_payment_value: existingEconomics?.down_payment_value ?? null,
                                      term_length: existingEconomics?.term_length ?? null,
                                      at_doc_fees: existingEconomics?.at_doc_fees ?? null,
                                      points: existingEconomics?.points ?? null,
                                      sell_rate: existingEconomics?.sell_rate ?? null,
                                      gross_margin: existingEconomics?.gross_margin ?? null,
                                      invoice_amount: existingEconomics?.invoice_amount ?? null,
                                      monthly_payment: existingEconomics?.monthly_payment ?? null,
                                      notes: existingEconomics?.notes ?? "",
                                    });
                                  });
                                }
                              } catch (error: any) {
                                toast.error("Failed to update status");
                              }
                            }}
                          >
                            <option value="NOT_SUBMITTED">Not Submitted</option>
                            <option value="SUBMITTED">Submitted</option>
                            <option value="NEEDS_INFO">Needs Info</option>
                            <option value="CONDITIONALLY_APPROVED">Conditionally Approved</option>
                            <option value="APPROVED">Approved</option>
                            <option value="DOCS_REQUESTED">Docs Requested</option>
                            <option value="DOCS_OUT">Docs Out</option>
                            <option value="DOCS_IN">Docs In</option>
                            <option value="FUNDED">Funded</option>
                            <option value="DECLINED">Declined</option>
                          </select>
                        </div>
                      )}
                      
                      {/* Notes Section - Show when status >= SUBMITTED, but hide if approval economics exist (notes are transferred) */}
                      {submission && (status === 'SUBMITTED' || status === 'NEEDS_INFO' || status === 'CONDITIONALLY_APPROVED' || 
                        status === 'APPROVED' || status === 'DOCS_REQUESTED' || status === 'DOCS_OUT' || 
                        status === 'DOCS_IN' || status === 'FUNDED' || status === 'DECLINED') && 
                        !submission.approval_economics && (
                        <div className="mt-2 space-y-1">
                          <label className="text-xs font-medium text-gray-700">Notes:</label>
                          {editingSubmissionNotes === submission.id ? (
                            <div className="space-y-2">
                              <textarea
                                className="w-full px-2 py-1 border rounded text-sm min-h-[60px] resize-y"
                                value={editedSubmissionNotes[submission.id] || submission.notes || ''}
                                onChange={(e) => setEditedSubmissionNotes(prev => ({
                                  ...prev,
                                  [submission.id]: e.target.value
                                }))}
                                placeholder="Add notes about this lender submission..."
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={async () => {
                                    try {
                                      await customInstance.patch(`/api/lender-submissions/${submission.id}/`, {
                                        notes: editedSubmissionNotes[submission.id] || ''
                                      });
                                      toast.success("Notes updated");
                                      queryClient.invalidateQueries({ queryKey: ["application", id] });
                                      setEditingSubmissionNotes(null);
                                    } catch (error) {
                                      toast.error("Failed to update notes");
                                    }
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingSubmissionNotes(null);
                                    setEditedSubmissionNotes(prev => {
                                      const newState = { ...prev };
                                      delete newState[submission.id];
                                      return newState;
                                    });
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <p className="text-sm text-gray-600 whitespace-pre-wrap min-h-[20px]">
                                {submission.notes || <span className="text-gray-400 italic">No notes</span>}
                              </p>
                              {!isSales && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs h-6 px-2"
                                  onClick={() => {
                                    setEditingSubmissionNotes(submission.id);
                                    setEditedSubmissionNotes(prev => ({
                                      ...prev,
                                      [submission.id]: submission.notes || ''
                                    }));
                                  }}
                                >
                                  <PencilIcon className="w-3 h-3 mr-1" />
                                  Edit Notes
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Approval Economics Section (Collapsible) */}
                      {submission && (status === 'CONDITIONALLY_APPROVED' || status === 'APPROVED' || status === 'DOCS_REQUESTED' || status === 'DOCS_OUT' || status === 'DOCS_IN' || status === 'FUNDED') && isFIManagerOrAdmin && (
                        <div className="mt-3 border-t pt-3">
                          <button
                            onClick={() => setExpandedApprovalEconomics(prev => ({
                              ...prev,
                              [submission.id]: !prev[submission.id]
                            }))}
                            className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
                          >
                            <span>Approval Economics</span>
                            <span>{expandedApprovalEconomics[submission.id] ? '−' : '+'}</span>
                          </button>
                          
                          {expandedApprovalEconomics[submission.id] && (
                            <div className="mt-3 space-y-3">
                              {editingApprovalEconomics[submission.id] ? (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Approval Amount ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.approval_amount === 0 || editedApprovalEconomicsData[submission.id]?.approval_amount === undefined ? '' : editedApprovalEconomicsData[submission.id]?.approval_amount}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], approval_amount: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Dealer Participation ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.dealer_participation === 0 || editedApprovalEconomicsData[submission.id]?.dealer_participation === undefined ? '' : editedApprovalEconomicsData[submission.id]?.dealer_participation}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], dealer_participation: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Term Length (months)</Label>
                                      <Input
                                        type="number"
                                        value={editedApprovalEconomicsData[submission.id]?.term_length === 0 || editedApprovalEconomicsData[submission.id]?.term_length === undefined ? '' : editedApprovalEconomicsData[submission.id]?.term_length}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], term_length: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                        }))}
                                        placeholder="0"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Down Payment Type</Label>
                                      <select
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        value={editedApprovalEconomicsData[submission.id]?.down_payment_type || ''}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], down_payment_type: (e.target.value as 'DOLLARS' | 'PERCENT' | null) || null }
                                        }))}
                                      >
                                        <option value="">Select...</option>
                                        <option value="DOLLARS">Dollars ($)</option>
                                        <option value="PERCENT">Percent (%)</option>
                                      </select>
                                    </div>
                                    <div>
                                      <Label className="text-xs">Down Payment Value</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.down_payment_value === 0 || editedApprovalEconomicsData[submission.id]?.down_payment_value === undefined ? '' : editedApprovalEconomicsData[submission.id]?.down_payment_value}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], down_payment_value: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Advance Payments</Label>
                                      <Input
                                        type="number"
                                        value={editedApprovalEconomicsData[submission.id]?.advance_payments === 0 || editedApprovalEconomicsData[submission.id]?.advance_payments === undefined ? '' : editedApprovalEconomicsData[submission.id]?.advance_payments}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], advance_payments: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }
                                        }))}
                                        placeholder="0"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Lender Doc Fees ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.lender_doc_fees === 0 || editedApprovalEconomicsData[submission.id]?.lender_doc_fees === undefined ? '' : editedApprovalEconomicsData[submission.id]?.lender_doc_fees}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], lender_doc_fees: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">TCC Doc Fees ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.at_doc_fees === 0 || editedApprovalEconomicsData[submission.id]?.at_doc_fees === undefined ? '' : editedApprovalEconomicsData[submission.id]?.at_doc_fees}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], at_doc_fees: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Label className="text-xs">Contract Type</Label>
                                    <select
                                      className="w-full px-3 py-2 border rounded-md text-sm"
                                      value={editedApprovalEconomicsData[submission.id]?.contract_type || ''}
                                      onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                        ...prev,
                                        [submission.id]: { ...prev[submission.id], contract_type: e.target.value || null }
                                      }))}
                                    >
                                      <option value="">Select...</option>
                                      <option value="EFA">EFA</option>
                                      <option value="FMV_LEASE">FMV Lease</option>
                                      <option value="ONE_DOLLAR_OUT_LEASE">$1 out Lease</option>
                                      <option value="TRAC_LEASE">TRAC Lease</option>
                                    </select>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Max Compensation Type</Label>
                                      <select
                                        className="w-full px-3 py-2 border rounded-md text-sm"
                                        value={editedApprovalEconomicsData[submission.id]?.max_compensation_type || ''}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], max_compensation_type: (e.target.value as 'POINTS' | 'COMMISSION' | null) || null }
                                        }))}
                                      >
                                        <option value="">Select...</option>
                                        <option value="POINTS">Points</option>
                                        <option value="COMMISSION">Commission ($)</option>
                                      </select>
                                    </div>
                                    <div>
                                      <Label className="text-xs">Max Compensation Value</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.max_compensation_value === 0 || editedApprovalEconomicsData[submission.id]?.max_compensation_value === undefined ? '' : editedApprovalEconomicsData[submission.id]?.max_compensation_value}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], max_compensation_value: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Label className="text-xs">Buy Rate (%)</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={editedApprovalEconomicsData[submission.id]?.buy_rate === 0 || editedApprovalEconomicsData[submission.id]?.buy_rate === null || editedApprovalEconomicsData[submission.id]?.buy_rate === undefined ? '' : editedApprovalEconomicsData[submission.id]?.buy_rate}
                                      onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                      onWheel={(e) => {
                                        e.currentTarget.blur();
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                        ...prev,
                                        [submission.id]: { ...prev[submission.id], buy_rate: e.target.value === '' ? null : parseFloat(e.target.value) || null }
                                      }))}
                                      placeholder="0.00"
                                    />
                                  </div>
                                  
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Sell Rate (%)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.sell_rate === 0 || editedApprovalEconomicsData[submission.id]?.sell_rate === null || editedApprovalEconomicsData[submission.id]?.sell_rate === undefined ? '' : editedApprovalEconomicsData[submission.id]?.sell_rate}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], sell_rate: e.target.value === '' ? null : parseFloat(e.target.value) || null }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Gross Margin ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.gross_margin === 0 || editedApprovalEconomicsData[submission.id]?.gross_margin === null || editedApprovalEconomicsData[submission.id]?.gross_margin === undefined ? '' : editedApprovalEconomicsData[submission.id]?.gross_margin}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], gross_margin: e.target.value === '' ? null : parseFloat(e.target.value) || null }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Invoice Amount ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.invoice_amount === 0 || editedApprovalEconomicsData[submission.id]?.invoice_amount === null || editedApprovalEconomicsData[submission.id]?.invoice_amount === undefined ? '' : editedApprovalEconomicsData[submission.id]?.invoice_amount}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], invoice_amount: e.target.value === '' ? null : parseFloat(e.target.value) || null }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Total Financed Amount ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.total_financed_amount === 0 || editedApprovalEconomicsData[submission.id]?.total_financed_amount === null || editedApprovalEconomicsData[submission.id]?.total_financed_amount === undefined ? '' : editedApprovalEconomicsData[submission.id]?.total_financed_amount}
                                        disabled
                                        className="bg-gray-100"
                                        placeholder="Calculated automatically"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Total Commission ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.total_commission === 0 || editedApprovalEconomicsData[submission.id]?.total_commission === null || editedApprovalEconomicsData[submission.id]?.total_commission === undefined ? '' : editedApprovalEconomicsData[submission.id]?.total_commission}
                                        disabled
                                        className="bg-gray-100"
                                        placeholder="Calculated automatically"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Monthly Payment ($)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.monthly_payment === 0 || editedApprovalEconomicsData[submission.id]?.monthly_payment === null || editedApprovalEconomicsData[submission.id]?.monthly_payment === undefined ? '' : editedApprovalEconomicsData[submission.id]?.monthly_payment}
                                        onFocus={(e) => { 
                                          const val = e.target.value;
                                          if (val === '0' || val === '0.0' || val === '0.00' || val === '') {
                                            e.target.select();
                                          }
                                        }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onKeyDown={(e) => {
                                          const target = e.target as HTMLInputElement;
                                          const val = target.value;
                                          // If value is 0, 0.0, 0.00, or empty, and user types a digit, clear the field
                                          if ((val === '0' || val === '0.0' || val === '0.00' || val === '' || parseFloat(val) === 0) && /[0-9]/.test(e.key)) {
                                            target.value = '';
                                          }
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], monthly_payment: e.target.value === '' ? null : parseFloat(e.target.value) || null }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Points</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={editedApprovalEconomicsData[submission.id]?.points === 0 || editedApprovalEconomicsData[submission.id]?.points === null || editedApprovalEconomicsData[submission.id]?.points === undefined ? '' : editedApprovalEconomicsData[submission.id]?.points}
                                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                                        onWheel={(e) => {
                                          e.currentTarget.blur();
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                          ...prev,
                                          [submission.id]: { ...prev[submission.id], points: e.target.value === '' ? null : parseFloat(e.target.value) || null }
                                        }))}
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Label className="text-xs">Approved Date</Label>
                                    <Input
                                      type="datetime-local"
                                      value={utcToLocalDatetime(editedApprovalEconomicsData[submission.id]?.approved_date)}
                                      onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                        ...prev,
                                        [submission.id]: { ...prev[submission.id], approved_date: localToUtcISO(e.target.value) }
                                      }))}
                                    />
                                  </div>
                                  
                                  <div>
                                    <Label className="text-xs">Notes</Label>
                                    <textarea
                                      className="w-full min-h-[80px] p-2 border rounded text-sm"
                                      value={editedApprovalEconomicsData[submission.id]?.notes || ''}
                                      onChange={(e) => setEditedApprovalEconomicsData(prev => ({
                                        ...prev,
                                        [submission.id]: { ...prev[submission.id], notes: e.target.value }
                                      }))}
                                      placeholder="Add notes..."
                                    />
                                  </div>
                                  
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        if (editedApprovalEconomicsData[submission.id]) {
                                          updateApprovalEconomicsMutation.mutate({
                                            submissionId: submission.id,
                                            data: editedApprovalEconomicsData[submission.id]
                                          });
                                          setEditingApprovalEconomics(prev => ({ ...prev, [submission.id]: false }));
                                        }
                                      }}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingApprovalEconomics(prev => ({ ...prev, [submission.id]: false }));
                                        setEditedApprovalEconomicsData(prev => {
                                          const newData = { ...prev };
                                          delete newData[submission.id];
                                          return newData;
                                        });
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {submission.approval_economics ? (
                                    <>
                                      {(() => {
                                        const isApprovalStage = status === 'CONDITIONALLY_APPROVED' || status === 'APPROVED';
                                        return (
                                          <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                              <span className="text-gray-500">Approval Amount:</span>
                                              <span className="ml-2 font-medium">${Number(submission.approval_economics.approval_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                            {!isApprovalStage && (
                                              <div>
                                                <span className="text-gray-500">Dealer Participation:</span>
                                                <span className="ml-2 font-medium">${Number(submission.approval_economics.dealer_participation || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                              </div>
                                            )}
                                            <div>
                                              <span className="text-gray-500">Term Length:</span>
                                              <span className="ml-2 font-medium">{submission.approval_economics.term_length || 0} months</span>
                                            </div>
                                            {submission.approval_economics.down_payment_type && (
                                              <div>
                                                <span className="text-gray-500">Down Payment ({submission.approval_economics.down_payment_type === 'DOLLARS' ? '$' : '%'}):</span>
                                                <span className="ml-2 font-medium">
                                                  {submission.approval_economics.down_payment_type === 'DOLLARS'
                                                    ? `$${Number(submission.approval_economics.down_payment_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                    : `${Number(submission.approval_economics.down_payment_value || 0).toFixed(2)}%`}
                                                </span>
                                              </div>
                                            )}
                                            <div>
                                              <span className="text-gray-500">Advance Payments:</span>
                                              <span className="ml-2 font-medium">{submission.approval_economics.advance_payments || 0}</span>
                                            </div>
                                            <div>
                                              <span className="text-gray-500">Lender Doc Fees:</span>
                                              <span className="ml-2 font-medium">${Number(submission.approval_economics.lender_doc_fees || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                            {!isApprovalStage && (
                                              <div>
                                                <span className="text-gray-500">TCC Doc Fees:</span>
                                                <span className="ml-2 font-medium">${Number(submission.approval_economics.at_doc_fees || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                              </div>
                                            )}
                                            {submission.approval_economics.contract_type && (
                                              <div>
                                                <span className="text-gray-500">Contract Type:</span>
                                                <span className="ml-2 font-medium">{submission.approval_economics.contract_type.replace(/_/g, ' ')}</span>
                                              </div>
                                            )}
                                            {submission.approval_economics.max_compensation_type && (
                                              <div>
                                                <span className="text-gray-500">Max {submission.approval_economics.max_compensation_type === 'POINTS' ? 'Points' : 'Commission'}:</span>
                                                <span className="ml-2 font-medium">
                                                  {submission.approval_economics.max_compensation_type === 'POINTS' 
                                                    ? `${submission.approval_economics.max_compensation_value || 0}`
                                                    : `$${Number(submission.approval_economics.max_compensation_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                </span>
                                              </div>
                                            )}
                                            {submission.approval_economics.buy_rate !== null && submission.approval_economics.buy_rate !== undefined && submission.approval_economics.buy_rate !== 0 && (
                                              <div>
                                                <span className="text-gray-500">Buy Rate:</span>
                                                <span className="ml-2 font-medium">{Number(submission.approval_economics.buy_rate || 0).toFixed(2)}%</span>
                                              </div>
                                            )}
                                            {!isApprovalStage && submission.approval_economics.sell_rate !== null && submission.approval_economics.sell_rate !== undefined && submission.approval_economics.sell_rate !== 0 && (
                                              <div>
                                                <span className="text-gray-500">Sell Rate:</span>
                                                <span className="ml-2 font-medium">{Number(submission.approval_economics.sell_rate || 0).toFixed(2)}%</span>
                                              </div>
                                            )}
                                            {!isApprovalStage && submission.approval_economics.gross_margin !== null && submission.approval_economics.gross_margin !== undefined && submission.approval_economics.gross_margin !== 0 && (
                                              <div>
                                                <span className="text-gray-500">Gross Margin:</span>
                                                <span className="ml-2 font-medium">${Number(submission.approval_economics.gross_margin || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                              </div>
                                            )}
                                            {!isApprovalStage && submission.approval_economics.invoice_amount !== null && submission.approval_economics.invoice_amount !== undefined && submission.approval_economics.invoice_amount !== 0 && (
                                              <div>
                                                <span className="text-gray-500">Invoice Amount:</span>
                                                <span className="ml-2 font-medium">${Number(submission.approval_economics.invoice_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                              </div>
                                            )}
                                            {!isApprovalStage && submission.approval_economics.total_financed_amount !== null && submission.approval_economics.total_financed_amount !== undefined && (
                                              <div>
                                                <span className="text-gray-500">Total Financed Amount:</span>
                                                <span className="ml-2 font-medium">${Number(submission.approval_economics.total_financed_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                              </div>
                                            )}
                                            {!isApprovalStage && submission.approval_economics.total_commission !== null && submission.approval_economics.total_commission !== undefined && (
                                              <div>
                                                <span className="text-gray-500">Total Commission:</span>
                                                <span className="ml-2 font-medium">${Number(submission.approval_economics.total_commission || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                              </div>
                                            )}
                                            {!isApprovalStage && submission.approval_economics.monthly_payment !== null && submission.approval_economics.monthly_payment !== undefined && submission.approval_economics.monthly_payment !== 0 && (
                                              <div>
                                                <span className="text-gray-500">Monthly Payment:</span>
                                                <span className="ml-2 font-medium">${Number(submission.approval_economics.monthly_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                              </div>
                                            )}
                                            {!isApprovalStage && submission.approval_economics.points !== null && submission.approval_economics.points !== undefined && submission.approval_economics.points !== 0 && (
                                              <div>
                                                <span className="text-gray-500">Points:</span>
                                                <span className="ml-2 font-medium">{Number(submission.approval_economics.points || 0).toFixed(2)}</span>
                                              </div>
                                            )}
                                            <div>
                                              <span className="text-gray-500">Approved Date:</span>
                                              <span className="ml-2 font-medium">
                                                {submission.approval_economics.approved_date
                                                  ? new Date(submission.approval_economics.approved_date).toLocaleDateString('en-US', {
                                                      month: '2-digit',
                                                      day: '2-digit',
                                                      year: 'numeric'
                                                    })
                                                  : 'Not set'}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      {submission.approval_economics.notes && (
                                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                                          <span className="text-gray-500 font-medium">Notes:</span>
                                          <p className="mt-1 text-gray-700">{submission.approval_economics.notes}</p>
                                        </div>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setEditingApprovalEconomics(prev => ({ ...prev, [submission.id]: true }));
                                          setEditedApprovalEconomicsData(prev => ({
                                            ...prev,
                                            [submission.id]: {
                                              approval_amount: submission.approval_economics?.approval_amount || 0,
                                              dealer_participation: submission.approval_economics?.dealer_participation || 0,
                                              down_payment_type: submission.approval_economics?.down_payment_type || null,
                                              down_payment_value: submission.approval_economics?.down_payment_value || 0,
                                              term_length: submission.approval_economics?.term_length || 0,
                                              advance_payments: submission.approval_economics?.advance_payments || 0,
                                              lender_doc_fees: submission.approval_economics?.lender_doc_fees || 0,
                                              at_doc_fees: submission.approval_economics?.at_doc_fees || 0,
                                              contract_type: submission.approval_economics?.contract_type || null,
                                              max_compensation_type: submission.approval_economics?.max_compensation_type || null,
                                              max_compensation_value: submission.approval_economics?.max_compensation_value || 0,
                                              buy_rate: submission.approval_economics?.buy_rate || null,
                                              sell_rate: submission.approval_economics?.sell_rate || null,
                                              gross_margin: submission.approval_economics?.gross_margin || null,
                                              monthly_payment: submission.approval_economics?.monthly_payment || null,
                                              points: submission.approval_economics?.points || null,
                                              notes: submission.approval_economics?.notes || '',
                                            }
                                          }));
                                        }}
                                        className="mt-2"
                                      >
                                        <PencilIcon className="w-4 h-4 mr-1" />
                                        Edit
                                      </Button>
                                    </>
                                  ) : (
                                    <div>
                                      <p className="text-sm text-gray-500 mb-2">No approval economics entered yet</p>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setEditingApprovalEconomics(prev => ({ ...prev, [submission.id]: true }));
                                          setEditedApprovalEconomicsData(prev => ({
                                            ...prev,
                                            [submission.id]: {
                                              approval_amount: 0,
                                              dealer_participation: 0,
                                              down_payment_type: null,
                                              down_payment_value: 0,
                                              term_length: 0,
                                              advance_payments: 0,
                                              lender_doc_fees: 0,
                                              at_doc_fees: 0,
                                              contract_type: null,
                                              max_compensation_type: null,
                                              max_compensation_value: 0,
                                              buy_rate: null,
                                              sell_rate: null,
                                              gross_margin: null,
                                              monthly_payment: null,
                                              points: null,
                                              notes: '',
                                            }
                                          }));
                                        }}
                                      >
                                        Add Approval Economics
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
              </div>
            </CardContent>
          </Card>
          )}
          </div>

          {/* Screening answers from intake (CDL / SAFER) */}
          <div className="order-2">
          {(isSuperAdminOrAccountManager ||
            currentUser?.role === "ORG_ADMIN" ||
            currentUser?.role === "TERRITORY_SALES_MANAGER") &&
            application.prequal_gate_answers &&
            typeof application.prequal_gate_answers === "object" &&
            Object.keys(application.prequal_gate_answers).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Screening answers</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    CDL and fleet responses from the application form (used for program matching).
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    {(() => {
                      const pga = application.prequal_gate_answers as Record<string, unknown>;
                      const yn = (v: unknown) =>
                        v === "yes" || v === true ? "Yes" : v === "no" || v === false ? "No" : String(v ?? "—");
                      const rows: ReactNode[] = [];
                      if ("cdl_held" in pga && pga.cdl_held != null) {
                        rows.push(
                          <span key="cdl" className="text-muted-foreground">CDL held</span>,
                          <span key="cdl-v">{yn(pga.cdl_held)}</span>,
                        );
                      }
                      if ("cdl_held_over_one_year" in pga && pga.cdl_held_over_one_year != null) {
                        rows.push(
                          <span key="cdl1" className="text-muted-foreground">CDL held &gt; 1 year</span>,
                          <span key="cdl1-v">{yn(pga.cdl_held_over_one_year)}</span>,
                        );
                      } else if ("cdl_held_one_year" in pga && pga.cdl_held_one_year != null) {
                        rows.push(
                          <span key="cdl1b" className="text-muted-foreground">CDL held &gt; 1 year</span>,
                          <span key="cdl1b-v">{yn(pga.cdl_held_one_year)}</span>,
                        );
                      }
                      const firstTimeBuyer =
                        pga.first_time_buyer ?? pga.first_time_owner_operator;
                      if (firstTimeBuyer != null && firstTimeBuyer !== "") {
                        rows.push(
                          <span key="ft" className="text-muted-foreground">First time buyer</span>,
                          <span key="ft-v">{yn(firstTimeBuyer)}</span>,
                        );
                      }
                      const totalTrucks =
                        pga.safer_total_trucks_operated != null
                          ? pga.safer_total_trucks_operated
                          : pga.safer_all_reporting === "yes" && pga.safer_fleet_units != null
                            ? pga.safer_fleet_units
                            : null;
                      if (totalTrucks != null && totalTrucks !== "") {
                        rows.push(
                          <span key="tot" className="text-muted-foreground">Total trucks operated</span>,
                          <span key="tot-v">{String(totalTrucks)}</span>,
                        );
                      }
                      if ("safer_all_reporting" in pga && pga.safer_all_reporting != null) {
                        rows.push(
                          <span key="sa" className="text-muted-foreground">All trucks registered with DOT</span>,
                          <span key="sa-v">{yn(pga.safer_all_reporting)}</span>,
                        );
                      }
                      if (pga.safer_all_reporting === "no" && pga.safer_fleet_units != null && pga.safer_fleet_units !== "") {
                        rows.push(
                          <span key="ur" className="text-muted-foreground">Trucks registered with DOT</span>,
                          <span key="ur-v">{String(pga.safer_fleet_units)}</span>,
                        );
                      }
                      return rows;
                    })()}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {isSuperAdminOrAccountManager && ddInternalLens && id && (
            <div className={internalDdReorderSidebar ? "order-2" : "order-2"}>
              <InternalCdProgramsCard
                applicationId={id}
                bankMetrics={application.bank_statement_metrics}
                internalPrequal={application.internal_prequal_results}
                manualOfferPublish={application.manual_offer_publish}
                equipmentPrice={
                  application.equipment_price != null ? Number(application.equipment_price) : null
                }
                requestedAmount={
                  application.requested_amount != null ? Number(application.requested_amount) : null
                }
              />
            </div>
          )}

          {/* Prequalification Card */}
          <div className={internalDdReorderSidebar ? "order-1" : "order-3"}>
          {(application.prequal_results?.results?.length > 0 || application.prequal_selection || application.stage === 'RECEIVED' || application.manual_offer_publish?.dealer_card) &&
            !hideDealerPrequalAfterApproval && (
            <Card>
              <CardHeader>
                <CardTitle>Prequalification</CardTitle>
                {(() => {
                  const creditRaw = application.primary_contact?.credit_pulled_at as string | undefined;
                  const preqRaw = application.prequalified_at;
                  const creditDt = creditRaw ? new Date(creditRaw) : null;
                  const preqDt = preqRaw ? new Date(preqRaw) : null;
                  const creditInvalid = creditDt && Number.isNaN(creditDt.getTime());
                  const preqInvalid = preqDt && Number.isNaN(preqDt.getTime());
                  const stalePrequal =
                    !creditInvalid &&
                    !preqInvalid &&
                    creditDt &&
                    preqDt &&
                    preqDt.getTime() < creditDt.getTime();
                  return (
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                      <p>
                        Credit pulled:{" "}
                        {!creditInvalid && creditDt
                          ? creditDt.toLocaleString()
                          : creditRaw
                            ? String(creditRaw)
                            : "—"}
                      </p>
                      <p>
                        Prequal run:{" "}
                        {!preqInvalid && preqDt ? preqDt.toLocaleString() : preqRaw ? String(preqRaw) : "—"}
                      </p>
                      {stalePrequal && (
                        <p className="text-amber-700 font-medium">
                          Prequal may be stale — credit was re-pulled after last prequal run. Consider rerunning.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Request manual review (when stage is RECEIVED) */}
                {application.stage === "RECEIVED" && (
                  application.manual_review_requested ? (
                    <div className={staffUi.warningPanel}>
                      <Badge variant="secondary" className="bg-amber-100 text-amber-900">Manual review requested</Badge>
                    </div>
                  ) : (
                    <div className={staffUi.warningPanel}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => requestManualReviewMutation.mutate()}
                        disabled={requestManualReviewMutation.isPending}
                        className="bg-white"
                      >
                        {requestManualReviewMutation.isPending ? "Requesting…" : "Request manual review"}
                      </Button>
                    </div>
                  )
                )}
                {application.seasonal_payment_eligible &&
                  (application.seasonal_payment_requests?.length ?? 0) > 0 && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-2">
                      <p className="text-sm font-medium text-green-900">Seasonal payment requests</p>
                      {(application.seasonal_payment_requests ?? []).map((req) => {
                        const lenderPrefix = effectiveMaskLenderNames
                          ? maskedOfferLabels.get(req.lender_id) ?? "Offer"
                          : lenders?.find((l: { id: number }) => l.id === req.lender_id)?.name ??
                            `Lender ${req.lender_id}`;
                        return (
                          <div key={req.id ?? `${req.lender_id}`} className="text-sm text-green-900">
                            {effectiveMaskLenderNames ? (
                              formatSeasonalRequestSummary(req)
                            ) : (
                              <>
                                <span className="font-medium">{lenderPrefix}</span>
                                {" — "}
                                {formatSeasonalRequestSummary(req)}
                              </>
                            )}
                            {req.notes ? (
                              <p className="text-xs text-green-800 mt-0.5 italic">{req.notes}</p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                {/* Customer selection */}
                {application.prequal_selection && (() => {
                  const sel = application.prequal_selection;
                  const live = (application.prequal_results?.results ?? []).find(
                    (r: PrequalResultItem) =>
                      r.matched && r.lender_id === sel?.lender_id && r.tier_id === sel?.tier_id
                  );
                  const storedLenderName = sel.lender_name ?? "";
                  const storedTierName = sel.tier_name ?? "";
                  const displayLenderName = effectiveMaskLenderNames
                    ? (sel.lender_id != null
                        ? maskedOfferLabels.get(sel.lender_id) ?? live?.lender_name ?? "Offer"
                        : "—")
                    : isSuperAdminOrAccountManager && sel.lender_id != null
                      ? (lenders?.find((l: any) => l.id === sel.lender_id)?.name ??
                          live?.lender_name ??
                          storedLenderName ??
                          "—")
                      : (live?.lender_name ?? storedLenderName ?? (sel.lender_id != null ? "Offer" : "—"));
                  const displayTierName = effectiveMaskLenderNames
                    ? "Tier"
                    : live?.tier_name || storedTierName || "—";
                  const tierNameStale = Boolean(
                    live &&
                      storedTierName.trim() &&
                      (live.tier_name ?? "").trim() &&
                      storedTierName.trim() !== (live.tier_name ?? "").trim()
                  );
                  return (
                  <div className="rounded-lg border-2 border-primary bg-primary-50 p-3 space-y-2">
                    <p className="text-sm font-medium text-primary">Customer selection</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <span className="text-muted-foreground">{effectiveMaskLenderNames ? "Offer" : "Lender"}</span>
                      <span>{displayLenderName}</span>
                      <span className="text-muted-foreground">Tier</span>
                      <span>{displayTierName}</span>
                      <span className="text-muted-foreground">Term</span>
                      <span>{sel.term_months ? `${sel.term_months} months` : "—"}</span>
                      <span className="text-muted-foreground">Down payment</span>
                      <span>
                        {sel.down_payment_amount != null
                          ? `$${Number(sel.down_payment_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                          : "—"}
                      </span>
                      <span className="text-muted-foreground">
                        {CADENCE_LABELS[sel.payment_cadence ?? "monthly"]} payment
                      </span>
                      <span className="font-medium">
                        {(sel.payment_amount ?? sel.monthly_payment) != null
                          ? `$${Number(sel.payment_amount ?? sel.monthly_payment).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </span>
                      {sel.total_payments != null && (
                        <>
                          <span className="text-muted-foreground">Payment schedule</span>
                          <span>
                            {sel.total_payments} payments
                            {Number(sel.due_at_signing ?? 0) > 0
                              ? ` · $${Number(sel.due_at_signing).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })} due at signing`
                              : Number(sel.first_payment_due_months ?? 0) > 0
                                ? ` · first due in ${sel.first_payment_due_months} mo`
                                : ""}
                          </span>
                        </>
                      )}
                      {sel.selected_at && (
                        <>
                          <span className="text-muted-foreground">Selected at</span>
                          <span>{new Date(sel.selected_at).toLocaleString()}</span>
                        </>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-snug">{PAYMENT_ESTIMATE_DISCLAIMER}</p>
                    {tierNameStale && (
                      <p className="text-xs text-muted-foreground italic">
                        Offer details may have changed — tier name was updated since this selection was saved.
                      </p>
                    )}
                    {!live && (application.prequal_results?.results?.length ?? 0) > 0 && (
                      <p className="text-xs text-muted-foreground italic">
                        Showing saved selection; this tier is not in the latest matched prequal results.
                      </p>
                    )}
                    {(() => {
                      const match = (application.prequal_results?.results ?? []).find(
                        (r: PrequalResultItem) => r.lender_id === sel?.lender_id && r.tier_id === sel?.tier_id
                      );
                      const note = match?.approval_card_note;
                      return note ? (
                        <p className="text-sm text-muted-foreground italic border-t border-primary/20 pt-2 mt-2">
                          {note}
                        </p>
                      ) : null;
                    })()}
                  </div>
                  );
                })()}
                {/* Lever answers (time-in-business) — what applicant submitted to refresh offers */}
                {application.prequal_lever_answers?.submitted_at && (
                  <div className={`${staffUi.insetPanel} space-y-2`}>
                    <p className="text-sm font-medium text-gray-900">Lever answers (time in business)</p>
                    <p className="text-xs text-muted-foreground">
                      Submitted {new Date(application.prequal_lever_answers.submitted_at).toLocaleString()}
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      {application.prequal_lever_answers.prior_1099_or_sole_prop != null && (
                        <>
                          <span className="text-muted-foreground">1099/Schedule C/F (2+ years ago)</span>
                          <span>{application.prequal_lever_answers.prior_1099_or_sole_prop ? "Yes" : "No"}</span>
                        </>
                      )}
                      {application.prequal_lever_answers.updated_years_in_business != null && application.prequal_lever_answers.updated_years_in_business !== "" && (
                        <>
                          <span className="text-muted-foreground">Updated years in business (1099)</span>
                          <span>{String(application.prequal_lever_answers.updated_years_in_business)}</span>
                        </>
                      )}
                      {application.prequal_lever_answers.other_company_similar_industry != null && (
                        <>
                          <span className="text-muted-foreground">Related business entity</span>
                          <span>{application.prequal_lever_answers.other_company_similar_industry ? "Yes" : "No"}</span>
                        </>
                      )}
                      {application.prequal_lever_answers.other_company_years_in_business != null && application.prequal_lever_answers.other_company_years_in_business !== "" && (
                        <>
                          <span className="text-muted-foreground">Updated years in business (entity)</span>
                          <span>{String(application.prequal_lever_answers.other_company_years_in_business)}</span>
                        </>
                      )}
                      {application.prequal_lever_answers.new_business_name && (
                        <>
                          <span className="text-muted-foreground">New business name</span>
                          <span>{application.prequal_lever_answers.new_business_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {/* Select offer for customer (or change offer) - SUPERADMIN, DD_ACCOUNT_MANAGER, ORG_ADMIN */}
                {(() => {
                  const prequalRes = application.prequal_results;
                  const displayOffers = prequalRes?.display_offers;
                  const baseMatchedResults: PrequalResultItem[] = (
                    displayOffers?.length
                      ? displayOffers
                      : (prequalRes?.results ?? []).filter((r: PrequalResultItem) => r.matched)
                  );
                  const selectedIsVisible = selectedPrequalTierForCustomer
                    ? baseMatchedResults.some(
                        (r) =>
                          r.lender_id === selectedPrequalTierForCustomer.lender_id &&
                          r.tier_id === selectedPrequalTierForCustomer.tier_id
                      )
                    : true;
                  const matchedResults: PrequalResultItem[] =
                    selectedPrequalTierForCustomer && !selectedIsVisible
                      ? [selectedPrequalTierForCustomer, ...baseMatchedResults]
                      : baseMatchedResults;
                  const canSelectOffer = (isSuperAdminOrAccountManager || currentUser?.role === 'ORG_ADMIN' || currentUser?.role === 'TERRITORY_SALES_MANAGER') &&
                    (application.stage === 'RECEIVED' || application.stage === 'IN_REVIEW') &&
                    (prequalRes?.results?.length ?? 0) > 0;
                  // Principal = equipment price before down. Backend sets requested_amount from amount_financed_after_down_selection (RDL: min OOP may not reduce financed principal). Prefer equipment_price; if missing, requested_amount + saved down only matches tiers where full down reduces principal.
                  const equipmentPriceNum = parseFloat(String(application.equipment_price || 0)) || 0;
                  const requestedNum = parseFloat(String(application.requested_amount || 0)) || 0;
                  const savedDownNum = application.prequal_selection?.down_payment_amount != null ? Number(application.prequal_selection.down_payment_amount) : 0;
                  const principal = Math.max(
                    0,
                    equipmentPriceNum > 0
                      ? equipmentPriceNum
                      : savedDownNum > 0 && requestedNum > 0
                        ? requestedNum + savedDownNum
                        : requestedNum
                  );
                  const prequalMinDown = (selectedPrequalTierForCustomer && principal > 0)
                    ? Math.max(
                        0,
                        (selectedPrequalTierForCustomer.min_down_payment_pct != null ? principal * (Number(selectedPrequalTierForCustomer.min_down_payment_pct) / 100) : 0),
                        (selectedPrequalTierForCustomer.min_down_payment_amount != null ? Number(selectedPrequalTierForCustomer.min_down_payment_amount) : 0)
                      )
                    : 0;
                  const effectiveDown = Math.max(prequalMinDown, Math.min(principal, internalPrequalDownPayment));
                  const financePrincipal = financedPrincipalForPayment(
                    principal,
                    effectiveDown,
                    selectedPrequalTierForCustomer
                  );
                  const rateMid = selectedPrequalTierForCustomer
                    ? ((selectedPrequalTierForCustomer.rate_min ?? 0) + (selectedPrequalTierForCustomer.rate_max ?? 0)) / 2
                    : 0;
                  const pointsPct = selectedPrequalTierForCustomer?.total_points_used ?? 0;
                  const principalForPayment = financePrincipal * (1 + pointsPct / 100);
                  const computedMonthly = selectedPrequalTierForCustomer && internalPrequalTermMonths > 0
                    ? monthlyPaymentFromPrequal(principalForPayment, rateMid, internalPrequalTermMonths)
                    : 0;
                  const lenderCadences = enabledCadencesFromOffer(selectedPrequalTierForCustomer);
                  const showCadencePicker =
                    Boolean(application.intake_program?.cadence_picker) || lenderCadences.length > 1;
                  const effectiveCadence: PaymentCadence = lenderCadences.includes(internalPrequalCadence)
                    ? internalPrequalCadence
                    : (lenderCadences[0] ?? "monthly");
                  const cadenceCfg = cadenceConfigFromOffer(selectedPrequalTierForCustomer, effectiveCadence);
                  const cadenceQuote =
                    selectedPrequalTierForCustomer &&
                    showCadencePicker &&
                    financePrincipal > 0 &&
                    internalPrequalTermMonths > 0
                      ? computePaymentScheduleQuote({
                          principalPrePoints: financePrincipal,
                          downPayment: 0,
                          annualRatePct: rateMid,
                          termMonths: internalPrequalTermMonths,
                          pointsPct,
                          cadence: effectiveCadence,
                          advancePayment: Boolean(cadenceCfg.advance_payment),
                          deferFirstMonths: cadenceCfg.defer_first_months ?? null,
                        })
                      : null;
                  const cadencePaymentAmount =
                    showCadencePicker && cadenceQuote
                      ? cadenceQuote.paymentAmount
                      : computedMonthly;
                  const rmin = selectedPrequalTierForCustomer?.rate_min ?? 0;
                  const rmax = selectedPrequalTierForCustomer?.rate_max ?? 0;
                  const simpleInterestVals = selectedPrequalTierForCustomer && financePrincipal > 0 && internalPrequalTermMonths > 0
                    ? [
                        simpleInterestPct(financePrincipal, monthlyPaymentFromPrequal(principalForPayment, rmin, internalPrequalTermMonths), internalPrequalTermMonths),
                        simpleInterestPct(financePrincipal, monthlyPaymentFromPrequal(principalForPayment, rmax, internalPrequalTermMonths), internalPrequalTermMonths),
                      ].filter((x) => x >= 0 && isFinite(x))
                    : [];
                  const simpleInterestMin = simpleInterestVals.length ? Math.min(...simpleInterestVals) : 0;
                  const simpleInterestMax = simpleInterestVals.length ? Math.max(...simpleInterestVals) : 0;
                  const aprVals = selectedPrequalTierForCustomer && financePrincipal > 0 && internalPrequalTermMonths > 0
                    ? [
                        finalAprIncludingPoints(financePrincipal, rmin, internalPrequalTermMonths, pointsPct),
                        finalAprIncludingPoints(financePrincipal, rmax, internalPrequalTermMonths, pointsPct),
                      ].filter((x) => x > 0 && isFinite(x))
                    : [];
                  const aprMin = aprVals.length ? Math.min(...aprVals) : 0;
                  const aprMax = aprVals.length ? Math.max(...aprVals) : 0;
                  if (!canSelectOffer || matchedResults.length === 0) return null;
                  const hasExistingSelection = !!application.prequal_selection;
                  return (
                    <div
                      ref={prequalSelectionPanelRef}
                      tabIndex={-1}
                      className={`border-2 ${staffUi.warningPanel} space-y-3 scroll-mt-4 focus:outline-none`}
                    >
                      <p className="text-sm font-medium text-amber-900">{hasExistingSelection ? "Change selected offer" : "Select offer for customer"}</p>
                      <p className="text-xs text-amber-800">
                        {hasExistingSelection
                          ? showCadencePicker
                            ? "Click a different offer below, set term, down payment, and payment cadence, then click Save to update the customer’s selection."
                            : "Click a different offer below, set term and down payment, then click Save to update the customer’s selection."
                          : showCadencePicker
                            ? "Choose a matched offer, term, down payment, and payment cadence to save as the customer’s selection. You can then submit to that lender. (Dealer/org admin can do this before any lender is submitted.)"
                            : "Choose a matched offer, term, and down payment to save as the customer’s selection. You can then submit to that lender. (Dealer/org admin can do this before any lender is submitted.)"}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {matchedResults.map((r: PrequalResultItem, idx: number) => (
                          <button
                            key={r.lender_id != null && r.tier_id != null ? `${r.lender_id}-${r.tier_id}` : idx}
                            type="button"
                            onClick={() => {
                              setSelectedPrequalTierForCustomer(r);
                              const terms = tierTermMonthsOrDefault(r.term_months);
                              setInternalPrequalTermMonths((prev) => (terms.includes(prev) ? prev : terms[terms.length - 1] ?? 60));
                              const principalHere = Math.max(
                                0,
                                equipmentPriceNum > 0
                                  ? equipmentPriceNum
                                  : savedDownNum > 0 && requestedNum > 0
                                    ? requestedNum + savedDownNum
                                    : requestedNum
                              );
                              const minForTier = principalHere > 0 ? Math.max(
                                0,
                                (r.min_down_payment_pct != null ? principalHere * (Number(r.min_down_payment_pct) / 100) : 0),
                                (r.min_down_payment_amount != null ? Number(r.min_down_payment_amount) : 0)
                              ) : 0;
                              setInternalPrequalDownPayment(minForTier);
                              setInternalPrequalCadence(
                                preferredCadenceFromOffer(
                                  r,
                                  application.intake_program?.intake_profile === "demco_grain_cart"
                                )
                              );
                            }}
                            className={`text-left rounded-lg border p-3 text-sm transition-colors ${
                              selectedPrequalTierForCustomer?.lender_id === r.lender_id && selectedPrequalTierForCustomer?.tier_id === r.tier_id
                                ? "border-2 border-primary ring-2 ring-primary bg-primary-50"
                                : "border-border hover:bg-gray-50"
                            }`}
                          >
                            <span className="font-medium">
                              {effectiveMaskLenderNames
                                ? (r.lender_id != null
                                    ? maskedOfferLabels.get(r.lender_id) ?? `Offer ${idx + 1}`
                                    : `Offer ${idx + 1}`)
                                : isSuperAdminOrAccountManager && r.lender_id != null
                                  ? (lenders?.find((l: any) => l.id === r.lender_id)?.name ?? r.lender_name ?? "—")
                                  : (r.lender_name ?? (r.lender_id != null ? "Offer" : "—"))}
                            </span>
                            {!effectiveMaskLenderNames && r.tier_name && (
                              <span className="text-muted-foreground"> · {r.tier_name}</span>
                            )}
                            {r.term_months?.length ? (
                              <span className="text-muted-foreground text-xs"> · Terms: {r.term_months.join(", ")} mo</span>
                            ) : null}
                            {(r.display_bucket || r.display_rank_in_bucket) && (
                              <span className="text-xs text-muted-foreground block mt-0.5">
                                {r.display_bucket === "no_down_payment" ? "No down payment" : r.display_bucket === "down_payment_required" ? "Down payment required" : ""}
                                {r.display_bucket && r.display_rank_in_bucket ? " · " : ""}
                                {r.display_rank_in_bucket === 1 ? "Best match" : r.display_rank_in_bucket === 2 ? "Also recommended" : ""}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      {selectedPrequalTierForCustomer && (
                        <div className="space-y-2 pt-2 border-t border-amber-200">
                          <div className="flex flex-wrap gap-4 items-center">
                            <div>
                              <Label className="text-xs text-amber-900">Term (months)</Label>
                              <select
                                className="ml-1 mt-0.5 px-2 py-1 border rounded text-sm"
                                value={internalPrequalTermMonths}
                                onChange={(e) => setInternalPrequalTermMonths(Number(e.target.value))}
                              >
                                {tierTermMonthsOrDefault(selectedPrequalTierForCustomer.term_months).map((t: number) => (
                                  <option key={t} value={t}>{t} months</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <Label className="text-xs text-amber-900">Down payment ($)</Label>
                              <input
                                type="number"
                                min={prequalMinDown}
                                max={principal}
                                step={100}
                                className="ml-1 mt-0.5 w-28 px-2 py-1 border rounded text-sm"
                                value={Math.max(prequalMinDown, Math.min(principal, internalPrequalDownPayment))}
                                onChange={(e) => setInternalPrequalDownPayment(Math.max(prequalMinDown, Math.min(principal, Number(e.target.value) || 0)))}
                              />
                              {prequalMinDown > 0 && (
                                <p className="text-xs text-muted-foreground mt-0.5">Min ${Math.round(prequalMinDown).toLocaleString()} for this offer</p>
                              )}
                            </div>
                            {showCadencePicker && (
                              <div>
                                <Label className="text-xs text-amber-900">Payment cadence</Label>
                                <select
                                  className="ml-1 mt-0.5 px-2 py-1 border rounded text-sm"
                                  value={effectiveCadence}
                                  onChange={(e) => setInternalPrequalCadence(e.target.value as PaymentCadence)}
                                >
                                  {lenderCadences.map((c) => (
                                    <option key={c} value={c}>{CADENCE_LABELS[c]}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div className="text-sm">
                              <span className="text-muted-foreground">
                                {showCadencePicker && effectiveCadence !== "monthly"
                                  ? `Est. ${CADENCE_LABELS[effectiveCadence].toLowerCase()}: `
                                  : "Est. monthly: "}
                              </span>
                              <span className="font-medium">
                                ${cadencePaymentAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                              {showCadencePicker && cadenceQuote && effectiveCadence !== "monthly" && (
                                <span className="text-muted-foreground text-xs block mt-0.5">
                                  {cadenceQuote.advancePayment
                                    ? `${cadenceQuote.totalPayments} payments${
                                        cadenceQuote.dueAtSigning > 0
                                          ? ` · $${cadenceQuote.dueAtSigning.toLocaleString("en-US", {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })} due at signing`
                                          : ""
                                      }`
                                    : `${cadenceQuote.totalPayments} payments · first due in ${cadenceQuote.deferFirstMonths} mo`}
                                </span>
                              )}
                            </div>
                            {application.show_prequal_interest_and_apr !== false && (
                              <>
                                <div className="text-sm">
                                  <span className="text-muted-foreground">Simple interest: </span>
                                  <span className="font-medium">{simpleInterestMin.toFixed(2)}—{simpleInterestMax.toFixed(2)}%</span>
                                </div>
                                <div className="text-sm">
                                  <span className="text-muted-foreground">APR: </span>
                                  <span className="font-medium">{aprMin.toFixed(1)}—{aprMax.toFixed(1)}%</span>
                                </div>
                              </>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground leading-snug">{PAYMENT_ESTIMATE_DISCLAIMER}</p>
                          <Button
                            size="sm"
                            onClick={() => {
                              const amount = Math.round(cadencePaymentAmount * 100) / 100;
                              savePrequalSelectionMutation.mutate({
                                lender_id: selectedPrequalTierForCustomer.lender_id,
                                tier_id: selectedPrequalTierForCustomer.tier_id,
                                term_months: internalPrequalTermMonths,
                                down_payment_amount: effectiveDown,
                                monthly_payment: amount,
                                payment_cadence: showCadencePicker ? effectiveCadence : "monthly",
                                payment_amount: amount,
                                total_payments: cadenceQuote?.totalPayments,
                                due_at_signing: cadenceQuote?.dueAtSigning,
                                first_payment_due_months: cadenceQuote?.deferFirstMonths,
                              });
                            }}
                            disabled={savePrequalSelectionMutation.isPending || cadencePaymentAmount <= 0}
                          >
                            {savePrequalSelectionMutation.isPending ? "Saving…" : (hasExistingSelection ? "Update selection" : "Save selection")}
                          </Button>
                          {application.seasonal_payment_eligible &&
                            application.intake_program?.intake_profile !== "demco_grain_cart" &&
                            selectedPrequalTierForCustomer.lender_id != null && (
                              <SeasonalPaymentRequestButton
                                className="w-full sm:w-auto"
                                existing={(application.seasonal_payment_requests ?? []).find(
                                  (req) => req.lender_id === selectedPrequalTierForCustomer.lender_id
                                )}
                                onClick={() =>
                                  setSeasonalModalTarget({
                                    lenderId: selectedPrequalTierForCustomer.lender_id!,
                                    tierId: selectedPrequalTierForCustomer.tier_id,
                                    termMonths: internalPrequalTermMonths,
                                    downPaymentAmount: effectiveDown,
                                    monthlyPayment: Math.round(computedMonthly * 100) / 100,
                                  })
                                }
                              />
                            )}
                          {selectedPrequalTierForCustomer.approval_card_note && (
                            <p className="text-sm text-muted-foreground italic pt-1">
                              {selectedPrequalTierForCustomer.approval_card_note}
                            </p>
                          )}
                          {selectedPrequalTierForCustomer.ownership_100_pct_note && (
                            <p className="text-sm text-amber-800 italic pt-1">
                              {selectedPrequalTierForCustomer.ownership_100_pct_note}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* Matched offers (dealer-facing list); other matched + unmatched only in internal workspace lens */}
                {application.prequal_results?.results?.length > 0 && (() => {
                  const prequalRes = application.prequal_results;
                  const allResults = prequalRes.results ?? [];
                  const displayOffers = prequalRes.display_offers;
                  const matchedResults = (displayOffers?.length ? displayOffers : allResults.filter((r: { matched?: boolean }) => r.matched));
                  const allMatched = allResults.filter((r: { matched?: boolean }) => r.matched);
                  const displayOfferKeys = new Set((displayOffers ?? []).map((d: { lender_id?: number; tier_id?: number }) => `${d.lender_id}-${d.tier_id}`));
                  const otherMatchedResults = allMatched.filter((r: { lender_id?: number; tier_id?: number }) => !displayOfferKeys.has(`${r.lender_id}-${r.tier_id}`));
                  const unmatchedResults = allResults.filter((r: { matched?: boolean }) => !r.matched);
                  const showMatched = matchedResults.length > 0;
                  const showStaffOnlyPrequal =
                    isSuperAdminOrAccountManager &&
                    ddInternalLens &&
                    (otherMatchedResults.length > 0 || unmatchedResults.length > 0);
                  return (
                    <>
                      {showMatched && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Matched offers</p>
                          <p className="text-[11px] text-muted-foreground leading-snug mb-2">{PAYMENT_ESTIMATE_DISCLAIMER}</p>
                          <div className="space-y-2">
                            {matchedResults.map((r: { lender_id?: number; lender_name?: string; tier_id?: number; tier_name?: string; matched?: boolean; failure_reason?: string; rate_min?: number; rate_max?: number; term_months?: number[]; approval_card_note?: string | null; ownership_100_pct_note?: string | null }, idx: number) => (
                              <div
                                key={r.lender_id != null && r.tier_id != null ? `${r.lender_id}-${r.tier_id}` : idx}
                                className={
                                  application.prequal_selection?.lender_id === r.lender_id && application.prequal_selection?.tier_id === r.tier_id
                                    ? staffUi.offerRowSelected
                                    : staffUi.offerRow
                                }
                              >
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span className="font-medium">
                                    {effectiveMaskLenderNames
                                      ? `Offer ${idx + 1}`
                                      : isSuperAdminOrAccountManager && r.lender_id != null
                                        ? (lenders?.find((l: any) => l.id === r.lender_id)?.name ?? r.lender_name ?? "—")
                                        : (r.lender_name ?? (r.lender_id != null ? "Offer" : "—"))}
                                  </span>
                                  <div className="flex items-center gap-1.5">
                                    {(r as PrequalResultItem).display_bucket || (r as PrequalResultItem).display_rank_in_bucket ? (
                                      <span className="text-xs text-muted-foreground">
                                        {(r as PrequalResultItem).display_bucket === "no_down_payment" ? "No down payment" : (r as PrequalResultItem).display_bucket === "down_payment_required" ? "Down payment required" : ""}
                                        {(r as PrequalResultItem).display_bucket && (r as PrequalResultItem).display_rank_in_bucket ? " · " : ""}
                                        {(r as PrequalResultItem).display_rank_in_bucket === 1 ? "Best match" : (r as PrequalResultItem).display_rank_in_bucket === 2 ? "Also recommended" : ""}
                                      </span>
                                    ) : null}
                                    <Badge variant="default">Matched</Badge>
                                  </div>
                                </div>
                                <div className="mt-1 text-muted-foreground">
                                  {!effectiveMaskLenderNames && r.tier_name && <span>{r.tier_name}</span>}
                                  {r.term_months?.length ? (
                                    <span>
                                      {!effectiveMaskLenderNames && r.tier_name ? " · " : ""}
                                      Terms: {r.term_months.join(", ")} mo
                                    </span>
                                  ) : null}
                                </div>
                                {r.approval_card_note && (
                                  <p className="mt-2 text-xs text-muted-foreground italic">{r.approval_card_note}</p>
                                )}
                                {r.ownership_100_pct_note && (
                                  <p className="mt-2 text-xs text-amber-800 italic">{r.ownership_100_pct_note}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {showStaffOnlyPrequal && (
                        <div className={staffUi.staffOnlyPanel}>
                          <div>
                            <p className={staffUi.staffOnlyEyebrow}>Staff-only prequal</p>
                            <p className="text-xs text-muted-foreground mt-1 leading-snug">
                              Extra matched tiers not on the dealer card, plus unmatched lenders. Hidden in Dealer view.
                            </p>
                          </div>
                          {otherMatchedResults.length > 0 && (
                        <div>
                          <button
                            type="button"
                            onClick={() => {
                              const next = !internalOtherMatchedOpen;
                              setInternalOtherMatchedOpen(next);
                              writeInternalPrequalOpen(currentUser?.id, "otherMatched", next);
                            }}
                            className={staffUi.staffOnlySectionBtn}
                          >
                            <span>Other matched offers (not on dealer card)</span>
                            <span className="shrink-0">{internalOtherMatchedOpen ? "−" : "+"}</span>
                          </button>
                          {internalOtherMatchedOpen && (
                          <div className="space-y-2">
                            {otherMatchedResults.map((r: PrequalResultItem & { lender_id?: number; lender_name?: string; tier_id?: number; tier_name?: string; term_months?: number[]; payment_range?: Record<number, { min_payment: number; max_payment: number }>; min_down_payment_pct?: number | null; min_down_payment_amount?: number | null }, idx: number) => {
                              const terms = tierTermMonthsOrDefault(r.term_months);
                              const termMonths = (terms.find((t) => r.payment_range?.[t] != null) ??
                                terms[terms.length - 1]) as number;
                              const principal = Number(application.equipment_price) || Number(application.requested_amount) || 0;
                              const minDownPct = r.min_down_payment_pct != null ? Number(r.min_down_payment_pct) : 0;
                              const minDownAmount = r.min_down_payment_amount != null ? Number(r.min_down_payment_amount) : 0;
                              const downPayment = Math.max(minDownAmount, principal * (minDownPct / 100));
                              return (
                                <div
                                  key={r.lender_id != null && r.tier_id != null ? `${r.lender_id}-${r.tier_id}` : idx}
                                  className={
                                    selectedPrequalTierForCustomer?.lender_id === r.lender_id &&
                                    selectedPrequalTierForCustomer?.tier_id === r.tier_id
                                      ? staffUi.offerRowSelected
                                      : staffUi.offerRow
                                  }
                                >
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-medium">
                                      {lenders?.find((l: any) => l.id === r.lender_id)?.name ?? r.lender_name ?? "—"}
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                      <Badge variant="default">Matched</Badge>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedPrequalTierForCustomer(r as PrequalResultItem);
                                          setInternalPrequalTermMonths(termMonths);
                                          setInternalPrequalDownPayment(downPayment);
                                          setInternalPrequalCadence(
                                            preferredCadenceFromOffer(
                                              r as PrequalResultItem,
                                              application.intake_program?.intake_profile === "demco_grain_cart"
                                            )
                                          );
                                          requestAnimationFrame(() => {
                                            prequalSelectionPanelRef.current?.scrollIntoView({
                                              behavior: "smooth",
                                              block: "start",
                                            });
                                            prequalSelectionPanelRef.current?.focus({ preventScroll: true });
                                          });
                                          toast.info("Offer loaded in Select offer for customer.");
                                        }}
                                        disabled={savePrequalSelectionMutation.isPending}
                                      >
                                        Configure & select
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="mt-1 text-muted-foreground">
                                    {r.tier_name && <span>{r.tier_name}</span>}
                                    {r.term_months?.length ? (
                                      <span> · Terms: {r.term_months.join(", ")} mo</span>
                                    ) : null}
                                  </div>
                                  {r.approval_card_note && (
                                    <p className="mt-2 text-xs text-muted-foreground italic">{r.approval_card_note}</p>
                                  )}
                                  {r.ownership_100_pct_note && (
                                    <p className="mt-2 text-xs text-amber-800 italic">{r.ownership_100_pct_note}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          )}
                        </div>
                          )}
                      {unmatchedResults.length > 0 && (
                        <div>
                          <button
                            type="button"
                            onClick={() => {
                              const next = !internalUnmatchedOpen;
                              setInternalUnmatchedOpen(next);
                              writeInternalPrequalOpen(currentUser?.id, "unmatched", next);
                            }}
                            className={staffUi.staffOnlySectionBtn}
                          >
                            <span>Unmatched offers</span>
                            <span className="shrink-0">{internalUnmatchedOpen ? "−" : "+"}</span>
                          </button>
                          {internalUnmatchedOpen && (
                          <div className="space-y-2">
                            {unmatchedResults.map((r: PrequalResultItem & { lender_id?: number; lender_name?: string; tier_id?: number; tier_name?: string; matched?: boolean; failure_reason?: string; failure_code?: string | null; failure_reasons?: { reason: string; code?: string | null }[] | null; rate_min?: number; rate_max?: number; term_months?: number[] }, idx: number) => {
                              const reasonsList = (r.failure_reasons && r.failure_reasons.length > 0)
                                ? r.failure_reasons
                                : (r.failure_reason ? [{ reason: r.failure_reason, code: r.failure_code ?? null }] : []);
                              const firstEntry = reasonsList[0];
                              const firstReasonLine =
                                firstEntry != null
                                  ? formatPrequalFailureForDisplay(
                                      firstEntry.code,
                                      firstEntry.reason,
                                      isSuperAdminOrAccountManager
                                    )
                                  : "";
                              const rowKey = r.lender_id != null && r.tier_id != null ? `${r.lender_id}-${r.tier_id}` : `idx-${idx}`;
                              const lenderName = lenders?.find((l: any) => l.id === r.lender_id)?.name ?? r.lender_name ?? "—";
                              return (
                                <div
                                  key={rowKey}
                                  className={staffUi.offerRowUnmatched}
                                >
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-medium">{lenderName}</span>
                                    <Badge variant="secondary">Not matched</Badge>
                                  </div>
                                  <div className="mt-1 text-muted-foreground">
                                    {r.tier_name && <span>{r.tier_name}</span>}
                                    {r.rate_min != null && r.rate_max != null && (
                                      <span> · Rate {Number(r.rate_min).toFixed(1)}—{Number(r.rate_max).toFixed(1)}%</span>
                                    )}
                                    {r.term_months?.length ? (
                                      <span> · Terms: {r.term_months.join(", ")} mo</span>
                                    ) : null}
                                  </div>
                                  {firstReasonLine && (
                                    <div className="mt-1 flex items-start gap-1.5">
                                      <p className="text-xs text-amber-700 flex-1">{firstReasonLine}</p>
                                      {reasonsList.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => setUnmatchedReasonsDialog({
                                            key: rowKey,
                                            lenderName,
                                            tierName: r.tier_name,
                                            reasons: reasonsList,
                                          })}
                                          className="shrink-0 text-amber-600 hover:text-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-400 rounded p-0.5"
                                          title={reasonsList.length > 1 ? `View all ${reasonsList.length} reasons` : "View reason"}
                                        >
                                          <Info className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          )}
                        </div>
                      )}
                        </div>
                      )}
                    </>
                  );
                })()}
                {!hasPublicMatchedOffers(application.prequal_results) &&
                  application.manual_offer_publish?.dealer_card && (
                    <ManualOfferDealerSection card={application.manual_offer_publish.dealer_card} />
                  )}
                {application.seasonal_payment_eligible &&
                  application.intake_program?.intake_profile !== "demco_grain_cart" &&
                  seasonalModalTarget && (
                  <SeasonalPaymentRequestModal
                    open={Boolean(seasonalModalTarget)}
                    onOpenChange={(open) => {
                      if (!open) setSeasonalModalTarget(null);
                    }}
                    lenderId={seasonalModalTarget.lenderId}
                    tierId={seasonalModalTarget.tierId}
                    termMonths={seasonalModalTarget.termMonths}
                    downPaymentAmount={seasonalModalTarget.downPaymentAmount}
                    monthlyPayment={seasonalModalTarget.monthlyPayment}
                    existing={(application.seasonal_payment_requests ?? []).find(
                      (req) => req.lender_id === seasonalModalTarget.lenderId
                    )}
                    isPending={saveSeasonalPaymentRequestMutation.isPending}
                    onSubmit={(payload) => saveSeasonalPaymentRequestMutation.mutate(payload)}
                  />
                )}
              </CardContent>
            </Card>
          )}
          </div>
          </div>

          {/* Stipulations Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle>Stipulations</CardTitle>
              {isFIManagerOrAdmin && (
                <Button
                  size="sm"
                  onClick={() => setShowAddStipsDialog(true)}
                  className="h-8"
                >
                  Add Stips
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {application.stips && application.stips.length > 0 ? (
                <div className="space-y-3">
                  {application.stips.map((stip) => {
                    const statusColors: Record<string, string> = {
                      REQUESTED: "bg-yellow-500",
                      SUBMITTED_BY_CUSTOMER: "bg-blue-500",
                      SUBMITTED_TO_LENDER: "bg-purple-500",
                      CLEARED: "bg-green-500",
                    };
                    
                    // Use shared status labels

                    return (
                      <div key={stip.id} className="border rounded-lg p-3 space-y-2 relative">
                        {isFIManagerOrAdmin && (
                          <button
                            onClick={() => {
                              if (confirm(`Are you sure you want to delete "${stip.template_name}"?`)) {
                                deleteStipMutation.mutate(stip.id);
                              }
                            }}
                            disabled={deleteStipMutation.isPending}
                            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Delete stip"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-8">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium">{stip.template_name}</p>
                              {isSuperAdminOrAccountManager && (
                                <button
                                  type="button"
                                  className="text-xs text-blue-600 hover:underline"
                                  onClick={() => {
                                    setEditStipInstructionsStip(stip);
                                    setEditStipInstructionsDraft(stip.instructions ?? "");
                                  }}
                                >
                                  Edit instructions
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{stip.template_description}</p>
                            {stip.template_name === BANK_STATEMENT_STIP_NAME && plaidBankLink?.applicable && (
                              <div className="mt-3 p-3 bg-gray-50 rounded border space-y-2">
                                <p className="text-xs text-gray-600 font-medium">Bank statement link</p>
                                <p className="text-xs text-gray-600">
                                  {plaidBankLink.status === "statements_ready"
                                    ? `${plaidBankLink.statements_saved_count} statement${plaidBankLink.statements_saved_count === 1 ? "" : "s"} saved${plaidBankLink.institution_name ? ` from ${plaidBankLink.institution_name}` : ""}.`
                                    : plaidBankLink.status === "connected" || plaidBankLink.status === "refreshing"
                                    ? "Connected, fetching statements."
                                    : plaidBankLink.status === "failed"
                                    ? (plaidBankLink.error || "Bank connection failed.")
                                    : plaidBankLink.expires_at
                                    ? `Link expires ${new Date(plaidBankLink.expires_at).toLocaleString()}.`
                                    : "Bank statement link is not available yet."}
                                </p>
                                {plaidBankLink.missing_months?.length > 0 && (
                                  <p className="text-xs text-amber-700">
                                    Missing: {plaidBankLink.missing_months.join(", ")}
                                  </p>
                                )}
                                {plaidBankLink.hosted_link_url && (
                                  <div className="flex items-center gap-2">
                                    <Input readOnly value={plaidBankLink.hosted_link_url} className="text-sm flex-1" />
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard.writeText(plaidBankLink.hosted_link_url || "");
                                        toast.success("Link copied!");
                                      }}
                                    >
                                      Copy
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                            {(stip.destination === "CUSTOMER" || stip.destination === "INTERNAL") &&
                              (stip.instructions || "").trim() !== "" && (
                                <details className={`mt-2 ${staffUi.insetPanel} px-2 py-1 text-sm`}>
                                  <summary className="cursor-pointer font-medium text-gray-800">
                                    See instructions
                                  </summary>
                                  <div className="mt-2 border-t border-gray-100 pt-2">
                                    <StipInstructionsContent content={stip.instructions || ""} />
                                  </div>
                                </details>
                              )}
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={statusColors[stip.status] || "bg-gray-500"}>
                                {stipStatusLabels[stip.status] || stip.status}
                              </Badge>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {stip.destination === 'CUSTOMER' ? 'Customer' : 'Internal'}
                                </Badge>
                                {isFIManagerOrAdmin && (
                                  <button
                                    onClick={() => {
                                      const newDestination = stip.destination === 'CUSTOMER' ? 'INTERNAL' : 'CUSTOMER';
                                      updateStipDestinationMutation.mutate({
                                        stipId: stip.id,
                                        destination: newDestination,
                                        previousDestination: stip.destination,
                                      });
                                    }}
                                    disabled={updateStipDestinationMutation.isPending}
                                    className={`
                                      relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                                      ${stip.destination === 'CUSTOMER' ? 'bg-blue-600' : 'bg-gray-300'}
                                      ${updateStipDestinationMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                    `}
                                    title={`Switch to ${stip.destination === 'CUSTOMER' ? 'Internal' : 'Customer'}`}
                                  >
                                    <span
                                      className={`
                                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                        ${stip.destination === 'CUSTOMER' ? 'translate-x-5' : 'translate-x-0.5'}
                                      `}
                                    />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        {isFIManagerOrAdmin && (
                          <div className="flex items-center gap-2 mt-2">
                            <select
                              className="flex-1 px-2 py-1 border rounded text-sm"
                              value={stip.status}
                              onChange={(e) => {
                                updateStipStatusMutation.mutate({
                                  stipId: stip.id,
                                  status: e.target.value,
                                });
                              }}
                              disabled={updateStipStatusMutation.isPending}
                            >
                              <option value="REQUESTED">Requested</option>
                              <option value="SUBMITTED_BY_CUSTOMER">Submitted by Customer</option>
                              <option value="SUBMITTED_TO_LENDER">Submitted to Lender</option>
                              <option value="CLEARED">Cleared</option>
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500">No stipulations</p>
              )}
              
              {/* Upload Link Section - Show to F&I/Admin if there are customer stips with REQUESTED status, or to Sales Rep if link exists */}
              {((isFIManagerOrAdmin && application.stips && application.stips.some(
                (stip) => stip.destination === 'CUSTOMER' && stip.status === 'REQUESTED'
              )) || (isSales && uploadLinkUrl)) && (
                <div className="mt-4 pt-4 border-t">
                  {isFIManagerOrAdmin && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          getUploadLinkMutation.mutate();
                        }}
                        disabled={getUploadLinkMutation.isPending}
                      >
                        {getUploadLinkMutation.isPending ? "Loading..." : uploadLinkUrl ? "Update Customer Upload Link" : "Get Customer Upload Link"}
                      </Button>
                    </div>
                  )}
                  {uploadLinkUrl && (
                    <div className="mt-3 p-3 bg-gray-50 rounded border">
                      <p className="text-xs text-gray-600 mb-2 font-medium">Customer Upload Link (for all REQUESTED customer stips):</p>
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={uploadLinkUrl}
                          className="text-sm flex-1"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(uploadLinkUrl);
                            toast.success("Link copied!");
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents Card */}
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload Section */}
              <div className="space-y-2">
                <Label>Upload Document</Label>
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-start">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    disabled={uploadingDocument}
                    className="hidden"
                    id="document-upload-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                  />
                  <div
                    role="button"
                    tabIndex={uploadingDocument ? -1 : 0}
                    onKeyDown={(e) => {
                      if (uploadingDocument) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    onClick={() => !uploadingDocument && fileInputRef.current?.click()}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className={`flex-1 min-w-0 border-2 border-dashed rounded-lg px-5 py-6 text-center transition-colors flex flex-col items-center justify-center gap-2 ${
                      isDragging
                        ? "border-primary bg-primary-100 text-primary"
                        : "border-primary bg-primary-50 hover:border-primary hover:bg-primary-100"
                    } ${uploadingDocument ? "opacity-50 cursor-not-allowed pointer-events-none" : "cursor-pointer"}`}
                  >
                    <Upload className={`h-8 w-8 shrink-0 ${isDragging ? "text-primary" : "text-primary/80"}`} aria-hidden />
                    {uploadingDocument ? (
                      <div className="text-sm font-semibold text-muted-foreground">Uploading…</div>
                    ) : isDragging ? (
                      <div className="text-sm font-semibold text-primary">Drop files here to upload</div>
                    ) : (
                      <div className="text-sm text-foreground">
                        <span className="font-semibold text-primary">Click to choose files</span>
                        <span className="text-muted-foreground"> or drag and drop</span>
                      </div>
                    )}
                    {!uploadingDocument && (
                      <p className="text-xs text-muted-foreground">PDF, Word, Excel, images</p>
                    )}
                  </div>
                  {application?.stips && application.stips.filter(s => s.status === 'REQUESTED').length > 0 && (
                    <select
                      className="px-3 py-2 border rounded-md text-sm w-full sm:w-auto sm:min-w-[180px] sm:self-center"
                      value={selectedStipForUpload || ''}
                      onChange={(e) => setSelectedStipForUpload(e.target.value ? parseInt(e.target.value) : null)}
                      disabled={uploadingDocument}
                    >
                      <option value="">Tag to stip (optional)</option>
                      {application.stips
                        .filter(s => s.status === 'REQUESTED')
                        .map((stip) => (
                          <option key={stip.id} value={stip.id}>
                            {stip.template_name}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
                {selectedStipForUpload && (
                  <p className="text-xs text-gray-500">
                    Document will be tagged to: {application?.stips?.find(s => s.id === selectedStipForUpload)?.template_name}
                  </p>
                )}
              </div>

              {/* Documents List */}
              {application?.documents && application.documents.length > 0 ? (
                <div className="space-y-2">
                  <Label>Uploaded Documents ({application.documents.length})</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {application.documents.map((doc: Document) => (
                      <div
                        key={doc.id}
                        className="flex items-start justify-between p-2 border rounded hover:bg-gray-50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {documentRename?.id === doc.id ? (
                              <div className="flex flex-wrap items-center gap-2 w-full">
                                <Input
                                  value={documentRename.base}
                                  onChange={(e) =>
                                    setDocumentRename({ ...documentRename, base: e.target.value })
                                  }
                                  className="max-w-[min(100%,220px)] h-8 text-sm"
                                  disabled={renameDocumentMutation.isPending}
                                  aria-label="Document name (extension is fixed)"
                                />
                                <span className="text-sm text-muted-foreground shrink-0">
                                  {documentRename.ext || "(no extension)"}
                                </span>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8"
                                  disabled={
                                    renameDocumentMutation.isPending ||
                                    !documentRename.base.trim() ||
                                    !documentRename.ext
                                  }
                                  onClick={() =>
                                    renameDocumentMutation.mutate({
                                      documentId: doc.id,
                                      baseName: documentRename.base.trim(),
                                    })
                                  }
                                >
                                  Save
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-8"
                                  disabled={renameDocumentMutation.isPending}
                                  onClick={() => setDocumentRename(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <>
                                <a
                                  href={`${customInstance.defaults.baseURL || ''}/api/documents/${doc.id}/download/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 truncate"
                                >
                                  {doc.filename}
                                </a>
                                {isFIManagerOrAdmin && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 shrink-0"
                                    title="Rename display name"
                                    aria-label="Rename document"
                                    onClick={() => {
                                      const lastDot = doc.filename.lastIndexOf(".");
                                      const ext =
                                        lastDot > 0 ? doc.filename.slice(lastDot) : "";
                                      const base =
                                        lastDot > 0 ? doc.filename.slice(0, lastDot) : doc.filename;
                                      setDocumentRename({ id: doc.id, base, ext });
                                    }}
                                  >
                                    <PencilIcon className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </>
                            )}
                            {doc.stip_name && (
                              <Badge variant="outline" className="text-xs">
                                {doc.stip_name}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {(doc.size / 1024).toFixed(1)} KB • {new Date(doc.created_at).toLocaleDateString()}
                            {doc.uploaded_by_name && ` • Uploaded by ${doc.uploaded_by_name}`}
                          </div>
                          {/* Stip Tagging Dropdown */}
                          <div className="mt-2">
                            <select
                              className="text-xs px-2 py-1 border rounded w-full max-w-xs"
                              value={doc.stip_id || ''}
                              onChange={(e) => {
                                const newStipId = e.target.value ? parseInt(e.target.value) : null;
                                updateDocumentStipMutation.mutate({
                                  documentId: doc.id,
                                  stipId: newStipId,
                                });
                              }}
                              disabled={updateDocumentStipMutation.isPending}
                            >
                              <option value="">Tag to stip (optional)</option>
                              {application.stips?.map((stip) => {
                                const statusDisplay = stipStatusLabels[stip.status] || stip.status;
                                return (
                                  <option key={stip.id} value={stip.id}>
                                    {stip.template_name} ({statusDisplay})
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                        {/* Delete button - only for Admin/FI Manager */}
                        {isFIManagerOrAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 h-7 w-7 p-0"
                            onClick={() => setDocumentToDelete(doc.id)}
                            disabled={deleteDocumentMutation.isPending}
                            title="Delete document"
                          >
                            <X className="h-4 w-4" aria-hidden />
                            <span className="sr-only">Delete</span>
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No documents uploaded</p>
              )}
            </CardContent>
          </Card>

          {/* Funded Deal Economics Card — DD staff internal lens only */}
          {application.stage === "FUNDED" && isSuperAdminOrAccountManager && ddInternalLens && (
            <Card>
              <CardHeader className="relative">
                <CardTitle>Funded Deal Economics</CardTitle>
                {isFIManagerOrAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingFundedEconomics(!editingFundedEconomics);
                      if (!editingFundedEconomics) {
                        const economics = application.funded_deal_economics;
                        setEditedFundedEconomicsData({
                          lender_financed_with: economics?.lender_financed_with || null,
                          equipment_cost: economics?.equipment_cost || 0,
                          shipping_warranty_other_fees: economics?.shipping_warranty_other_fees || 0,
                          dealer_participation: economics?.dealer_participation || 0,
                          lender_fees_financed: economics?.lender_fees_financed || 0,
                          down_payment: economics?.down_payment || 0,
                          term_months: economics?.term_months || 0,
                          lender_doc_fees_collected: economics?.lender_doc_fees_collected || 0,
                          at_doc_fees: economics?.at_doc_fees || 0,
                          dealer_referral: economics?.dealer_referral ?? 0,
                          monthly_payment: economics?.monthly_payment || 0,
                          number_of_advances: economics?.number_of_advances || 0,
                          contract_type: economics?.contract_type || null,
                          buy_rate: economics?.buy_rate || 0,
                          sell_rate: economics?.sell_rate || 0,
                          gross_margin: economics?.gross_margin || 0,
                          invoice_amount: economics?.invoice_amount || null,
                          invoice_number: economics?.invoice_number || null,
                          points: economics?.points || 0,
                          funded_date: economics?.funded_date || null,
                        });
                      }
                    }}
                    className="absolute top-4 right-4 p-1"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {editingFundedEconomics && isFIManagerOrAdmin ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Lender Financed With</Label>
                        <select
                          value={editedFundedEconomicsData.lender_financed_with || ""}
                          onChange={(e) => {
                            const lenderId = e.target.value ? parseInt(e.target.value) : null;
                            setEditedFundedEconomicsData(prev => ({ ...prev, lender_financed_with: lenderId }));
                            // Fetch and populate approval economics immediately
                            fetchApprovalEconomicsForLender(lenderId);
                          }}
                          className="w-full px-3 py-2 border rounded"
                        >
                          <option value="">Select Lender</option>
                          {lenders?.map((lender) => (
                            <option key={lender.id} value={lender.id}>{lender.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Contract Type</Label>
                        <select
                          value={editedFundedEconomicsData.contract_type || ""}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, contract_type: e.target.value || null }))}
                          className="w-full px-3 py-2 border rounded"
                        >
                          <option value="">Select Contract Type</option>
                          <option value="EFA">EFA</option>
                          <option value="FMV_LEASE">FMV Lease</option>
                          <option value="ONE_DOLLAR_OUT_LEASE">$1 out Lease</option>
                          <option value="TRAC_LEASE">TRAC Lease</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Equipment Cost ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.equipment_cost === 0 || editedFundedEconomicsData.equipment_cost === undefined ? '' : editedFundedEconomicsData.equipment_cost}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, equipment_cost: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }))}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Shipping/Warranty/Other Fees ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.shipping_warranty_other_fees === 0 || editedFundedEconomicsData.shipping_warranty_other_fees === undefined ? '' : editedFundedEconomicsData.shipping_warranty_other_fees}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, shipping_warranty_other_fees: 0 }));
                            } else {
                              const num = parseFloat(val);
                              // Allow negative values - parseFloat("-100") returns -100 correctly
                              // Only default to 0 if value is empty or truly invalid (not a number at all)
                              setEditedFundedEconomicsData(prev => ({ ...prev, shipping_warranty_other_fees: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Dealer Participation ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.dealer_participation === 0 || editedFundedEconomicsData.dealer_participation === undefined ? '' : editedFundedEconomicsData.dealer_participation}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, dealer_participation: 0 }));
                            } else {
                              const num = parseFloat(val);
                              // Allow negative values - parseFloat("-100") returns -100 correctly
                              // Only default to 0 if value is empty or truly invalid (not a number at all)
                              setEditedFundedEconomicsData(prev => ({ ...prev, dealer_participation: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Lender Fees Financed ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.lender_fees_financed === 0 || editedFundedEconomicsData.lender_fees_financed === undefined ? '' : editedFundedEconomicsData.lender_fees_financed}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, lender_fees_financed: 0 }));
                            } else {
                              const num = parseFloat(val);
                              // Allow negative values - parseFloat("-100") returns -100 correctly
                              // Only default to 0 if value is empty or truly invalid (not a number at all)
                              setEditedFundedEconomicsData(prev => ({ ...prev, lender_fees_financed: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Down Payment ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.down_payment === 0 || editedFundedEconomicsData.down_payment === undefined ? '' : editedFundedEconomicsData.down_payment}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, down_payment: 0 }));
                            } else {
                              const num = parseFloat(val);
                              // Allow negative values - parseFloat("-100") returns -100 correctly
                              // Only default to 0 if value is empty or truly invalid (not a number at all)
                              setEditedFundedEconomicsData(prev => ({ ...prev, down_payment: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Term (months)</Label>
                        <Input
                          type="number"
                          value={editedFundedEconomicsData.term_months === 0 || editedFundedEconomicsData.term_months === undefined ? '' : editedFundedEconomicsData.term_months}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, term_months: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Lender Doc Fees Collected ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.lender_doc_fees_collected === 0 || editedFundedEconomicsData.lender_doc_fees_collected === undefined ? '' : editedFundedEconomicsData.lender_doc_fees_collected}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, lender_doc_fees_collected: 0 }));
                            } else {
                              const num = parseFloat(val);
                              // Allow negative values - parseFloat("-100") returns -100 correctly
                              // Only default to 0 if value is empty or truly invalid (not a number at all)
                              setEditedFundedEconomicsData(prev => ({ ...prev, lender_doc_fees_collected: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">TCC Doc Fees ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.at_doc_fees === 0 || editedFundedEconomicsData.at_doc_fees === undefined ? '' : editedFundedEconomicsData.at_doc_fees}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, at_doc_fees: 0 }));
                            } else {
                              const num = parseFloat(val);
                              // Allow negative values - parseFloat("-100") returns -100 correctly
                              // Only default to 0 if value is empty or truly invalid (not a number at all)
                              setEditedFundedEconomicsData(prev => ({ ...prev, at_doc_fees: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Dealer Referral ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.dealer_referral === 0 || editedFundedEconomicsData.dealer_referral === undefined || editedFundedEconomicsData.dealer_referral === null ? '' : editedFundedEconomicsData.dealer_referral}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onWheel={(e) => { e.currentTarget.blur(); e.preventDefault(); e.stopPropagation(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) target.value = '';
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, dealer_referral: 0 }));
                            } else {
                              const num = parseFloat(val);
                              setEditedFundedEconomicsData(prev => ({ ...prev, dealer_referral: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Monthly Payment ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.monthly_payment === 0 || editedFundedEconomicsData.monthly_payment === undefined ? '' : editedFundedEconomicsData.monthly_payment}
                          onFocus={(e) => { 
                            const val = e.target.value;
                            if (val === '0' || val === '0.0' || val === '0.00' || val === '') {
                              e.target.select();
                            }
                          }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            const val = target.value;
                            // If value is 0, 0.0, 0.00, or empty, and user types a digit, clear the field
                            if ((val === '0' || val === '0.0' || val === '0.00' || val === '' || parseFloat(val) === 0) && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setEditedFundedEconomicsData(prev => ({ ...prev, monthly_payment: 0 }));
                            } else {
                              const num = parseFloat(val);
                              // Allow negative values - parseFloat("-100") returns -100 correctly
                              // Only default to 0 if value is empty or truly invalid (not a number at all)
                              setEditedFundedEconomicsData(prev => ({ ...prev, monthly_payment: isNaN(num) ? 0 : num }));
                            }
                          }}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Number of Advances</Label>
                        <Input
                          type="number"
                          value={editedFundedEconomicsData.number_of_advances === 0 || editedFundedEconomicsData.number_of_advances === undefined ? '' : editedFundedEconomicsData.number_of_advances}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, number_of_advances: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Buy Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.buy_rate === 0 || editedFundedEconomicsData.buy_rate === undefined ? '' : editedFundedEconomicsData.buy_rate}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, buy_rate: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }))}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Sell Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.sell_rate === 0 || editedFundedEconomicsData.sell_rate === undefined ? '' : editedFundedEconomicsData.sell_rate}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, sell_rate: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }))}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Gross Margin ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.gross_margin === 0 || editedFundedEconomicsData.gross_margin === undefined ? '' : editedFundedEconomicsData.gross_margin}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, gross_margin: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }))}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Points</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.points === 0 || editedFundedEconomicsData.points === undefined ? '' : editedFundedEconomicsData.points}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onKeyDown={(e) => {
                            const target = e.target as HTMLInputElement;
                            if ((target.value === '0' || target.value === '') && /[0-9]/.test(e.key)) {
                              target.value = '';
                            }
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, points: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 }))}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Invoice Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.invoice_amount === 0 || editedFundedEconomicsData.invoice_amount === null || editedFundedEconomicsData.invoice_amount === undefined ? '' : editedFundedEconomicsData.invoice_amount}
                          onFocus={(e) => { if (e.target.value === '0' || e.target.value === '') e.target.select(); }}
                          onWheel={(e) => {
                            e.currentTarget.blur();
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, invoice_amount: e.target.value === '' ? null : parseFloat(e.target.value) || null }))}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Invoice Number</Label>
                        <Input
                          type="text"
                          value={editedFundedEconomicsData.invoice_number || ''}
                          onChange={(e) => setEditedFundedEconomicsData(prev => ({ ...prev, invoice_number: e.target.value || null }))}
                          placeholder="Enter invoice number"
                          maxLength={255}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Total Financed Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.total_financed_amount === 0 || editedFundedEconomicsData.total_financed_amount === null || editedFundedEconomicsData.total_financed_amount === undefined ? '' : editedFundedEconomicsData.total_financed_amount}
                          disabled
                          className="bg-gray-100"
                          placeholder="Calculated automatically"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Total Commission ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editedFundedEconomicsData.total_commission === 0 || editedFundedEconomicsData.total_commission === null || editedFundedEconomicsData.total_commission === undefined ? '' : editedFundedEconomicsData.total_commission}
                          disabled
                          className="bg-gray-100"
                          placeholder="Calculated automatically"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Funded Date</Label>
                      <Input
                        type="datetime-local"
                        value={utcToLocalDatetime(editedFundedEconomicsData.funded_date)}
                        onChange={(e) => setEditedFundedEconomicsData(prev => ({
                          ...prev,
                          funded_date: localToUtcISO(e.target.value)
                        }))}
                      />
                    </div>
                    {/* Soft validation warning */}
                    {(() => {
                      const totalCost = Number(editedFundedEconomicsData.equipment_cost || 0) + 
                                       Number(editedFundedEconomicsData.shipping_warranty_other_fees || 0) + 
                                       Number(editedFundedEconomicsData.lender_fees_financed || 0);
                      const downPaymentNum = Number(editedFundedEconomicsData.down_payment || 0);
                      const financed = totalCost - downPaymentNum;
                      const diff = Math.abs(financed - totalCost + downPaymentNum);
                      if (diff > 0.01 && totalCost > 0) {
                        return (
                          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                            <p className="font-medium">Validation Note:</p>
                            <p>Total Cost: ${totalCost.toFixed(2)} | Down Payment: ${downPaymentNum.toFixed(2)} | Financed Amount: ${financed.toFixed(2)}</p>
                            <p className="text-xs mt-1">Note: Down Payment + Financed Amount should equal Total Cost (Equipment Cost + Fees)</p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button size="sm" onClick={() => updateFundedEconomicsMutation.mutate(editedFundedEconomicsData)}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingFundedEconomics(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {application.funded_deal_economics ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Lender Financed With</p>
                            <p className="text-sm font-semibold text-gray-900">{application.funded_deal_economics.lender_financed_with_name || "Not set"}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Contract Type</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {application.funded_deal_economics.contract_type === 'EFA' ? 'EFA' :
                               application.funded_deal_economics.contract_type === 'FMV_LEASE' ? 'FMV Lease' :
                               application.funded_deal_economics.contract_type === 'ONE_DOLLAR_OUT_LEASE' ? '$1 out Lease' :
                               application.funded_deal_economics.contract_type === 'TRAC_LEASE' ? 'TRAC Lease' : 'Not set'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Equipment Cost</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.equipment_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Shipping/Warranty/Other Fees</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.shipping_warranty_other_fees || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Dealer Participation</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.dealer_participation || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Lender Fees Financed</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.lender_fees_financed || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Down Payment</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.down_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Term (months)</p>
                            <p className="text-sm font-semibold text-gray-900">{application.funded_deal_economics.term_months || 0}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Lender Doc Fees Collected</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.lender_doc_fees_collected || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">TCC Doc Fees</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.at_doc_fees || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Monthly Payment</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.monthly_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Number of Advances</p>
                            <p className="text-sm font-semibold text-gray-900">{application.funded_deal_economics.number_of_advances || 0}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Funded Date</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {application.funded_deal_economics.funded_date 
                                ? new Date(application.funded_deal_economics.funded_date).toLocaleDateString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric'
                                  })
                                : 'Not set'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Buy Rate</p>
                            <p className="text-sm font-semibold text-gray-900">{Number(application.funded_deal_economics.buy_rate || 0).toFixed(2)}%</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Sell Rate</p>
                            <p className="text-sm font-semibold text-gray-900">{Number(application.funded_deal_economics.sell_rate || 0).toFixed(2)}%</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Gross Margin</p>
                            <p className="text-sm font-semibold text-gray-900">${Number(application.funded_deal_economics.gross_margin || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Points</p>
                            <p className="text-sm font-semibold text-gray-900">{Number(application.funded_deal_economics.points || 0).toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Invoice Amount</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {application.funded_deal_economics.invoice_amount 
                                ? `$${Number(application.funded_deal_economics.invoice_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : 'Not set'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Invoice Number</p>
                            <p className="text-sm font-semibold text-gray-900">{application.funded_deal_economics.invoice_number || 'Not set'}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Total Financed Amount</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {application.funded_deal_economics.total_financed_amount !== null && application.funded_deal_economics.total_financed_amount !== undefined
                                ? `$${Number(application.funded_deal_economics.total_financed_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : 'Not calculated'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Total Commission</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {application.funded_deal_economics.total_commission !== null && application.funded_deal_economics.total_commission !== undefined
                                ? `$${Number(application.funded_deal_economics.total_commission).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : 'Not calculated'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Dealer Referral</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {application.funded_deal_economics.dealer_referral != null && Number(application.funded_deal_economics.dealer_referral) !== 0
                                ? `$${Number(application.funded_deal_economics.dealer_referral).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : '—'}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-500">No funded deal economics data entered yet</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      <div className="mt-6">
        <Link to="/apps">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to Applications
          </Button>
        </Link>
      </div>

      <Dialog open={showAddOwnerModal} onOpenChange={setShowAddOwnerModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add guarantor</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Creates a new co-guarantor on this application. You can send a{" "}
            <span className="font-medium">Customer sign link</span> for supplemental signature; if
            email, SSN, or address is not on file yet, the customer can complete those on the link
            (email is required before they can sign).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2 text-sm">
            <div>
              <Label className="text-xs">First name</Label>
              <Input
                className="mt-1"
                value={addOwnerForm.first_name}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, first_name: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Last name</Label>
              <Input
                className="mt-1"
                value={addOwnerForm.last_name}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, last_name: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Email (optional)</Label>
              <Input
                type="email"
                className="mt-1"
                value={addOwnerForm.email}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Phone (optional)</Label>
              <Input
                className="mt-1"
                value={addOwnerForm.phone}
                onChange={(e) =>
                  handlePhoneChange(e.target.value, (val) => setAddOwnerForm((p) => ({ ...p, phone: val })))
                }
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Street address (optional)</Label>
              <Input
                className="mt-1"
                value={addOwnerForm.address_1}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, address_1: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Address line 2 (optional)</Label>
              <Input
                className="mt-1"
                value={addOwnerForm.address_2}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, address_2: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">City (optional)</Label>
              <Input
                className="mt-1"
                value={addOwnerForm.city}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, city: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">State (optional)</Label>
              <select
                className="mt-1 w-full px-3 py-2 border rounded-md"
                value={addOwnerForm.state}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, state: e.target.value }))}
              >
                <option value="">Select…</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">ZIP (optional)</Label>
              <Input
                className="mt-1"
                value={addOwnerForm.postal_code}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, postal_code: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">SSN (optional, 9 digits if provided)</Label>
              <Input
                className="mt-1"
                inputMode="numeric"
                autoComplete="off"
                value={addOwnerForm.ssn}
                onChange={(e) => handleSSNChange(e.target.value, (val) => setAddOwnerForm((p) => ({ ...p, ssn: val })))}
              />
            </div>
            <div>
              <Label className="text-xs">DOB (optional)</Label>
              <Input
                className="mt-1"
                placeholder="MM/DD/YYYY"
                value={addOwnerForm.dob}
                onChange={(e) =>
                  handleDOBChange(e.target.value, (val) => setAddOwnerForm((p) => ({ ...p, dob: val })))
                }
              />
            </div>
            <div>
              <Label className="text-xs">Ownership % (optional)</Label>
              <Input
                className="mt-1"
                inputMode="decimal"
                value={addOwnerForm.ownership_percent}
                onChange={(e) => setAddOwnerForm((p) => ({ ...p, ownership_percent: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setShowAddOwnerModal(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-blue-600 text-white"
              disabled={addOwnerMutation.isPending}
              onClick={() => {
                if (!addOwnerForm.first_name.trim() || !addOwnerForm.last_name.trim()) {
                  toast.error("First name and last name are required.");
                  return;
                }
                const ph = toDigits(addOwnerForm.phone);
                if (ph.length > 0 && ph.length !== 10) {
                  toast.error("Enter a full 10-digit phone number, or leave phone blank.");
                  return;
                }
                const ssnD = toDigits(addOwnerForm.ssn);
                if (ssnD.length > 0 && ssnD.length !== 9) {
                  toast.error("Enter a full 9-digit SSN, or leave SSN blank.");
                  return;
                }
                addOwnerMutation.mutate();
              }}
            >
              {addOwnerMutation.isPending ? "Saving…" : "Add guarantor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCustomerSignModal} onOpenChange={setShowCustomerSignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Customer sign link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 text-sm">
            <p className="text-gray-600">
              Generate a URL for the customer to verify identity and sign. Send the link manually (email/SMS).
              {isCorpOnlyApplication &&
                " For corporate-only applications, choose Personal guarantor upgrade after the customer has signed the corp-only application."}
            </p>
            <div>
              <Label>Signer</Label>
              <select
                className="mt-1 w-full px-3 py-2 border rounded-md"
                value={customerSignKind}
                onChange={(e) => {
                  const v = e.target.value as "PRIMARY_CONTACT" | "ADDITIONAL_OWNER" | "PG_UPGRADE";
                  setCustomerSignKind(v);
                  setCustomerSignOwnerRowId("");
                }}
              >
                <option value="PRIMARY_CONTACT" disabled={isCorpOnlyApplication && application.application_signed}>
                  Primary contact (main application signature)
                </option>
                <option value="ADDITIONAL_OWNER" disabled={supplementalSignOwnerRows.length === 0}>
                  Additional owner (supplemental signature only)
                </option>
                <option value="PG_UPGRADE" disabled={!isCorpOnlyApplication}>
                  Personal guarantor upgrade (corp-only → full PG)
                </option>
              </select>
              {customerSignKind === "ADDITIONAL_OWNER" && (
                <div className="mt-3">
                  <Label>Owner row</Label>
                  <select
                    className="mt-1 w-full px-3 py-2 border rounded-md"
                    value={customerSignOwnerRowId}
                    onChange={(e) => setCustomerSignOwnerRowId(e.target.value)}
                  >
                    <option value="">Select owner…</option>
                    {supplementalSignOwnerRows.map((row) => (
                      <option key={row.id} value={String(row.id)}>
                        {row.owner.first_name} {row.owner.last_name}
                        {row.role_label ? ` (${row.role_label})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {customerSignUrl && (
              <div className="space-y-2">
                <Label>URL</Label>
                <Input readOnly value={customerSignUrl} className="text-xs" />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(customerSignUrl);
                    toast.success("Copied to clipboard");
                  }}
                >
                  Copy URL
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setShowCustomerSignModal(false)}>
              Close
            </Button>
            <Button
              type="button"
              className="bg-blue-600 text-white"
              disabled={
                customerSignLinkMutation.isPending ||
                (customerSignKind === "ADDITIONAL_OWNER" &&
                  (!customerSignOwnerRowId || supplementalSignOwnerRows.length === 0))
              }
              onClick={() => customerSignLinkMutation.mutate()}
            >
              {customerSignLinkMutation.isPending ? "Generating…" : "Generate link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Suspense fallback={null}>
        <ApplicationEconomicsModalsLazy
          ref={economicsModalsRef}
          applicationId={id!}
          application={application}
          lenders={lenders}
          isSuperAdminOrAccountManager={isSuperAdminOrAccountManager}
          ddInternalLens={ddInternalLens}
          buildApprovalEconomicsPayload={buildApprovalEconomicsPayload}
          formatValidationErrors={formatValidationErrors}
          localToUtcISO={localToUtcISO}
        />
      </Suspense>


      {/* Add Stips Dialog */}
      <Dialog
        open={showAddStipsDialog}
        onOpenChange={(open) => {
          setShowAddStipsDialog(open);
          if (!open) {
            setAddStipsTemplateSearch("");
            setAddStipsTemplateInstructions({});
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Stipulations</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Destination</Label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-md"
                value={stipDestination}
                onChange={(e) => {
                  setStipDestination(e.target.value as 'CUSTOMER' | 'INTERNAL');
                  // Don't clear selections - allow mixing templates from different destinations
                }}
              >
                <option value="CUSTOMER">Customer</option>
                <option value="INTERNAL">Internal</option>
              </select>
            </div>

            <div>
              <Label htmlFor="add-stips-template-search">Select Stipulation Templates</Label>
              <Input
                id="add-stips-template-search"
                className="mt-2"
                placeholder="Search by name or description…"
                value={addStipsTemplateSearch}
                onChange={(e) => setAddStipsTemplateSearch(e.target.value)}
                autoComplete="off"
              />
              {selectedTemplateIds.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs font-medium text-blue-900 mb-1">Selected ({selectedTemplateIds.length}):</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedTemplateIds.map(id => {
                      const template = stipulationTemplates?.find(t => t.id === id);
                      if (!template) return null;
                      return (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {template.name} ({template.destination === 'CUSTOMER' ? 'Customer' : 'Internal'})
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
              {(currentUser?.role === "SUPERADMIN" || currentUser?.role === "DD_ACCOUNT_MANAGER") &&
                selectedTemplateIds.length > 0 && (
                  <div className={`mt-3 space-y-2 ${staffUi.warningPanel}`}>
                    <p className="text-xs font-medium text-amber-950">Optional instructions (rich text)</p>
                    <p className="text-xs text-amber-900/85">
                      Shown with this stip to customers or internal staff. Org admins can view but not edit. Content is
                      sanitized on the server.
                    </p>
                    <div className="space-y-3 max-h-[min(28rem,55vh)] overflow-y-auto pr-1">
                      {selectedTemplateIds.map((tid) => {
                        const template = stipulationTemplates?.find((t) => t.id === tid);
                        if (!template) return null;
                        return (
                          <div key={tid} className="space-y-1">
                            <Label className="text-xs font-medium text-gray-800">{template.name}</Label>
                            <StipInstructionsEditor
                              value={addStipsTemplateInstructions[tid] ?? ""}
                              onChange={(html) =>
                                setAddStipsTemplateInstructions((prev) => ({
                                  ...prev,
                                  [tid]: html,
                                }))
                              }
                              placeholder="Instructions for this stip…"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              <div className="mt-2 max-h-[min(24rem,50vh)] overflow-y-auto border rounded-md p-2 space-y-2">
                {stipulationTemplatesLoading ? (
                  <p className="text-gray-500 text-sm px-2 py-4 text-center">Loading templates…</p>
                ) : !stipulationTemplates?.length ? (
                  <p className="text-gray-500 text-sm px-2">No templates available</p>
                ) : addStipsGroupedTemplates.kind === "no-destination" ? (
                  <p className="text-gray-500 text-sm px-2">
                    No {stipDestination === "CUSTOMER" ? "Customer" : "Internal"} templates available
                  </p>
                ) : addStipsGroupedTemplates.kind === "no-search-match" ? (
                  <p className="text-gray-500 text-sm px-2">No templates match your search.</p>
                ) : addStipsGroupedTemplates.kind === "groups" ? (
                  addStipsGroupedTemplates.groups.map((group, idx) => (
                    <details
                      key={`${group.label}-${idx}`}
                      className="rounded-md border border-gray-200 bg-white open:shadow-sm"
                      open={idx === 0}
                    >
                      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 rounded-md">
                        {group.label}{" "}
                        <span className="font-normal text-gray-500">({group.templates.length})</span>
                      </summary>
                      <div className="mt-1 space-y-1 px-1 pb-2">
                        {group.templates.map((template) => (
                          <label
                            key={template.id}
                            className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTemplateIds.includes(template.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTemplateIds([...selectedTemplateIds, template.id]);
                                } else {
                                  setSelectedTemplateIds(selectedTemplateIds.filter((tid) => tid !== template.id));
                                }
                              }}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{template.name}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                              <Badge variant="outline" className="mt-1 text-xs">
                                {template.destination === "CUSTOMER" ? "Customer" : "Internal"}
                              </Badge>
                            </div>
                          </label>
                        ))}
                      </div>
                    </details>
                  ))
                ) : null}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddStipsDialog(false);
                setAddStipsTemplateSearch("");
                setSelectedTemplateIds([]);
                setAddStipsTemplateInstructions({});
                setStipDestination('CUSTOMER');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Add all selected templates - backend will use each template's own destination
                addStipsMutation.mutate();
              }}
              disabled={selectedTemplateIds.length === 0 || addStipsMutation.isPending}
            >
              {addStipsMutation.isPending ? "Adding..." : "Add Stips"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editStipInstructionsStip}
        onOpenChange={(open) => {
          if (!open) {
            setEditStipInstructionsStip(null);
            setEditStipInstructionsDraft("");
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit stip instructions</DialogTitle>
          </DialogHeader>
          {editStipInstructionsStip && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">{editStipInstructionsStip.template_name}</p>
              <StipInstructionsEditor
                key={editStipInstructionsStip.id}
                value={editStipInstructionsDraft}
                onChange={setEditStipInstructionsDraft}
                placeholder="Rich text; leave empty to clear. Saved content is sanitized on the server."
              />
              <p className="text-xs text-gray-500">Preview</p>
              <div className={`${staffUi.insetPanel} max-h-48 overflow-y-auto`}>
                {(editStipInstructionsDraft || "").trim() !== "" &&
                !(isProbablyRichTextHtml(editStipInstructionsDraft) && isStipInstructionsEmpty(editStipInstructionsDraft)) ? (
                  <StipInstructionsContent content={editStipInstructionsDraft} />
                ) : (
                  <p className="text-sm text-gray-400">Nothing to preview.</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditStipInstructionsStip(null);
                setEditStipInstructionsDraft("");
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={patchStipInstructionsMutation.isPending || !editStipInstructionsStip}
              onClick={() => {
                if (!editStipInstructionsStip) return;
                patchStipInstructionsMutation.mutate({
                  stipId: editStipInstructionsStip.id,
                  instructions: editStipInstructionsDraft,
                });
              }}
            >
              {patchStipInstructionsMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unmatched offer reasons (all failure reasons for one tier) */}
      <Dialog open={!!unmatchedReasonsDialog} onOpenChange={(open) => !open && setUnmatchedReasonsDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Why this offer didn&apos;t qualify</DialogTitle>
          </DialogHeader>
          {unmatchedReasonsDialog && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {unmatchedReasonsDialog.lenderName}
                {unmatchedReasonsDialog.tierName && ` · ${unmatchedReasonsDialog.tierName}`}
              </p>
              <ul className="list-disc list-inside text-sm text-amber-800 space-y-1">
                {unmatchedReasonsDialog.reasons.map((item, i) => (
                  <li key={i}>
                    {formatPrequalFailureForDisplay(item.code, item.reason, isSuperAdminOrAccountManager)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lender Submission Dialog — DD staff internal lens only */}
      {isSuperAdminOrAccountManager && ddInternalLens && (
      <Dialog open={!!selectedLender} onOpenChange={(open) => {
        if (!open) {
          setSelectedLender(null);
          setEmailBody("");
          setCcEmails("");
          setEmailSubject("");
          setLenderApiSubmissionNotes("");
          setSelectedDocuments([]);
          setUploadedFiles([]);
          setLenderSubmitDragging(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedLender
                ? (effectiveMaskLenderNames
                    ? (application.lender_submissions?.find((s: LenderSubmission) => s.lender === selectedLender)?.lender_name
                        ?? application.prequal_results?.results?.find((r: any) => r.lender_id === selectedLender)?.lender_name
                        ?? "Offer")
                    : lenders?.find(l => l.id === selectedLender)?.name)
                : "Submit to Lender"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedLender && (() => {
            const selectedLenderMeta = lenders?.find(l => l.id === selectedLender);
            const isApiSubmission = selectedLenderMeta?.submission_delivery === "API";
            const isPeacSubmission = isApiSubmission && selectedLenderMeta?.id === 6;
            const isChannelSubmission = isApiSubmission && selectedLenderMeta?.id === 7;

            const addLenderSubmitFiles = (incoming: File[]) => {
              if (incoming.length === 0) return;
              const MAX_FILE_SIZE = 25 * 1024 * 1024;
              const MAX_TOTAL_SIZE = 100 * 1024 * 1024;
              setUploadedFiles((prev) => {
                let totalSize = prev.reduce((sum, f) => sum + f.size, 0);
                const validFiles: File[] = [];
                for (const file of incoming) {
                  if (file.size > MAX_FILE_SIZE) {
                    toast.error(`File ${file.name} exceeds 25MB limit`);
                    continue;
                  }
                  if (totalSize + file.size > MAX_TOTAL_SIZE) {
                    toast.error(`Total attachment size exceeds 100MB limit`);
                    break;
                  }
                  totalSize += file.size;
                  validFiles.push(file);
                }
                if (validFiles.length === 0) return prev;
                const next = [...prev, ...validFiles];
                if (isPeacSubmission && selectedDocuments.length + next.length > 3) {
                  toast.info("PEAC accepts up to 3 attachments. Only the first 3 will be submitted.");
                }
                return next;
              });
              if (lenderSubmitFileInputRef.current) lenderSubmitFileInputRef.current.value = "";
            };

            return (
            <div className="space-y-4">
              {isApiSubmission && (
                <p className="text-sm text-muted-foreground">
                  This lender receives applications via API. Select files to attach and optionally add notes. At least one attachment is required.
                </p>
              )}
              {isPeacSubmission && (
                <p className="text-sm text-muted-foreground">
                  PEAC accepts up to 3 attachments and up to 2 guarantors. If more are selected, only the first allowed items are sent.
                </p>
              )}
              {isChannelSubmission && (
                <p className="text-sm text-muted-foreground">
                  Channel receives a multi-step API submission. Files named with &quot;application&quot; are categorized as application docs; other files as miscellaneous. Long filenames may be shortened to meet API limits.
                </p>
              )}
              {!isApiSubmission && (
              <>
              <div>
                <Label>Subject</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject line"
                />
              </div>
              <div>
                <Label>Email Body</Label>
                <textarea
                  className="w-full mt-1 min-h-[150px] p-2 border rounded"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Dear Lender,&#10;&#10;Please find attached..."
                />
              </div>
              <div>
                <Label>CC Emails (comma separated)</Label>
                <Input
                  value={ccEmails}
                  onChange={(e) => setCcEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                />
              </div>
              </>
              )}
              {isApiSubmission && (
                <div>
                  <Label>Notes for lender (optional)</Label>
                  <textarea
                    className="w-full mt-1 min-h-[100px] p-2 border rounded"
                    value={lenderApiSubmissionNotes}
                    onChange={(e) => setLenderApiSubmissionNotes(e.target.value)}
                    placeholder="Context or instructions for the lender (sent in the API payload)…"
                  />
                </div>
              )}
              
              {/* Attachments Section */}
              <div className="space-y-3">
                <Label>Attachments</Label>
                
                {/* Existing Documents */}
                {application?.documents && application.documents.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-normal text-gray-600">Select from existing documents:</Label>
                    <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                      {application.documents.map((doc: Document) => (
                        <label key={doc.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={selectedDocuments.includes(doc.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDocuments([...selectedDocuments, doc.id]);
                              } else {
                                setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{doc.filename}</span>
                          <span className="text-xs text-gray-500">
                            ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* File Upload */}
                <div>
                  <Label className="text-sm font-normal text-gray-600">Upload additional files:</Label>
                  <input
                    ref={lenderSubmitFileInputRef}
                    type="file"
                    multiple
                    id="lender-submit-files-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    className="sr-only"
                    onChange={(e) => {
                      addLenderSubmitFiles(Array.from(e.target.files || []));
                      e.target.value = "";
                    }}
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        lenderSubmitFileInputRef.current?.click();
                      }
                    }}
                    onClick={() => lenderSubmitFileInputRef.current?.click()}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLenderSubmitDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.currentTarget === e.target) setLenderSubmitDragging(false);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLenderSubmitDragging(false);
                      addLenderSubmitFiles(Array.from(e.dataTransfer.files || []));
                    }}
                    className={`mt-2 border-2 border-dashed rounded-lg px-5 py-6 text-center transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer ${
                      lenderSubmitDragging
                        ? "border-primary bg-primary-100 text-primary"
                        : "border-primary bg-primary-50 hover:border-primary hover:bg-primary-100"
                    }`}
                  >
                    <Upload className={`h-8 w-8 shrink-0 ${lenderSubmitDragging ? "text-primary" : "text-primary/80"}`} aria-hidden />
                    {lenderSubmitDragging ? (
                      <div className="text-sm font-semibold text-primary">Drop files here</div>
                    ) : (
                      <div className="text-sm text-foreground">
                        <span className="font-semibold text-primary">Click to choose files</span>
                        <span className="text-muted-foreground"> or drag and drop</span>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {isApiSubmission
                        ? "At least one attachment required for API submission (25 MB per file, 100 MB total)."
                        : "25 MB per file, 100 MB total."}
                    </p>
                  </div>
                  
                  {/* Show uploaded files */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                          <span>{file.name}</span>
                          <span className="text-gray-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
                            }}
                            className="text-red-600 hover:text-red-800 ml-2"
                            aria-label="Remove file"
                          >
                            <X className="h-4 w-4" aria-hidden />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <Button 
                onClick={async () => {
                  if (!selectedLender || !id || isSales || application?.can_submit_to_lender === false) return;
                  const apiMode = lenders?.find(l => l.id === selectedLender)?.submission_delivery === "API";
                  if (apiMode && selectedDocuments.length === 0 && uploadedFiles.length === 0) {
                    toast.error("Select at least one file to attach for API submission.");
                    return;
                  }
                  try {
                    // Create FormData for multipart/form-data
                    const formData = new FormData();
                    formData.append('subject', emailSubject);
                    formData.append('email_body', emailBody);
                    formData.append('cc_emails', ccEmails);
                    if (apiMode) {
                      formData.append('submission_notes', lenderApiSubmissionNotes);
                    }
                    
                    // Add document IDs
                    selectedDocuments.forEach(docId => {
                      formData.append('document_ids', docId.toString());
                    });
                    
                    // Add uploaded files
                    uploadedFiles.forEach(file => {
                      formData.append('files', file);
                    });
                    
                    await customInstance.post(`/api/applications/${id}/lenders/${selectedLender}/submit/`, formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                    });
                    toast.success(apiMode ? "Application submitted via API." : "Application submitted to lender!");
                    queryClient.invalidateQueries({ queryKey: ["application", id] });
                    setSelectedLender(null);
                    setEmailBody("");
                    setCcEmails("");
                    setEmailSubject("");
                    setLenderApiSubmissionNotes("");
                    setSelectedDocuments([]);
                    setUploadedFiles([]);
                  } catch (error: any) {
                    toast.error(error.response?.data?.error || "Failed to submit");
                  }
                }}
                disabled={
                  isSales ||
                  application?.can_submit_to_lender === false ||
                  (lenders?.find(l => l.id === selectedLender)?.submission_delivery === "API" &&
                    selectedDocuments.length === 0 &&
                    uploadedFiles.length === 0)
                }
              >
                {lenders?.find(l => l.id === selectedLender)?.submission_delivery === "API"
                  ? "Submit via API"
                  : "Send Email"}
              </Button>
            </div>
            );
          })()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSelectedLender(null);
              setEmailBody("");
              setCcEmails("");
              setEmailSubject("");
              setLenderApiSubmissionNotes("");
              setSelectedDocuments([]);
              setUploadedFiles([]);
            }}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Document Delete Confirmation Dialog */}
      <Dialog open={documentToDelete !== null} onOpenChange={(open) => !open && setDocumentToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this document? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (documentToDelete !== null) {
                  deleteDocumentMutation.mutate(documentToDelete);
                }
              }}
              disabled={deleteDocumentMutation.isPending}
            >
              {deleteDocumentMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


